import React, { useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect, useContext } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import { Pouch, POUCH_COLORS, HistoryActionType, Group, GROUP_COLORS, Sheet } from '../types';
import {
  resolvePouchColorHotkey,
  isEditableElement,
  isTableKeyboardContextActive,
  shouldSuppressValueFieldKeyInput,
  isAllowedPouchValueInputChar,
  POUCH_COLOR_HOTKEY_DISPLAY_ORDER,
} from '../utils/pouchColorHotkeys';
import { usePouchValueKeyboardBinding } from '../hooks/usePouchValueKeyboardBinding';
import { filterPouchExpressionInput, normalizeTimePouchValueInputText, isBlurToPouchValueKeyboard } from '../utils/pouchValueInput';
import { NumberCounter } from './NumberCounterComponent';
import {
  calculateGroupSubtotal,
  shouldShowTotalLabel,
  clearGTPRelatedCache,
  getGroupIndex,
  getGroupSerialNumber,
  isInMuteGroup,
  getInclusiveGtpDisplayMultiplier,
  isInclusiveGtpSelfDisplay,
  getInclusiveDivGtpAdjustedDisplaySubtotal,
  getGroupTotalPouchDisplayBase,
  getGroupTotalPouchRoundUnitScaledCap,
  groupHasDivisionInclusiveGtp,
  isInclusiveGtpOfGroup,
  pouchCountsAsTimeForSheetDisplay,
} from '../logic/GroupLogic';
import { handleUnifiedExpression } from '../utils/ExpressionEvaluator';
import type { ExpressionResult } from '../utils/ExpressionEvaluator';
import { roundPouchValue } from '../utils/pouchValueRounding';
import { shiftSnapIncrementMagnitude, shiftMultiplierStepMagnitude } from '../utils/shiftSnapIncrement';
import {
  formatTooltip,
  formatPouchNumericTooltipBoldBlock,
  appendLineToPouchNumericTooltipBoldBlock,
  formatPouchGuidePcExtrasHtml,
  formatPouchGuideArrowValueMultiplierPcHtml,
  formatPouchGuideEditPcExtrasHtml,
  formatPouchGuideDeletePcExtrasHtml,
  formatKeyDescLine,
  formatKeysLeftWithDesc,
  formatReflectQtyToValueFormulaGuideHtml,
} from '../utils/tooltipFormatter';
import { handleConfirmingDeleteButtonClick } from '../utils/confirmingDeleteButton';
import { getModifierKeys } from '../App';
import { GuideContext } from '../contexts/GuideContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  changePouchColor,
  commitValue,
  enableBalancingToTotal,
  switchBalancingToTarget,
} from '../i18n';
import { TIME_POUCH_BALANCING_BASE_VALUE, getTimePouchEffectiveMinutes, getPouchContributionForTotals } from '../logic/BalancingPouchLogic';
import {
  getPouchDisplayValueForEdit,
  getPouchEditInitialString,
  getPouchLayerExpressionFields,
  getPouchEffectiveMultiplierForSheet,
  getPouchLayerMultiplier,
  getPouchLayerRoundUnit,
  getSortedLayers,
  isPouchExpressionOutdatedForSheet,
  pouchUsesLayerValues,
  resolveActiveLayerId,
  pouchLayerShouldSlideMultiplier,
} from '../logic/LayerLogic';
import { useLayerSwitch } from '../context/LayerSwitchContext';
import { useLayerSlideDeferred } from '../hooks/useLayerSlideDeferred';
import { LayerSlideNumber } from './LayerSlideNumber';
import { LayerSlideMultiplier } from './LayerSlideMultiplier';
import { LayerSlideTimeMultBase } from './LayerSlideTimeMultBase';
import { LayerSlideRoundUnit } from './LayerSlideRoundUnit';
import {
  buildPouchLayerTooltipLines,
  resolveLayerNumericTooltipHtml,
  shouldForcePouchEquationTooltip,
} from '../utils/layerValueTooltip';
import { applyPouchScaleAndRound, getPouchScaledValue, roundScaledValue } from '../utils/pouchScaleAndRound';
import { canSplitPouchByMultiplier, canHalfSplitPouchByMultiplier } from '../logic/SplitPouchLogic';
import {
  applyRoundUnitDelta,
  applyRoundUnitShift,
  applyRoundUnitLongPressStep,
  calculateRoundUnitIncrement,
} from '../utils/roundUnitShift';
import {
  armHeldRepeat,
  cancelTempResetTimer,
  clearRepeatTimers,
  getRepeatInterval,
  isDragBeyondThreshold,
  scheduleTempReset,
} from '../utils/heldButtonRepeat';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';
import './styles/PouchComponent.scss';

/**
 * GTP 値編集での opposite は「−5 と −5%、10% と 10%」の対応だけは明示。（− を付けると opposite＋マルチは絶対値のみ）
 * それ以外は gtpSignedPureCoefficient の符号でも判定（例: `/` 連鎖の負）。
 */
function deriveGtpOppositeToTotal(editValueTrimmed: string, exprResult: ExpressionResult): boolean | undefined {
  const t = editValueTrimmed.replace(/,/g, '').replace(/[xX×]/g, '*').trim();
  const compact = t.replace(/\s/g, '');

  if (/^-(\d+(?:\.\d+)?)%$/.test(t)) return true;
  if (/^-(\d+(?:\.\d+)?)$/.test(t)) return true;

  if (/^\+(\d+(?:\.\d+)?)%$/.test(t)) return false;
  if (/^\d+(?:\.\d+)?%$/.test(t)) return false;
  if (/^\d+(?:\.\d+)?$/.test(t)) return false;

  if (compact.startsWith('*')) {
    if (/^\*-\d/.test(compact)) return true;
    if (/^\*\+/.test(compact)) return false;
    // bare *N (no explicit sign): user typed the default multiplier without sign intent
    // → return undefined so oppositeToTotal is not changed
  }

  // 急入力中の中間式（/ 連鎖や未確定記号）で opposite が反転しないよう、
  // 係数符号からの暗黙推定は行わない。opposite は明示入力（先頭 +/-）のみで変更する。
  return undefined;
}

/**
 * GTP 値欄の「マルチだけ」略記を handleUnifiedExpression 向けに *係数 に展開する。
 * −n は *−|n|（または展開済み係数）、−n% は *(n/100)。符号は入力の − が opposite、マルチは常に正の係数のみ evaluator に渡す。
 * 10%, +5% → テーブルと同様（n/100, 1+n/100）。
 * −n% で先頭だけ * を付けた *−n% は評価器で壊れるため、必ず数値化してから渡す。
 */
function expandGtpShorthandMultiplierInput(editValue: string): string {
  const t = editValue.trim().replace(/,/g, '');
  // + は +5% のような割合用のみ（+*8 は評価器で不安定になり得る）
  const bareMult = /^[-]?\d+(?:\.\d+)?%?$/;
  const plusPercentOnly = /^\+(?:\d+(?:\.\d+)?)%$/;
  if (!(bareMult.test(t) || plusPercentOnly.test(t))) {
    return editValue;
  }
  if (t.includes('%')) {
    let mult: number;
    if (t.startsWith('+')) {
      const p = parseFloat(t.slice(1, -1)) || 0;
      mult = 1 + p / 100;
    } else if (t.startsWith('-')) {
      const p = parseFloat(t.slice(1, -1)) || 0;
      mult = p / 100;
      // 式は正の係数のみ（*-0.1 は applyFinal が値を反転し opposite と二重になり得る）
      return `*${mult}`;
    } else {
      const p = parseFloat(t.slice(0, -1)) || 0;
      mult = p / 100;
    }
    return `*${mult}`;
  }
  return `*${t}`;
}

interface PouchComponentProps {
  pouch: Pouch & {
    _isDragging?: boolean;
    _isDeleting?: boolean; // 削除アニメーション用フラグを追加
    _animateFromZero?: boolean; // シート切替時の初期アニメーション用フラグ
    _isCloneSource?: boolean; // 複製元パウチのフラグを追加
    _isCloning?: boolean; // 複製パウチのフラグを追加
  };
  lastPouchValue: number;
  lastPouchColor: string;
  onUpdateMultiplier?: (pouchId: string, multiplier: number, forceNewEntry?: boolean, roundUnitOverride?: number | null) => void;
  onUpdateValueAndMultiplier?: (pouchId: string, value: number, multiplier: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean, lastExpression?: string, valueWhenLastExpression?: number, multiplyWhenLastExpression?: number, isTime?: boolean, oppositeToTotal?: boolean, roundUnit?: number | null, roundUnitWhenLastExpression?: number) => void;
  onSetPouchError?: (pouchId: string, hasError: boolean) => void;
  onDeletePouch?: (pouchId: string) => void;
  onSplitPouch?: (pouchId: string) => void;
  onChangeColor?: (pouchId: string, isShiftPressed?: boolean) => void;
  /** キーボードショートカット用: 色 id を直接指定 */
  onSetPouchColor?: (pouchId: string, colorId: string) => void;
  /** true のときは PC ホバー時の色ホットキーを付けない */
  isTouchDevice?: boolean;
  onUpdateValue?: (id: string, value: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onEditConfirm?: () => void;
  isInitialLoading?: boolean; // リロード時とシート切替時のフラグ
  isSheetSwitching?: boolean;
  isTableSelected?: boolean; // テーブルで選択されているかを示すフラグ
  allGroups?: Group[]; // グループ情報を追加
  
  allPouches?: Pouch[]; // パウチ情報を追加
  currentSheet?: Sheet; // 現在のシート情報
  onPouchHover?: (pouchId: string | null) => void; // パウチホバー時のコールバック
  hoveredPouchId?: string | null; // 現在ホバーされているパウチID
  /** ホバー対象の同期判定用（キーボードショートカットで state の遅延を避ける） */
  hoveredPouchIdRef?: React.MutableRefObject<string | null>;
  hoveredGroupId?: string | null; // 現在ホバーされているグループID
  onGtpGroupHover?: (groupId: string | null) => void; // GTPホバー時のグループクラス付け用
  interaction?: { type: string | null; targetId: string | null; targetType: string | null }; // インタラクション状態
  onToggleBalancingPouch?: (pouchId: string) => void; // 帳尻パウチ（BP）トグル
  /** 計算エリアのキーボード新規作成時のみ: 値編集開始直後の入力文字列を1回だけ差し替える */
  keyboardValueEditSeedRef?: React.MutableRefObject<{
    pouchId: string;
    text?: string;
    caretAtEnd?: boolean;
    selectAllOnFocus?: boolean;
  } | null>;
  /** キーボード新規: 値編集オープン直後に input 未マウントの間に溜めた追加入力 */
  keyboardOpenExtraCharsRef?: React.MutableRefObject<{ pouchId: string; chars: string } | null>;
  /** 上記 ref 更新時に Pouch を再同期するためのカウンタ */
  keyboardGraceAppendBump?: number;
  /** キーボード新規で値編集を開いた直後のパウチID（Esc で履歴なし削除） */
  keyboardNewPouchFirstValueEditPouchIdRef?: React.MutableRefObject<string | null>;
  /** 履歴に残さずパウチを削除（キーボード新規キャンセル用） */
  onDeletePouchWithoutHistory?: (pouchId: string) => void;
  /** シート全体で個々のパウチ表示に影響する構造/モードのシグネチャ（値は含めない）。
   *  React.memo の比較に用い、値編集では変化しないため未編集パウチの再描画を抑止する。 */
  pouchRenderSignature?: string;
}

// Z-indexの定数定義
const MIN_POUCH_Z_INDEX = 200001;

// 長押し・連続実行の設定
const LONG_PRESS_THRESHOLD = 800; // 長押し判定の時間(ms)、ActiveSheetComponentと同じ値
const REPEAT_INTERVAL = 100;      // 繰り返し間隔(ms) - 非推奨、現在は動的に計算

// マルチプライヤーの刻み配列（/2から*2まで）
const MULTIPLIER_STEPS = [
  -2,     // /2
  0.01,   // 1%
  0.02,   // 2%
  0.03,   // 3%
  0.04,   // 4%
  0.05,   // 5%
  0.06,   // 6%
  0.07,   // 7%
  0.08,   // 8%
  0.09,   // 9%
  0.1,    // 10%
  0.11,   // 11%
  0.12,   // 12%
  0.13,   // 13%
  0.14,   // 14%
  0.15,   // 15%
  0.16,   // 16%
  0.17,   // 17%
  0.18,   // 18%
  0.19,   // 19%
  0.2,    // 20%
  0.21,   // 21%
  0.22,   // 22%
  0.23,   // 23%
  0.24,   // 24%
  0.25,   // 25%
  0.26,   // 26%
  0.27,   // 27%
  0.28,   // 28%
  0.29,   // 29%
  0.3,    // 30%
  0.31,   // 31%
  0.32,   // 32%
  0.33,   // 33%
  0.34,   // 34%
  0.35,   // 35%
  0.36,   // 36%
  0.37,   // 37%
  0.38,   // 38%
  0.39,   // 39%
  0.4,    // 40%
  0.41,   // 41%
  0.42,   // 42%
  0.43,   // 43%
  0.44,   // 44%
  0.45,   // 45%
  0.46,   // 46%
  0.47,   // 47%
  0.48,   // 48%
  0.49,   // 49%
  0.5,    // 50%
  0.51,   // 51%
  0.52,   // 52%
  0.53,   // 53%
  0.54,   // 54%
  0.55,   // 55%
  0.56,   // 56%
  0.57,   // 57%
  0.58,   // 58%
  0.59,   // 59%
  0.6,    // 60%
  0.61,   // 61%
  0.62,   // 62%
  0.63,   // 63%
  0.64,   // 64%
  0.65,   // 65%
  0.66,   // 66%
  0.67,   // 67%
  0.68,   // 68%
  0.69,   // 69%
  0.7,    // 70%
  0.71,   // 71%
  0.72,   // 72%
  0.73,   // 73%
  0.74,   // 74%
  0.75,   // 75%
  0.76,   // 76%
  0.77,   // 77%
  0.78,   // 78%
  0.79,   // 79%
  0.8,    // 80%
  0.81,   // 81%
  0.82,   // 82%
  0.83,   // 83%
  0.84,   // 84%
  0.85,   // 85%
  0.86,   // 86%
  0.87,   // 87%
  0.88,   // 88%
  0.89,   // 89%
  0.9,    // 90%
  0.91,   // 91%
  0.92,   // 92%
  0.93,   // 93%
  0.94,   // 94%
  0.95,   // 95%
  0.96,   // 96%
  0.97,   // 97%
  0.98,   // 98%
  0.99,   // 99%
  1,      // 100%
  // 1.01,   // 101%
  // 1.02,   // 102%
  // 1.03,   // 103%
  // 1.04,   // 104%
  // 1.05,   // 105%
  // 1.06,   // 106%
  // 1.07,   // 107%
  // 1.08,   // 108%
  // 1.09,   // 109%
  // 1.1,    // 110%
  // 1.11,   // 111%
  // 1.12,   // 112%
  // 1.13,   // 113%
  // 1.14,   // 114%
  // 1.15,   // 115%
  // 1.16,   // 116%
  // 1.17,   // 117%
  // 1.18,   // 118%
  // 1.19,   // 119%
  // 1.2,    // 120%
  // 1.21,   // 121%
  // 1.22,   // 122%
  // 1.23,   // 123%
  // 1.24,   // 124%
  // 1.25,   // 125%
  // 1.26,   // 126%
  // 1.27,   // 127%
  // 1.28,   // 128%
  // 1.29,   // 129%
  // 1.3,    // 130%
  // 1.31,   // 131%
  // 1.32,   // 132%
  // 1.33,   // 133%
  // 1.34,   // 134%
  // 1.35,   // 135%
  // 1.36,   // 136%
  // 1.37,   // 137%
  // 1.38,   // 138%
  // 1.39,   // 139%
  // 1.4,    // 140%
  // 1.41,   // 141%
  // 1.42,   // 142%
  // 1.43,   // 143%
  // 1.44,   // 144%
  // 1.45,   // 145%
  // 1.46,   // 146%
  // 1.47,   // 147%
  // 1.48,   // 148%
  // 1.49,   // 149%
  // 1.5,    // 150%
  // 1.51,   // 151%
  // 1.52,   // 152%
  // 1.53,   // 153%
  // 1.54,   // 154%
  // 1.55,   // 155%
  // 1.56,   // 156%
  // 1.57,   // 157%
  // 1.58,   // 158%
  // 1.59,   // 159%
  // 1.6,    // 160%
  // 1.61,   // 161%
  // 1.62,   // 162%
  // 1.63,   // 163%
  // 1.64,   // 164%
  // 1.65,   // 165%
  // 1.66,   // 166%
  // 1.67,   // 167%
  // 1.68,   // 168%
  // 1.69,   // 169%
  // 1.7,    // 170%
  // 1.71,   // 171%
  // 1.72,   // 172%
  // 1.73,   // 173%
  // 1.74,   // 174%
  // 1.75,   // 175%
  // 1.76,   // 176%
  // 1.77,   // 177%
  // 1.78,   // 178%
  // 1.79,   // 179%
  // 1.8,    // 180%
  // 1.81,   // 181%
  // 1.82,   // 182%
  // 1.83,   // 183%
  // 1.84,   // 184%
  // 1.85,   // 185%
  // 1.86,   // 186%
  // 1.87,   // 187%
  // 1.88,   // 188%
  // 1.89,   // 189%
  // 1.9,    // 190%
  // 1.91,   // 191%
  // 1.92,   // 192%
  // 1.93,   // 193%
  // 1.94,   // 194%
  // 1.95,   // 195%
  // 1.96,   // 196%
  // 1.97,   // 197%
  // 1.98,   // 198%
  // 1.99,   // 199%
  2       // 200% (*2)
];

// GTP マルチ +/- 用: /2 の次に *0（includes-self ×1-1）
const GTP_MULTIPLIER_STEPS = [-2, 0, ...MULTIPLIER_STEPS.slice(1)];

// マルチプライヤー値から配列インデックスを取得する関数
const getMultiplierIndex = (multiplier: number, steps: readonly number[] = MULTIPLIER_STEPS): number => {
  // 浮動小数点数精度を考慮した比較（小数点以下2桁まで）
  const roundedMultiplier = Math.round(multiplier * 100) / 100;
  
  for (let i = 0; i < steps.length; i++) {
    const roundedStep = Math.round(steps[i] * 100) / 100;
    if (Math.abs(roundedMultiplier - roundedStep) < 0.001) {
      return i;
    }
  }
  
  // 配列にない値の場合、最も近い値を探す
  let closestIndex = 0;
  let minDiff = Math.abs(multiplier - steps[0]);
  
  for (let i = 1; i < steps.length; i++) {
    const diff = Math.abs(multiplier - steps[i]);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  }
  
  return closestIndex;
};

// マルチプライヤー値を次の値に変更する関数
const getNextMultiplier = (
  currentMultiplier: number,
  steps: readonly number[] = MULTIPLIER_STEPS,
): number => {
  const hasZeroStep = steps.includes(0);
  const currentIndex = getMultiplierIndex(currentMultiplier, steps);
  
  // 通常パウチ: 0 は steps に無いので /2 と 1% の間を飛ばす
  if (!hasZeroStep && currentMultiplier === 0) {
    return 0.01;
  }
  
  // 配列の範囲内で次のインデックスを取得
  if (currentIndex < steps.length - 1) {
    return steps[currentIndex + 1];
  }
  
  // 配列の最後の場合は、現在の値に1を加算
  return currentMultiplier + 1;
};

// マルチプライヤー値を前の値に変更する関数
const getPreviousMultiplier = (
  currentMultiplier: number,
  steps: readonly number[] = MULTIPLIER_STEPS,
): number => {
  const hasZeroStep = steps.includes(0);
  const currentIndex = getMultiplierIndex(currentMultiplier, steps);
  
  // 通常パウチ: 0 は steps に無いので /2 と 1% の間を飛ばす
  if (!hasZeroStep && currentMultiplier === 0) {
    return -2;
  }
  
  // 配列の範囲内で前のインデックスを取得
  if (currentIndex > 0) {
    return steps[currentIndex - 1];
  }
  
  // 配列の最初の場合は、現在の値から1を減算
  return currentMultiplier - 1;
};

// デバッグ用
const DEBUG = false; // デバッグを無効化
const log = (...args: any[]) => {
  if (DEBUG) {
    
  }
};

// 基本サイズと桁数に応じた拡大率を定義
const BASE_SIZE = 70;
const DIGIT_SCALE_FACTOR = 0.2; // 1桁増えるごとに15%ずつ大きくする

// GTP用の基本サイズと拡大率
const BASE_SIZE_GTP = 80;
const DIGIT_SCALE_FACTOR_GTP = 0.12;
/** 桁数から求めた基準幅に掛ける（旧 80% 矩形≒正方形の余白分） */
const GTP_WIDTH_FACTOR = 1.2;
const GTP_HEIGHT_RATIO = 0.55;
const GTP_MIN_BAR_HEIGHT = 70;

const getGtpContainerHeight = (baseSize: number): number =>
  Math.max(baseSize * GTP_HEIGHT_RATIO, GTP_MIN_BAR_HEIGHT);

// 時間表示時の円サイズ（桁数計算後に追加で掛ける）
const TIME_POUCH_DISPLAY_SIZE_MULTIPLIER = 1.15;

/** 時間 GTP の幅は h:mm 字数基準（数値 GTP の GTP_WIDTH_FACTOR は使わない） */
const isTimeGtpForLayout = (
  isGroupTotalPouch: boolean | undefined,
  showTime: boolean,
): boolean => !!isGroupTotalPouch && showTime;

const PouchComponent = React.forwardRef<{
  revertToOriginalValues: () => void;
  handleMouseUp: () => void;
  keyboardSimulatePressStart: (
    type: 'value' | 'multiplier' | 'roundUnit',
    direction: 'increase' | 'decrease',
    shiftKey: boolean,
  ) => boolean;
  keyboardSimulatePressEnd: () => void;
}, PouchComponentProps>(({
  pouch,
  lastPouchColor,
  onUpdateMultiplier,
  onUpdateValueAndMultiplier,
  onSetPouchError,
  onDeletePouch,
  onSplitPouch,
  onChangeColor,
  onSetPouchColor,
  isTouchDevice = false,
  onUpdateValue,
  onUpdateTitle,
  onEditConfirm,
  isInitialLoading = false,
  isSheetSwitching,
  isTableSelected,
  allGroups = [],
  allPouches = [],
  currentSheet,
  onPouchHover = () => {},
  hoveredPouchId,
  hoveredPouchIdRef,
  hoveredGroupId,
  onGtpGroupHover = () => {},
  interaction,
  onToggleBalancingPouch,
  keyboardValueEditSeedRef,
  keyboardOpenExtraCharsRef,
  keyboardGraceAppendBump = 0,
  keyboardNewPouchFirstValueEditPouchIdRef,
  onDeletePouchWithoutHistory,
}, ref) => {
  const isGuideEnabled = useContext(GuideContext);
  const { appCopy: t, tooltipCopy: tt, locale } = useLocale();
  const isBalancingPouch = !!(currentSheet?.balancingPouchId && currentSheet.balancingPouchId === pouch.id);

  const currentTotal = useMemo(() => {
    if (!allPouches || !allGroups) return 0;
    return allPouches
      .filter(p => !p._isDeleting && !p._isGtpRenderPreview && !isInMuteGroup(p.id, allGroups || []))
      .reduce((sum, p) => sum + getPouchContributionForTotals(p, allGroups || [], allPouches), 0);
  }, [allPouches, allGroups]);

  // GTPの参照グループのプレースホルダーテキストを計算する関数
  const getReferencedGroupPlaceholderText = useCallback((referencedGroup: Group): string => {
    // TOTAL表示の条件を満たす場合はシート名をプレースホルダーとして使用
    if (shouldShowTotalLabel(referencedGroup, allGroups, allPouches) && currentSheet?.title) {
      return currentSheet.title;
    }
    
    // 通常の場合は「Group 1」「Group 2」などのプレースホルダー
    // シリアルナンバー計算を使用
    const serialNumber = getGroupSerialNumber(referencedGroup.id, allGroups);
    return t.table.groupN(serialNumber);
  }, [allGroups, allPouches, currentSheet, t]);
  
  // グループトータルパウチの値を計算（メモ化最適化）
  const groupTotalValue = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) {
      return pouch.value;
    }
    return getGroupTotalPouchDisplayBase(pouch, allGroups, allPouches);
  }, [
    pouch.isGroupTotalPouch,
    pouch.referencedGroupId,
    pouch.value,
    pouch.multiplier,
    allGroups,
    allPouches,
    currentSheet?.id,
  ]);

  // GTPの参照元グループの色を取得（hex値）
  const referencedGroupColor = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return undefined;
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup?.color) return undefined;
    
    // グループの色IDからhex値を取得
    const groupColorObj = GROUP_COLORS.find(c => c.id === referencedGroup.color);
    return groupColorObj ? groupColorObj.value : undefined;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId, allGroups]);

  // このパウチがGTPの場合、参照しているグループのIDを取得
  const referencedGroupId = useMemo(() => {
    return pouch.isGroupTotalPouch ? pouch.referencedGroupId : undefined;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId]);

  // 内包 GTP で includes-self 表示調整するか（× は |m|>=1、÷ は常に対象）
  const includesSelf = useMemo(
    () => isInclusiveGtpSelfDisplay(pouch, allGroups ?? []),
    [pouch, allGroups]
  );

  // このGTPが参照グループに所属しているかどうかを判定（数式判定用）
  const isInReferencedGroup = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return false;
    
    // 参照しているグループを取得
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) return false;
    
    // このGTPが参照しているグループに所属しているかチェック（純マルチ値に関係なく）
    return referencedGroup.pouches?.includes(pouch.id) || false;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId, pouch.id, allGroups]);

  // ホバー状態のクラス名を生成
  const pouchClassName = useMemo(() => {
    const classes = [];
    
    // このパウチがホバーされている場合
    if (hoveredPouchId === pouch.id) {
      classes.push('pouch-hovered');
    }
    
    // 関連するグループがホバーされている場合（GTPの場合）
    if (hoveredGroupId && referencedGroupId && hoveredGroupId === referencedGroupId) {
      classes.push('pouch-related-group-hovered');
    }
    
    // 参照グループに所属しているGTP（×内包 |m|>=1 または ÷内包）
    if (includesSelf) {
      classes.push('includes-self');
    }
    
    // oppositeToTotalフラグがtrueの場合
    if (pouch.oppositeToTotal) {
      classes.push('opposite');
    }
    
    return classes.join(' ');
  }, [hoveredPouchId, hoveredGroupId, pouch.id, referencedGroupId, includesSelf, pouch.oppositeToTotal]);
  
  // onUpdateValue を ref に保持し、hover re-render が 600ms タイマーをリセットしないようにする
  const onUpdateValueRef = useRef(onUpdateValue);
  useEffect(() => { onUpdateValueRef.current = onUpdateValue; }, [onUpdateValue]);

  // allGroups / allPouches も ref で保持（タイマー発火時に最新値を参照するため）
  const allGroupsRef = useRef(allGroups);
  const allPouchesRef = useRef(allPouches);
  useEffect(() => { allGroupsRef.current = allGroups; }, [allGroups]);
  useEffect(() => { allPouchesRef.current = allPouches; }, [allPouches]);

  // GTPの内部値をリアルタイムで更新
  useEffect(() => {
    // GTP レンダリングプレビュー中は自動ミラーを停止（値を凍結させる）
    if (pouch._isGtpRenderPreview) return;
    if (isInitialLoading || isSheetSwitching) return;
    // 自動書き戻しは GTP でのみ行う。
    // ただし ÷内包 GTP(IDGTP) は後段で明示的に停止する（BP 連動時のドリフト回避）。
    if (pouch.isGroupTotalPouch && groupTotalValue !== undefined && onUpdateValueRef.current) {
      // GTP関連のキャッシュをクリア（最新の値を取得するため）
      clearGTPRelatedCache();
      const referencedGroupForLock = allGroupsRef.current.find(g => g.id === pouch.referencedGroupId);
      const isBalancingLocked =
        !!(
          currentSheet?.balancingPouchId &&
          currentSheet.balancingTargetTotal !== undefined &&
          referencedGroupForLock?.pouches?.includes(currentSheet.balancingPouchId)
        );
      // BP ロック中は計算側（applyBalancingPouch）で BP/GTP を同時収束させるため、
      // ここでの GTP value ミラーは競合源になる。×GTPは停止し、÷系のみ従来ルートへ。
      if (isBalancingLocked && pouch.multiplier >= 0) return;
      const mirrorDelayMs = isBalancingLocked ? 0 : 600;

      // 通常は遅延ミラー。BPロック中は遅延で古い値を参照しやすいため即時ミラー。
      const timer = setTimeout(() => {
        // タイマー発火時点の最新値を ref から取得
        const currentAllGroups = allGroupsRef.current;
        const currentAllPouches = allPouchesRef.current;
        const updateValue = onUpdateValueRef.current;
        if (!updateValue) return;

        // 自分自身を参照するGTPを除外したパウチの合計を計算
        const referencedGroup = currentAllGroups.find(g => g.id === pouch.referencedGroupId);
        if (referencedGroup) {
          if (
            groupHasDivisionInclusiveGtp(referencedGroup, currentAllPouches) &&
            isInclusiveGtpOfGroup(pouch.id, referencedGroup, currentAllPouches)
          ) {
            // no-op: keep mirroring even while BP is locked
          }
          const newValue = groupTotalValue * (pouch.oppositeToTotal ? -1 : 1);
          if (newValue !== pouch.value) {
            updateValue(pouch.id, newValue);
          }
        }
      }, mirrorDelayMs);

      // クリーンアップ関数でタイマーをキャンセル
      return () => clearTimeout(timer);
    }
  }, [groupTotalValue, pouch.oppositeToTotal, pouch.id, pouch.value, pouch.isGroupTotalPouch, pouch.referencedGroupId, pouch.multiplier, currentSheet?.id, currentSheet?.balancingPouchId, currentSheet?.balancingTargetTotal, isInitialLoading, isSheetSwitching]);
  
  // パウチが属するグループのインデックスを計算
  const groupIndex = useMemo(() => {
    if (!allGroups || allGroups.length === 0) return 1;
    
    // パウチが属するグループを特定
    const parentGroup = allGroups.find(group => group.pouches?.includes(pouch.id));
    if (!parentGroup) return 1; // グループに属していない場合は1
    
    // グループを色順とサブトータル順にソート（GroupComponentと同じロジック）
    const sortedGroups = [...allGroups].sort((a, b) => {
      // 色でソート（GROUP_COLORSの配列順）
      const colorIndexA = GROUP_COLORS.findIndex(c => c.id === a.color);
      const colorIndexB = GROUP_COLORS.findIndex(c => c.id === b.color);
      if (colorIndexA !== colorIndexB) {
        return colorIndexA - colorIndexB;
      }
      
      // 色が同じ場合はタイトル昇順、タイトルがない場合はサブトータルの高い順にソート
      const aTitle = a.title?.trim() || '';
      const bTitle = b.title?.trim() || '';
      
      // 両方ともタイトルがある場合はタイトル昇順、同じ場合はサブトータル降順
      if (aTitle && bTitle) {
        const titleCompare = aTitle.localeCompare(bTitle, 'ja');
        if (titleCompare !== 0) {
          return titleCompare;
        }
        // タイトルが同じ場合はサブトータルの高い順
        const subtotalA = calculateGroupSubtotal(a.id, allPouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), allGroups);
        const subtotalB = calculateGroupSubtotal(b.id, allPouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), allGroups);
        return Math.abs(subtotalB) - Math.abs(subtotalA);
      }
      
      // 片方だけタイトルがある場合は、タイトルがある方を先に
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      
      // 両方ともタイトルがない場合はサブトータルの高い順
      const subtotalA = calculateGroupSubtotal(a.id, allPouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), allGroups);
      const subtotalB = calculateGroupSubtotal(b.id, allPouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), allGroups);
      return Math.abs(subtotalB) - Math.abs(subtotalA);
    });
    
    // 親グループのインデックスを取得（1から開始）
    return sortedGroups.findIndex(g => g.id === parentGroup.id) + 1;
  }, [pouch.id, allGroups, allPouches]);
  
  // 一時的な値を保持する状態を追加
  const [tempValue, setTempValue] = useState<number | null>(null);
  const [tempMultiplier, setTempMultiplier] = useState<number | null>(null);

  const { layerSwitchDirection, layerSwitchNonce, activeLayerId: contextActiveLayerId, layerSwitchFromLayerId } =
    useLayerSwitch();
  const sheetLayerCount = currentSheet ? getSortedLayers(currentSheet).length : 1;
  const useLayerSlide = sheetLayerCount > 1;
  const resolvedActiveLayerId = currentSheet
    ? resolveActiveLayerId(currentSheet)
    : contextActiveLayerId;

  const getEffectiveMultiplierForPouch = useCallback(
    (p: Pouch) => getPouchEffectiveMultiplierForSheet(p, currentSheet, resolvedActiveLayerId),
    [currentSheet, resolvedActiveLayerId],
  );

  const layerEffectiveMultiplier = useMemo(
    () => getEffectiveMultiplierForPouch(pouch),
    [pouch, getEffectiveMultiplierForPouch],
  );

  const absMultiplier = Math.abs(layerEffectiveMultiplier);

  /** 時間表示の実効分数 — 多層時は layerValues の時間 multiplier を使う */
  const getTimeMinutesForDisplay = useCallback(
    (multiplierOverride?: number | null): number => {
      const mult =
        multiplierOverride !== undefined && multiplierOverride !== null
          ? multiplierOverride
          : layerEffectiveMultiplier;
      return getTimePouchEffectiveMinutes(pouch, mult);
    },
    [pouch, layerEffectiveMultiplier],
  );

  const displayValueForLayer = useCallback(() => {
    return getPouchDisplayValueForEdit(pouch, currentSheet);
  }, [pouch, currentSheet]);

  const getRoundUnitScaledCap = useCallback(
    (multiplierOverride?: number) =>
      getGroupTotalPouchRoundUnitScaledCap(
        pouch,
        allGroups,
        allPouches,
        multiplierOverride ?? (tempMultiplier !== null ? tempMultiplier : layerEffectiveMultiplier),
        pouch.isGroupTotalPouch ? undefined : displayValueForLayer(),
      ),
    [
      pouch,
      allGroups,
      allPouches,
      tempMultiplier,
      layerEffectiveMultiplier,
      displayValueForLayer,
    ],
  );

  // メイン数値表示（NumberCounter）用の生値 — グループ小計と同様に絶対値 + isNegative で渡す
  const pouchValueDisplayRaw = useMemo(() => {
    if (tempValue !== null) return tempValue;
    if (pouch.isGroupTotalPouch) {
      return groupTotalValue * (pouch.oppositeToTotal ? -1 : 1);
    }
    if (isBalancingPouch) {
      return getPouchDisplayValueForEdit(pouch, currentSheet);
    }
    return getPouchDisplayValueForEdit(pouch, currentSheet);
  }, [
    tempValue,
    pouch,
    currentSheet,
    isBalancingPouch,
    pouch.isGroupTotalPouch,
    pouch.oppositeToTotal,
    pouch.value,
    groupTotalValue,
  ]);

  /** メイン値エリアが strong の時刻表示になるか（value-display 内 IIFE と同じ条件） */
  const shouldShowMainTimeDisplay = useMemo(() => {
    return pouchCountsAsTimeForSheetDisplay(
      pouch,
      allPouches,
      allGroups,
      currentSheet?.balancingPouchId,
      currentSheet,
    );
  }, [
    pouch,
    allPouches,
    allGroups,
    currentSheet,
  ]);

  /** レイヤースライド用: 時刻レイヤーは実効分、数値レイヤーは layer 値 */
  const layerSlideDisplay = useMemo(() => {
    if (shouldShowMainTimeDisplay) {
      const actual =
        tempValue !== null
          ? tempValue
          : pouch.isGroupTotalPouch
            ? groupTotalValue * (pouch.oppositeToTotal ? -1 : 1)
            : isBalancingPouch
              ? getTimeMinutesForDisplay(tempMultiplier)
              : getTimeMinutesForDisplay(tempMultiplier);
      return {
        value: Math.abs(actual),
        isNegative: actual < 0,
        displayAsTime: true as const,
      };
    }
    return {
      value: Math.abs(pouchValueDisplayRaw),
      isNegative: pouchValueDisplayRaw < 0,
      displayAsTime: false as const,
    };
  }, [
    shouldShowMainTimeDisplay,
    tempValue,
    tempMultiplier,
    pouch,
    currentSheet,
    isBalancingPouch,
    groupTotalValue,
    pouchValueDisplayRaw,
    getTimeMinutesForDisplay,
  ]);

  const layerSlideSignEnabled = useLayerSlide || layerSwitchDirection !== 0;
  const layerSlideSignNegative = useLayerSlideDeferred(
    layerSlideDisplay.isNegative,
    layerSwitchDirection,
    layerSwitchNonce,
    layerSlideSignEnabled,
  );

  /** レイヤー切替スライド中は no-value レイアウトクラスを固定（0 切替で縦ずれしない） */
  const hasNoValueForLayout = pouch.isGroupTotalPouch ? groupTotalValue === 0 : pouch.value === 0;
  const isTimeVisualForLayout = shouldShowMainTimeDisplay;
  const isTimeNegBaseForLayout =
    isTimeVisualForLayout &&
    (pouch.isGroupTotalPouch
      ? groupTotalValue * (pouch.oppositeToTotal ? -1 : 1) < 0
      : pouchValueDisplayRaw < 0);
  const layerSliding = layerSwitchDirection !== 0;
  const layerSlideVisualEnabled = useLayerSlide || layerSwitchDirection !== 0;
  const multiplierLayerSlideEnabled = useMemo(() => {
    if (!layerSwitchFromLayerId) return false;
    const toLayerId = currentSheet ? resolveActiveLayerId(currentSheet) : contextActiveLayerId;
    return pouchLayerShouldSlideMultiplier(pouch, layerSwitchFromLayerId, toLayerId);
  }, [pouch, currentSheet, layerSwitchFromLayerId, contextActiveLayerId]);
  const multLayerSlideEnabled = layerSlideVisualEnabled && multiplierLayerSlideEnabled;
  const pouchLayoutVisualRef = useRef({ noValue: hasNoValueForLayout });
  if (!layerSliding) {
    pouchLayoutVisualRef.current = { noValue: hasNoValueForLayout };
  }
  const showNoValue = layerSliding
    ? pouchLayoutVisualRef.current.noValue
    : hasNoValueForLayout;
  /** 時刻レイヤーの「60 ×」等 — スライド中間（LAYER_SLIDE_MS）で付け外し */
  const showTimePouch = useLayerSlideDeferred(
    isTimeVisualForLayout,
    layerSwitchDirection,
    layerSwitchNonce,
    multLayerSlideEnabled,
  );
  const showTimeNegBase = useLayerSlideDeferred(
    isTimeNegBaseForLayout,
    layerSwitchDirection,
    layerSwitchNonce,
    multLayerSlideEnabled,
  );

  /** 上記が真のときの表示文字列（strong 内と同一）— サイズ計算の字数に使用 */
  const getMainTimeDisplayString = useCallback((): string => {
    const actualValue =
      tempValue !== null
        ? tempValue
        : pouch.isGroupTotalPouch
          ? groupTotalValue * (pouch.oppositeToTotal ? -1 : 1)
          : isBalancingPouch
            ? getTimeMinutesForDisplay(tempMultiplier)
            : pouch.isTime
              ? getTimeMinutesForDisplay(tempMultiplier)
              : pouch.multiplier >= 0
                ? pouch.value * pouch.multiplier
                : pouch.value / Math.abs(pouch.multiplier);
    const totalMinutes = Math.round(Math.abs(actualValue));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const sign = actualValue < 0 ? '-' : '';
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  }, [
    tempValue,
    tempMultiplier,
    pouch,
    currentSheet,
    isBalancingPouch,
    pouch.isGroupTotalPouch,
    pouch.oppositeToTotal,
    groupTotalValue,
    pouch.value,
    pouch.multiplier,
    pouch.isTime,
    getTimeMinutesForDisplay,
  ]);
  
  // 乗算モードか除算モードか
  const isDivisionMode = tempMultiplier !== null ? tempMultiplier < 0 : layerEffectiveMultiplier < 0;
  
  // 参照の定義
  const startValueRef = useRef<number | null>(null);
  const startMultiplierRef = useRef<number | null>(null);
  const valueRef = useRef<number>(pouch.value);
  const multiplierRef = useRef<number>(layerEffectiveMultiplier);
  /** handlePressEnd / saveTimer 内で最新の pouch を見る（閉包が古い render のままだとマルチ更新がスキップされ、temp 解除後に戻る） */
  const pouchRef = useRef(pouch);
  pouchRef.current = pouch;
  // 前回のマルチプライヤー値を追跡（表示値ベースで初期化）
  const getInitialDisplayMultiplier = () =>
    pouch.isGroupTotalPouch && includesSelf
      ? getInclusiveGtpDisplayMultiplier(pouch, layerEffectiveMultiplier, true)
      : layerEffectiveMultiplier;
  const prevMultiplierRef = useRef<number>(getInitialDisplayMultiplier());
  const longPressTimerRef = useRef<number | null>(null);
  const repeatTimerRef = useRef<number | null>(null);
  const roundUnitRef = useRef<number | null | undefined>(undefined);
  const startRoundUnitRef = useRef<number | null>(null);
  const originalRoundUnitRef = useRef<number | undefined | null>(null);
  const isRoundUnitButtonOperationRef = useRef(false);
  const activePressTypeRef = useRef<'value' | 'multiplier' | 'roundUnit' | null>(null);
  /** null = 永続値表示（tempMultiplier と同型）。0 = 操作中の ~0 */
  const [tempRoundUnit, setTempRoundUnit] = useState<number | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const shouldAnimateRef = useRef<boolean>(true);
  
  // 操作状態の管理
  const [isValueDecreaseLongPressed, setIsValueDecreaseLongPressed] = useState(false);
  const [isValueIncreaseLongPressed, setIsValueIncreaseLongPressed] = useState(false);
  const [isDecreaseLongPressed, setIsDecreaseLongPressed] = useState(false);
  const [isIncreaseLongPressed, setIsIncreaseLongPressed] = useState(false);
  const [isRoundDecreasePressed, setIsRoundDecreasePressed] = useState(false);
  const [isRoundIncreasePressed, setIsRoundIncreasePressed] = useState(false);
  const [isRoundDecreaseLongPressed, setIsRoundDecreaseLongPressed] = useState(false);
  const [isRoundIncreaseLongPressed, setIsRoundIncreaseLongPressed] = useState(false);
  const [isDecreasePressed, setIsDecreasePressed] = useState(false);
  const [isIncreasePressed, setIsIncreasePressed] = useState(false);
  const [pouchTooltipContent, setPouchTooltipContent] = useState<string | null>(null);
  /** ホバー中のツールチップ元要素（色変更などで data-pouch-tooltip-html を再同期するため） */
  const pouchTooltipTargetRef = useRef<HTMLElement | null>(null);
  /** 直前のホバー錨（別要素に移ったらガイドを初期表示からやり直す） */
  const pouchTooltipHoverAnchorRef = useRef<HTMLElement | null>(null);
  /** handleMouseLeave が isEditing より前に定義されるため、編集中判定に使用 */
  const editModeActiveRef = useRef(false);
  /** 操作ガイド: ホバー 2 秒経過で tier 2（詳細表示） */
  const GUIDE_HOVER_DELAY_MS = 2000;
  const pouchGuideHoverTimerRef = useRef<number | null>(null);
  const [pouchGuideHoverTier, setPouchGuideHoverTier] = useState<0 | 1 | 2>(0);
  
  // 前回の値と現在の値が変わったことを検出するEffect
  useEffect(() => {
    if (multiplierRef.current !== layerEffectiveMultiplier) {
      const prevDisplayMultiplier =
        pouch.isGroupTotalPouch && includesSelf
          ? getInclusiveGtpDisplayMultiplier(pouch, multiplierRef.current, includesSelf)
          : multiplierRef.current;
      prevMultiplierRef.current = prevDisplayMultiplier;
      multiplierRef.current = layerEffectiveMultiplier;
    }
  }, [pouch, layerEffectiveMultiplier, pouch.isGroupTotalPouch, includesSelf]);
  
  // Viewport高さを記録するref（キーボード表示/非表示の検出用）
  const prevHeightRef = useRef(window.visualViewport?.height || window.innerHeight);
  
  // 編集開始時刻を記録するref（キーボード表示時のイベント誤検出防止用）
  const editStartTimeRef = useRef(0);
  /** タイトル編集開始直後の blur / viewport リサイズを無視（iOS で KB が一瞬で閉じる対策） */
  const titleEditBlurGuardRef = useRef(false);
  /** touchStart で編集開始した直後の合成 click を抑止 */
  const suppressTitleClickRef = useRef(false);
  /** IME 変換中（値・タイトル共通）。onChange フィルタ・keydown より先に同期するため ref を使う */
  const isComposingRef = useRef(false);

  // リサイズイベント用デバウンスタイマー
  const resizeTimerRef = useRef<number | null>(null);

  // ホバーイベントハンドラー
  const handleMouseEnter = useCallback(() => {
    onPouchHover(pouch.id);
    // GTPの場合、参照しているグループにクラスを付ける
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      onGtpGroupHover(pouch.referencedGroupId);
    }
    setPouchGuideHoverTier(1);
    if (pouchGuideHoverTimerRef.current !== null) {
      window.clearTimeout(pouchGuideHoverTimerRef.current);
    }
    if (isGuideEnabled) {
      pouchGuideHoverTimerRef.current = window.setTimeout(() => {
        setPouchGuideHoverTier(2);
        pouchGuideHoverTimerRef.current = null;
      }, GUIDE_HOVER_DELAY_MS);
    } else {
      pouchGuideHoverTimerRef.current = null;
    }
  }, [onPouchHover, onGtpGroupHover, pouch.id, pouch.isGroupTotalPouch, pouch.referencedGroupId, isGuideEnabled]);

  const handleMouseLeave = useCallback(() => {
    if (pouchGuideHoverTimerRef.current !== null) {
      window.clearTimeout(pouchGuideHoverTimerRef.current);
      pouchGuideHoverTimerRef.current = null;
    }
    setPouchGuideHoverTier(0);
    pouchTooltipHoverAnchorRef.current = null;
    if (!editModeActiveRef.current) {
      setPouchTooltipContent(null);
      pouchTooltipTargetRef.current = null;
    }
    onPouchHover(null);
    // GTPの場合、参照しているグループのクラスを外す
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      onGtpGroupHover(null);
    }
  }, [onPouchHover, onGtpGroupHover, pouch.isGroupTotalPouch, pouch.referencedGroupId]);
  
  // 表示値ベースのマルチ値を計算する関数（配列外のnextBoundary計算用）
  const getDisplayMultiplier = useCallback(
    (multiplier: number) => getInclusiveGtpDisplayMultiplier(pouch, multiplier, includesSelf),
    [pouch, includesSelf]
  );

  /** アクティブレイヤーの表示用 multiplier（扇形アニメ・percentage 計算の正本） */
  const displayLayerEffectiveMultiplier = useMemo(
    () =>
      pouch.isGroupTotalPouch
        ? getDisplayMultiplier(layerEffectiveMultiplier)
        : layerEffectiveMultiplier,
    [pouch.isGroupTotalPouch, layerEffectiveMultiplier, getDisplayMultiplier]
  );
  
  // CSS変数用のmultiplier-percentage値を計算
  const targetMultiplierPercentage = useMemo(() => {
    // エラー判定（0の場合）は内部値ベース
    if (absMultiplier === 0) {
      return 0;
    }
    
    // GTPの場合は表示値ベースのマルチプライヤーを使用
    const multiplierForPercentage = pouch.isGroupTotalPouch
      ? getDisplayMultiplier(layerEffectiveMultiplier)
      : layerEffectiveMultiplier;
    const absMultiplierForPercentage = Math.abs(multiplierForPercentage);
    
    // 表示値が0の場合は0を返す（塗りつぶしなし）
    if (absMultiplierForPercentage === 0) {
      return 0;
    }
    
    // 等価な正の値に変換（÷0.8 → ×1.25）
    const equivalentPositiveValue = multiplierForPercentage > 0 ? multiplierForPercentage : 1 / absMultiplierForPercentage;
    
    // InfinityやNaNの場合は0を返す
    if (!isFinite(equivalentPositiveValue)) {
      return 0;
    }
    
    // 小数部分を取得（例：1.25 → 0.25）
    const fractionalPart = equivalentPositiveValue - Math.floor(equivalentPositiveValue);
    if (fractionalPart === 0) {
      // 整数の場合は100.1
      return 100.1;
    } else {
      // 小数部分 * 100（例：0.25 * 100 = 25）
      return Math.round(fractionalPart * 100);
    }
  }, [absMultiplier, layerEffectiveMultiplier, pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal, getDisplayMultiplier]);

  // アニメーション用のmultiplier-percentage値の状態
  const [animatedPercentage, setAnimatedPercentage] = useState(isInitialLoading || isSheetSwitching ? 100.1 : targetMultiplierPercentage);
  const animatedPercentageRef = useRef(animatedPercentage);
  animatedPercentageRef.current = animatedPercentage;

  // multiplier-percentageのアニメーション用ref
  const percentageAnimFrameRef = useRef<number | null>(null);
  const percentageStartTimeRef = useRef<number | null>(null);
  const prevLayerSwitchNonceForPctRef = useRef(layerSwitchNonce);

  // multiplier-percentage値のアニメーション
  useEffect(() => {
    // 現在進行中のアニメーションをキャンセル
    if (percentageAnimFrameRef.current) {
      cancelAnimationFrame(percentageAnimFrameRef.current);
      percentageAnimFrameRef.current = null;
    }

    const isLayerSwitchTick = prevLayerSwitchNonceForPctRef.current !== layerSwitchNonce;
    prevLayerSwitchNonceForPctRef.current = layerSwitchNonce;
    const isLayerSwitchConicAnim =
      isLayerSwitchTick &&
      !isInitialLoading &&
      layerSwitchFromLayerId != null &&
      pouchLayerShouldSlideMultiplier(pouch, layerSwitchFromLayerId, resolvedActiveLayerId);

    // アニメーション初期値を決定
    let startPercentage: number;
    if (isLayerSwitchConicAnim) {
      startPercentage = animatedPercentageRef.current;
    } else if (isInitialLoading) {
      // 初期ロード時は、掛け算（正の値）なら0から、割り算（負の値）なら100.1から
      startPercentage = pouch.multiplier > 1 || (pouch.multiplier < 0 && pouch.multiplier > -1) ? 0 : 100.1;
    } else {
      // 前回と今回のマルチプライヤーの整数部分を比較（等価な正の値で統一）
      let prevIntegerPart: number;
      let currentIntegerPart: number;
      
      // 前回の値は ref が既に表示値（前フレームの表示空間）
      const prevDisplayMultiplier = prevMultiplierRef.current;
      const prevEquivalentPositive = prevDisplayMultiplier === 0 ? 0 :
        (prevDisplayMultiplier > 0 ? prevDisplayMultiplier : 1 / Math.abs(prevDisplayMultiplier));
      prevIntegerPart = Math.floor(prevEquivalentPositive);
      
      const currentEquivalentPositive = displayLayerEffectiveMultiplier === 0 ? 0 :
        (displayLayerEffectiveMultiplier > 0
          ? displayLayerEffectiveMultiplier
          : 1 / Math.abs(displayLayerEffectiveMultiplier));
      currentIntegerPart = Math.floor(currentEquivalentPositive);
      
      // ターゲットが.0（整数）の場合の特別処理
      if (currentEquivalentPositive === Math.floor(currentEquivalentPositive)) {
        const targetInteger = Math.floor(currentEquivalentPositive);
        // 0の次の整数（1）への変化の場合は、例外的に同じとして扱う（例：0.8→1.0）
        if (targetInteger === 1 && prevIntegerPart === 0) {
          currentIntegerPart = prevIntegerPart;
        }
        // その他の整数部分に実際の変化がない場合も同じとして扱う（例：0.9→1.0）
        else if (targetInteger === prevIntegerPart) {
          currentIntegerPart = prevIntegerPart;
        }
      }
      
      // デバッグログ
      
      
      
      // まず、小数→整数の特別処理をチェック
      const wasInteger = prevEquivalentPositive === Math.floor(prevEquivalentPositive);
      const isInteger = currentEquivalentPositive === Math.floor(currentEquivalentPositive);
      
      if (!wasInteger && isInteger) {
        // 小数から.0への変化
        // 次の整数への変化（例：2.5→3, 0.8→1）はanimatedPercentageから継続
        const prevIntegerFloor = Math.floor(prevEquivalentPositive);
        const isNextInteger = currentEquivalentPositive === prevIntegerFloor + 1;
        
        if (isNextInteger) {
          startPercentage = animatedPercentage;
          
        } else {
          startPercentage = 100.1;
          
        }
      } else if (wasInteger && !isInteger && currentIntegerPart === 0) {
        // 1→1未満の変化の場合は100.1から開始
        startPercentage = 100.1;        
      } else if (wasInteger && !isInteger) {
        // .0から小数への変化は0から開始
        startPercentage = 0;        
      } else if (prevIntegerPart < currentIntegerPart) {
        // 整数部分が増える場合は0から開始
        startPercentage = 0;        
      } else if (prevIntegerPart > currentIntegerPart) {
        // 整数部分が減る場合は100.1から開始
        startPercentage = 100.1;        
      } else {
        // その他は現在の値から
        startPercentage = animatedPercentage;        
      }
    }
    const endPercentage = targetMultiplierPercentage;

    if (isLayerSwitchConicAnim && startPercentage === endPercentage) {
      setAnimatedPercentage(endPercentage);
      prevMultiplierRef.current = displayLayerEffectiveMultiplier;
      return;
    }

    // 同じ値の場合はアニメーションしない
    // ただし、100.1でない かつ 前後の整数部分が同じ という条件も追加
    // または、整数同士の変化（1.0→2.0など）の場合もアニメーションしない
    
    
    // 整数同士の変化かチェック（表示値ベース）
    const prevDisplayMultiplierForSkip = prevMultiplierRef.current;
    const prevEquivalentPositiveForSkip = prevDisplayMultiplierForSkip > 0 ?
      prevDisplayMultiplierForSkip :
      1 / Math.abs(prevDisplayMultiplierForSkip);
    
    const currentEquivalentPositiveForSkip = displayLayerEffectiveMultiplier > 0 ?
      displayLayerEffectiveMultiplier :
      1 / Math.abs(displayLayerEffectiveMultiplier);
    const isIntegerToInteger = (prevEquivalentPositiveForSkip === Math.floor(prevEquivalentPositiveForSkip)) && 
                              (currentEquivalentPositiveForSkip === Math.floor(currentEquivalentPositiveForSkip));
    
    const shouldSkipAnimation = !isLayerSwitchConicAnim && ((startPercentage === endPercentage && 
      endPercentage !== 100.1 && 
      !isInitialLoading && 
      (() => {
        // 前後の整数部分を再計算して比較（等価な正の値で統一）
        let prevInt: number, currentInt: number;
        
        // 前回の値を等価な正の値に変換（表示値ベース）
        const prevDisplayMultiplierForSkipCheck = prevMultiplierRef.current;
        const prevEquivalentPositiveForSkipCheck = prevDisplayMultiplierForSkipCheck > 0 ?
          prevDisplayMultiplierForSkipCheck :
          1 / Math.abs(prevDisplayMultiplierForSkipCheck);
        prevInt = Math.floor(prevEquivalentPositiveForSkipCheck);
        
        const currentEquivalentPositiveForSkipCheck = displayLayerEffectiveMultiplier > 0 ?
          displayLayerEffectiveMultiplier :
          1 / Math.abs(displayLayerEffectiveMultiplier);
        currentInt = Math.floor(currentEquivalentPositiveForSkipCheck);
        
        // ターゲットが.0（整数）の場合の特別処理
        if (currentEquivalentPositiveForSkipCheck === Math.floor(currentEquivalentPositiveForSkipCheck)) {
          const targetInteger = Math.floor(currentEquivalentPositiveForSkipCheck);
          // 次の整数への変化（例：2.5→3, 0.8→1）は同じとして扱う
          const isNextInteger = targetInteger === prevInt + 1;
          if (isNextInteger) {
            currentInt = prevInt;
          }
          // その他の整数部分に実際の変化がない場合も同じとして扱う（例：0.9→1.0）
          else if (targetInteger === prevInt) {
            currentInt = prevInt;
          }
        }
        
        const integersSame = prevInt === currentInt;
        
        return integersSame;
      })()) || (isIntegerToInteger && !isInitialLoading));
    
    
    
    if (shouldSkipAnimation) {
      setAnimatedPercentage(endPercentage);
      return;
    }

    // アニメーション期間
    const duration = isLayerSwitchConicAnim
      ? LAYER_SLIDE_MS
      : isInitialLoading && !isSheetSwitching
        ? 600
        : 300;

    // アニメーション関数
    const animatePercentage = (timestamp: number) => {
      if (!percentageStartTimeRef.current) {
        percentageStartTimeRef.current = timestamp;
      }

      const elapsed = timestamp - percentageStartTimeRef.current;
      
      // アニメーション終了
      if (elapsed >= duration) {
        setAnimatedPercentage(endPercentage);
        percentageAnimFrameRef.current = null;
        percentageStartTimeRef.current = null;
        // アニメーション完了時に前回の値を現在の値で更新（表示値ベース）
        prevMultiplierRef.current = displayLayerEffectiveMultiplier;
        return;
      }

      // 進捗率を計算（0～1）
      const progress = elapsed / duration;
      
      // 現在の値を計算（線形補間）
      const currentPercentage = Math.round(startPercentage + (endPercentage - startPercentage) * progress);
      
      // 状態を更新
      setAnimatedPercentage(currentPercentage);
      
      // 次のフレームを要求
      percentageAnimFrameRef.current = requestAnimationFrame(animatePercentage);
    };

    // アニメーション開始
    percentageStartTimeRef.current = null;
    percentageAnimFrameRef.current = requestAnimationFrame(animatePercentage);

    // クリーンアップ
    return () => {
      if (percentageAnimFrameRef.current) {
        cancelAnimationFrame(percentageAnimFrameRef.current);
      }
    };
  }, [
    targetMultiplierPercentage,
    isInitialLoading,
    layerEffectiveMultiplier,
    displayLayerEffectiveMultiplier,
    layerSwitchNonce,
    layerSwitchFromLayerId,
    resolvedActiveLayerId,
    pouch,
  ]);
  
  // パウチの色を取得（IDからカラーコードを取得）
  const pouchColorValue = useMemo(() => {
    const colorObj = POUCH_COLORS.find(c => c.id === pouch.color);
    return colorObj ? colorObj.value : '#f7f7f7';
  }, [pouch.color]);
  
  /** サイズ計算用の字数（先頭の ASCII `-` のみ桁に含めない。数値は Math.abs 済み） */
  const countCharsForPouchSize = (s: string): number => s.replace(/^-/, '').length;

  /** サイズ計算用: 整数部の桁数（表示上の `.` と小数部分は数えない） */
  const getDigitCount = (value: number): number => {
    const intPart = Math.trunc(Math.abs(value));
    return Math.max(1, intPart.toString().length);
  };

  // 現在の値の桁数に基づいてサイズを計算
  const calculateSize = (value: number): number => {
    if (shouldShowMainTimeDisplay && (pouch.isGroupTotalPouch || pouch.isTime)) {
      // 時間 GTP / 時間パウチ: 確定表示の h:mm 字数のみ（分の整数値は使わない）
      const timeStr = getMainTimeDisplayString();
      const charCount = countCharsForPouchSize(timeStr);
      const scale = 1 + (Math.max(0, charCount - 4.5) * DIGIT_SCALE_FACTOR);
      return BASE_SIZE * scale * TIME_POUCH_DISPLAY_SIZE_MULTIPLIER;
    }
    if (pouch.isGroupTotalPouch) {
      const digitCount = getDigitCount(value);
      const scale = 1 + (Math.max(0, digitCount - 3) * DIGIT_SCALE_FACTOR_GTP);
      return BASE_SIZE_GTP * scale;
    }
    // 通常のパウチの場合は値の桁数を使用
    const digitCount = getDigitCount(value);
    const scale = 1 + (Math.max(0, digitCount - 3) * DIGIT_SCALE_FACTOR);
    return BASE_SIZE * scale;
  };

  // 表示する値を計算
  const displayValue = useMemo(() => {
    if (tempValue !== null) {
      return tempValue;
    }
    return displayValueForLayer();
  }, [tempValue, displayValueForLayer]);

  /** パウチサイズ計算の基準値（GTP は groupTotalValue、他は displayValue） */
  const pouchSizeBasis = useMemo(() => {
    if (tempValue !== null) {
      return tempValue;
    }
    if (pouch.isGroupTotalPouch) {
      return groupTotalValue * (pouch.oppositeToTotal ? -1 : 1);
    }
    return displayValue;
  }, [
    tempValue,
    pouch.isGroupTotalPouch,
    pouch.oppositeToTotal,
    groupTotalValue,
    displayValue,
  ]);

  // 編集中の値のサイズを管理
  const [currentSize, setCurrentSize] = useState(() => calculateSize(pouch.value));

  // 値が変更されたときにサイズを更新（時間・時間GTP以外）
  useEffect(() => {
    if (!pouch.isTime && !isTimeGtpForLayout(pouch.isGroupTotalPouch, shouldShowMainTimeDisplay)) {
      setCurrentSize(calculateSize(pouchSizeBasis));
    }
  }, [pouchSizeBasis, pouch.isTime, pouch.isGroupTotalPouch, shouldShowMainTimeDisplay]);

  const displayMultiplier = useMemo(() => {
    if (tempMultiplier !== null) {
      return tempMultiplier;
    }
    return layerEffectiveMultiplier;
  }, [tempMultiplier, layerEffectiveMultiplier]);

  // マルチプライヤー表示用の値とシンボルを計算
  const getMultiplierDisplay = useCallback((multiplier: number) => {
    const absMultiplier = Math.abs(multiplier);
    // GTPの場合は*と/を使用
    const isGtp = pouch.isGroupTotalPouch;
    
    let displayMultiplier = absMultiplier;
    if (isGtp && includesSelf) {
      const disp = getInclusiveGtpDisplayMultiplier(pouch, multiplier, true);
      displayMultiplier = Math.abs(disp);
    }
    
    // 絶対値が1以上、または整数の場合は通常表示
    if (displayMultiplier >= 1 || displayMultiplier === Math.floor(displayMultiplier)) {
      return {
        value: displayMultiplier,
        symbol: isGtp ? '*' : '×',
        isDivision: multiplier < 0,
        showSymbolAfter: false  // ×/*は数字の前に表示
      };
    }
    // 時間パウチ：1未満の小数は % ではなく 0.75× のように表示
    if (pouch.isTime) {
      const roundedDecimal = Math.round(displayMultiplier * 100) / 100;
      return {
        value: roundedDecimal,
        symbol: isGtp ? '*' : '×',
        isDivision: multiplier < 0,
        showSymbolAfter: false,
      };
    }
    // 絶対値が1未満で小数の場合はパーセント表示
    const percentage = displayMultiplier * 100;
    // 浮動小数点の精度問題を修正：小数点以下2桁で丸める
    const roundedPercentage = Math.round(percentage * 100) / 100;
    return {
      value: roundedPercentage,
      symbol: '%',
      isDivision: multiplier < 0,
      showSymbolAfter: true  // %は数字の後に表示
    };
  }, [pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal, pouch.isTime]);

  const multiplierDisplay = getMultiplierDisplay(displayMultiplier);

  const displayRoundUnit = useMemo(() => {
    if (currentSheet && pouchUsesLayerValues(currentSheet, pouch)) {
      return getPouchLayerRoundUnit(pouch, resolveActiveLayerId(currentSheet));
    }
    return pouch.roundUnit;
  }, [pouch, currentSheet]);

  const handleMultiplierValueClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (pouch._isDragging || isBalancingPouch) return;
      if (e.shiftKey) {
        if (displayRoundUnit) {
          if (onUpdateMultiplier) {
            onUpdateMultiplier(pouch.id, 1, false, null);
          }
          return;
        }
        const currentMultiplier = layerEffectiveMultiplier;
        if (!Number.isInteger(currentMultiplier)) {
          if (onSetPouchError) onSetPouchError(pouch.id, true);
          return;
        }
        const newValue = Math.abs(currentMultiplier) * (pouch.value < 0 ? -1 : 1);
        const newMultiplier = Math.abs(pouch.value) * (currentMultiplier < 0 ? -1 : 1);
        if (onUpdateValueAndMultiplier) {
          onUpdateValueAndMultiplier(
            pouch.id,
            newValue,
            newMultiplier,
            HistoryActionType.EDIT_POUCH_VALUE,
            false,
            undefined,
            undefined,
            undefined,
            pouch.isTime,
          );
        }
        return;
      }
      if (pouch.isTime) {
        valueRef.current = 60;
        multiplierRef.current = 1;
        setTempValue(60);
        setTempMultiplier(1);
        if (onUpdateValueAndMultiplier) {
          onUpdateValueAndMultiplier(pouch.id, 60, 1, HistoryActionType.EDIT_POUCH_VALUE);
        }
        return;
      }
      if (onUpdateMultiplier) {
        onUpdateMultiplier(pouch.id, 1);
      }
    },
    [
      pouch._isDragging,
      pouch.id,
      pouch.value,
      pouch.isTime,
      isBalancingPouch,
      displayRoundUnit,
      layerEffectiveMultiplier,
      onUpdateMultiplier,
      onSetPouchError,
      onUpdateValueAndMultiplier,
    ],
  );

  const multiplierCounterDuration = useMemo(() => {
    if (isInitialLoading) return 600;
    if (pouch._animateMultiplier && !isDecreasePressed && !isIncreasePressed) return 300;
    return 0;
  }, [isInitialLoading, pouch._animateMultiplier, isDecreasePressed, isIncreasePressed]);

  /** 丸めボタン連打中は temp を優先（tempMultiplier と同型。null = 永続値） */
  const effectiveDisplayRoundUnit = useMemo(() => {
    if (tempRoundUnit !== null) {
      return tempRoundUnit > 0 ? tempRoundUnit : undefined;
    }
    return displayRoundUnit;
  }, [tempRoundUnit, displayRoundUnit]);

  const readRoundUnitForOp = useCallback((): number | undefined => {
    if (roundUnitRef.current !== undefined) {
      return roundUnitRef.current ?? undefined;
    }
    if (tempRoundUnit !== null) {
      return tempRoundUnit > 0 ? tempRoundUnit : undefined;
    }
    return displayRoundUnit;
  }, [tempRoundUnit, displayRoundUnit]);

  const setLocalRoundUnit = useCallback((next: number | null) => {
    roundUnitRef.current = next;
    setTempRoundUnit(next === null ? 0 : next);
  }, []);

  // 表示値の純マルチを計算する関数（no-multiplierクラス判定用）
  const getDisplayPureMultiplier = useCallback((multiplier: number) => {
    const absMultiplier = Math.abs(multiplier);
    if (pouch.isGroupTotalPouch && includesSelf) {
      const disp = getInclusiveGtpDisplayMultiplier(pouch, multiplier, true);
      const a = Math.abs(disp);
      return disp < 0 ? 1 / a : a;
    }
    return multiplier < 0 ? 1 / absMultiplier : absMultiplier;
  }, [pouch.isGroupTotalPouch, includesSelf]);

  const showMultiplierControl =
    getDisplayPureMultiplier(
      tempMultiplier !== null ? tempMultiplier : layerEffectiveMultiplier
    ) !== 1 || !!effectiveDisplayRoundUnit || shouldShowMainTimeDisplay;

  const pureMultForLayout = getDisplayPureMultiplier(
    tempMultiplier !== null ? tempMultiplier : layerEffectiveMultiplier,
  );
  const hasRoundUnitDisplay =
    effectiveDisplayRoundUnit !== undefined && effectiveDisplayRoundUnit > 0;
  const showRoundUnitSlot = true;
  // pure-round-zero は「×1 かつ 丸めなし(~0)」の折りたたみ表示専用。
  // 丸めあり(~N)の折りたたみは pure-mult-one が担当する。
  // hasRoundUnitDisplay だけで真にすると、乗数≥2＋丸めありでも main 行が
  // 非hoverで opacity:0 になり乗数(マルチ)が消える回帰になるため条件を厳密化する。
  const pureRoundZero =
    showRoundUnitSlot && !hasRoundUnitDisplay && pureMultForLayout === 1;
  const pureMultOne = hasRoundUnitDisplay && pureMultForLayout === 1;
  const displayShowMultiplierControl = useLayerSlideDeferred(
    showMultiplierControl,
    layerSwitchDirection,
    layerSwitchNonce,
    multLayerSlideEnabled,
  );
  const displayPureMultOne = useLayerSlideDeferred(
    pureMultOne,
    layerSwitchDirection,
    layerSwitchNonce,
    multLayerSlideEnabled,
  );
  const displayPureRoundZero = useLayerSlideDeferred(
    pureRoundZero,
    layerSwitchDirection,
    layerSwitchNonce,
    multLayerSlideEnabled,
  );

  /** IME の組み立てで本文にかなが混ざるのを防ぐ（keydown 直挿入と二重にならないよう） */
  const handleValueBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const ev = e.nativeEvent as InputEvent;
    const t = ev.inputType;
    if (t === 'insertCompositionText' || t === 'insertFromComposition') {
      e.preventDefault();
      return;
    }
    // 合成中の削除のみ抑止。未合成なのに deleteComposition* と誤報されるとモバイルで Backspace が効かない
    if (t === 'deleteCompositionText' || t === 'deleteByComposition') {
      if (isComposingRef.current) {
        e.preventDefault();
      }
    }
  }, []);

  // 編集中の値変更時にもサイズを更新
  const handleEditValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // 常に許可セットでフィルタ（IME 未確定のかなはここで落ちる。直挿入は keydown 側）
    const newValue = filterPouchExpressionInput(raw, !!pouch.isTime);
    
    // ドット3連続の検出とinput typeのトグル
    if (newValue.includes('...')) {
      setInputType(prevType => prevType === 'decimal' ? 'text' : 'decimal');
      // ドット3連続を除去
      const cleanedValue = newValue.replace(/\.{3,}/g, '');
      setEditValue(cleanedValue);
      return;
    }
    
    setEditValue(newValue);
    if (!pouch.isTime && newValue) {
      const parsedValue = parseFloat(newValue);
      if (!isNaN(parsedValue)) {
        const newSize = calculateSize(parsedValue);
        setCurrentSize(newSize);
      }
    }
  };
  
  // すべてのスタイルをコンテナに適用（位置情報とその他のスタイルも含む）
  const containerStyle: React.CSSProperties = {
    left: `${pouch.x}px`,
    top: `${pouch.y}px`,
    position: 'absolute',
    zIndex: pouch.zIndex || MIN_POUCH_Z_INDEX,
    animation: pouch._isDeleting ? 'pouch-delete 0.3s cubic-bezier(.28,-0.42,.7,1) forwards' : pouch._animateFromZero ? 'pouch-appear 0.3s cubic-bezier(.57,.41,.66,1.38) forwards' : 'pouch-appear 0.3s cubic-bezier(.57,.41,.66,1.38) forwards',
    willChange: 'transform, left, top',
    '--multiplier-percentage': `${animatedPercentage}%`,
    '--pouch-color': pouchColorValue, // カラーIDに対応するカラーコードを使用
    ...(pouch.isGroupTotalPouch && referencedGroupColor && { '--group-color': referencedGroupColor }), // GTPの参照元グループの色
    width: isTimeGtpForLayout(pouch.isGroupTotalPouch, shouldShowMainTimeDisplay)
      ? `${currentSize}px`
      : pouch.isGroupTotalPouch
        ? `${currentSize * GTP_WIDTH_FACTOR}px`
        : `${currentSize}px`,
    height: pouch.isGroupTotalPouch ? `${getGtpContainerHeight(currentSize)}px` : `${currentSize}px`,
  } as React.CSSProperties;
  
  // ドラッグ中かどうかのクラス名
  const classNames = [];
  if (pouch._isDragging) {
    classNames.push('is-dragging');
  }
  if (isDivisionMode) {
    classNames.push('division-mode');
  }
  if (pouch._isCloneSource) {
    classNames.push('is-clone-source');
  }
  if (pouch._isCloning) {
    classNames.push('is-cloning');
  }
  if (isTableSelected) {
    classNames.push('table-selected');
  }
  if (showNoValue) {
    classNames.push('no-value');
  }
  
  // すべてのタイマーをクリアする
  const clearAllTimers = useCallback(() => {
    clearRepeatTimers({ longPressTimerRef, repeatTimerRef });
    isProcessingRef.current = false;
  }, []);
  
  // 連続更新を開始する関数
  const startRepeating = useCallback((callback: () => void) => {

    
    // 既存のタイマーをクリア
    if (repeatTimerRef.current !== null) {
      window.clearTimeout(repeatTimerRef.current);
    }
    
    // 即座に1回実行してから、インターバルを開始
    callback();
    
    // 繰り返し間隔で実行（この関数は新しいhandlePressStartで使われないため、コメントアウト）
    // repeatTimerRef.current = window.setInterval(() => {
    //   if (!isProcessingRef.current) {
    //     clearAllTimers();
    //     return;
    //   }
    //   callback();
    // }, REPEAT_INTERVAL);
  }, [clearAllTimers]);

  // 乗数を減少させる
  const decreaseMultiplierValue = useCallback(() => {
    if (!onUpdateMultiplier) return;
    
    const currentMultiplier = multiplierRef.current;
    const steps =
      pouch.isGroupTotalPouch && !pouch.isTime ? GTP_MULTIPLIER_STEPS : MULTIPLIER_STEPS;
    
    // 短押し時は配列ベースの関数を使用
    const newMultiplier = getPreviousMultiplier(currentMultiplier, steps);
    
    // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
    if (Math.abs(newMultiplier) > 9999999999) {
      console.error('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
      return; // エラーの場合は更新しない
    }
    
    // 一時的な表示用の値を更新
    setTempMultiplier(newMultiplier);
    multiplierRef.current = newMultiplier;
    
    // 実際の値を更新
    onUpdateMultiplier(pouch.id, normalizeMultiplier(newMultiplier));
  }, [pouch.id, pouch.isGroupTotalPouch, pouch.isTime, onUpdateMultiplier]);

  // 乗数を増加させる
  const increaseMultiplierValue = useCallback(() => {
    if (!onUpdateMultiplier) return;
    
    const currentMultiplier = multiplierRef.current;
    const steps =
      pouch.isGroupTotalPouch && !pouch.isTime ? GTP_MULTIPLIER_STEPS : MULTIPLIER_STEPS;
    
    // 短押し時は配列ベースの関数を使用
    const newMultiplier = getNextMultiplier(currentMultiplier, steps);
    
    // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
    if (Math.abs(newMultiplier) > 9999999999) {
      console.error('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
      return; // エラーの場合は更新しない
    }
    
    // 一時的な表示用の値を更新
    setTempMultiplier(newMultiplier);
    multiplierRef.current = newMultiplier;
    
    // 実際の値を更新
    onUpdateMultiplier(pouch.id, normalizeMultiplier(newMultiplier));
  }, [pouch.id, pouch.isGroupTotalPouch, pouch.isTime, onUpdateMultiplier]);

  // 値の編集用の状態
  const [isEditing, setIsEditing] = useState(false);
  /** キーボード新規の初回値編集（ref は再レンダーしないため UI 同期用） */
  const [keyboardNewPouchFirstEditUi, setKeyboardNewPouchFirstEditUi] = useState(false);
  useLayoutEffect(() => {
    setKeyboardNewPouchFirstEditUi(
      !!(isEditing && keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id),
    );
  }, [isEditing, pouch.id, keyboardGraceAppendBump, keyboardNewPouchFirstValueEditPouchIdRef]);
  const [isJustEdited, setIsJustEdited] = useState(false);
  const [editValue, setEditValue] = useState(pouch.isGroupTotalPouch ? (() => {
    const display = getMultiplierDisplay(displayMultiplier);
    return display.isDivision ? `/${display.value}${display.showSymbolAfter ? display.symbol : ''}` : `*${display.value}${display.showSymbolAfter ? display.symbol : ''}`;
  })() : pouch.isTime ? (() => {
    const actualValue = getTimeMinutesForDisplay();
    const totalMinutes = Math.round(Math.abs(actualValue));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const sign = actualValue < 0 ? '-' : '';
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  })() : pouch.value.toString());
  const [inputType, setInputType] = useState<'text' | 'decimal'>('decimal');
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;

  // 時間パウチ / 時間 GTP: 確定表示 h:mm にサイズを追従（編集中の入力文字列は反映しない）
  useEffect(() => {
    if (pouch.isTime || isTimeGtpForLayout(pouch.isGroupTotalPouch, shouldShowMainTimeDisplay)) {
      const newSize = calculateSize(displayValue);
      setCurrentSize(newSize);
    }
  }, [
    pouch.isTime,
    pouch.isGroupTotalPouch,
    pouch.value,
    pouch.multiplier,
    pouch.oppositeToTotal,
    pouch.layerValues,
    pouch.roundUnit,
    displayValue,
    tempValue,
    tempMultiplier,
    groupTotalValue,
    shouldShowMainTimeDisplay,
    isBalancingPouch,
    currentSheet,
  ]);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevValueRef = useRef(pouch.value);
  const originalEditValueRef = useRef<string>('');
  const isCancelledRef = useRef<boolean>(false);
  const originalSizeRef = useRef<number>(0);
  /** 計算エリアキーボード新規→値編集開始時のみ true（初回フォーカスで全選択しない） */
  const valueEditFocusCaretAtEndRef = useRef(false);
  /** Enter 空作成など selectAllOnFocus のとき、コミット後に確実に全選択（制御入力の同期ずれ対策） */
  const pendingSelectAllValueInputRef = useRef(false);


  // 文字の幅を取得（全角は2、半角は1）
  const getCharWidth = useCallback((char: string): number => {
    const code = char.charCodeAt(0);
    // 半角文字の範囲（ASCII、半角カナ）
    if (
      (code >= 0x0020 && code <= 0x007E) || // 基本ASCII
      (code >= 0xFF61 && code <= 0xFF9F)    // 半角カナ
    ) {
      return 1;
    }
    // それ以外は全角として扱う
    return 2;
  }, []);

  // 文字列の表示幅を計算（全角は2、半角は1）
  const getTextWidth = useCallback((text: string): number => {
    let width = 0;
    for (const char of text) {
      width += getCharWidth(char);
    }
    return width;
  }, [getCharWidth]);

  // 文字列を最大幅で切り詰める関数（全角は2、半角は1でカウント）
  const truncateText = useCallback((text: string, maxWidth: number = 140) => {
    if (!text) return text;
    
    let currentWidth = 0;
    let result = '';
    
    for (const char of text) {
      const charWidth = getCharWidth(char);
      if (currentWidth + charWidth > maxWidth) {
        return result + '...';
      }
      result += char;
      currentWidth += charWidth;
    }
    
    return text;
  }, [getCharWidth]);

  // GTPのプレースホルダーテキストを計算する関数
  const getPlaceholderText = useCallback(() => {
    let placeholder = '';
    
    if (pouch.title && pouch.title.trim() !== '') {
      return ''; // タイトルがある場合はプレースホルダーなし
    }
    
    // GTPでない場合のみlastExpressionをプレースホルダーとして表示
    if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '') {
      placeholder = pouch.lastExpression;
    } else if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
      if (referencedGroup) {
        // 参照グループのタイトルがある場合はそれを使用
        if (referencedGroup.title && referencedGroup.title.trim() !== '') {
          placeholder = referencedGroup.title;
        } else {
          // 参照グループのプレースホルダーテキストを計算して表示
          placeholder = getReferencedGroupPlaceholderText(referencedGroup);
        }
      }
    }
    
    // placeholderを16文字で切り詰め
    return truncateText(placeholder, 16);
  }, [pouch.title, pouch.lastExpression, pouch.isGroupTotalPouch, pouch.referencedGroupId, allGroups, getReferencedGroupPlaceholderText, truncateText]);

  // 数式記録時と現在の値・マルチプライヤーが異なるかどうかを判定する関数
  const isExpressionOutdated = useCallback(() => {
    return isPouchExpressionOutdatedForSheet(pouch, currentSheet, resolvedActiveLayerId);
  }, [pouch, currentSheet, resolvedActiveLayerId]);

  // タイトル編集用の状態
  const [editTitle, setEditTitle] = useState(pouch.title || '');
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  /** 値／タイトル編集中: 初期は Enter/Esc のみ、2 秒後に色ガイド */
  const editGuideTimerRef = useRef<number | null>(null);
  const [editGuideTier, setEditGuideTier] = useState<1 | 2>(1);

  useEffect(() => {
    if (!isEditing && !isTitleEditing) {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
      setEditGuideTier(1);
      return;
    }
    setEditGuideTier(1);
    if (editGuideTimerRef.current !== null) {
      window.clearTimeout(editGuideTimerRef.current);
      editGuideTimerRef.current = null;
    }
    if (isGuideEnabled) {
      editGuideTimerRef.current = window.setTimeout(() => {
        setEditGuideTier(2);
        editGuideTimerRef.current = null;
      }, GUIDE_HOVER_DELAY_MS);
    }
    return () => {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
    };
  }, [isEditing, isTitleEditing, isGuideEnabled]);

  useLayoutEffect(() => {
    editModeActiveRef.current = isEditing || isTitleEditing;
  }, [isEditing, isTitleEditing]);

  // 値が変更されたときのアニメーション制御
  useEffect(() => {
    if (pouch.value !== prevValueRef.current) {
      prevValueRef.current = pouch.value;
    }
  }, [pouch.value]);

  // 乗数が変更されたことを検出するEffect
  // ボタン操作中は更新しない（連打時にrefが上書きされるのを防ぐ）
  useEffect(() => {
    if (!isButtonOperationRef.current && multiplierRef.current !== layerEffectiveMultiplier) {
      multiplierRef.current = layerEffectiveMultiplier;
    }
  }, [layerEffectiveMultiplier]);

  // シート切替時のアニメーション制御
  useEffect(() => {
    // シート切替時の処理（アニメーション制御は削除）
  }, [pouch._animateFromZero]);

// マルチアニメーションフラグのクリア
useEffect(() => {
  if (!pouch._animateMultiplier) return;
  // Undo/Redo時はタイマーによる更新を無効にする
  const isUndoRedo = document.documentElement.classList.contains('undoredo');
  if (isUndoRedo || isInitialLoading) {
    return;
  }

  // アニメーション時間（600ms）後にフラグをクリア
  const timer = setTimeout(() => {
    if (onUpdateMultiplier) {
      // 多層時間レイヤーでは top-level multiplier（数量共用）ではなく layer 実効値でクリア
      onUpdateMultiplier(pouch.id, layerEffectiveMultiplier, false);
    }
  }, 600);

  return () => clearTimeout(timer);
}, [pouch._animateMultiplier, isInitialLoading, layerEffectiveMultiplier, onUpdateMultiplier, pouch.id]);

  // 値の編集を開始
  const handleValueClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // e.stopPropagation();
    if (pouch._isDragging || isBalancingPouch) {
      return;
    }
    setIsEditing(true);
    setIsJustEdited(true);
    isCancelledRef.current = false; // キャンセルフラグをリセット
    
    // 元の値とサイズを保存
    let newEditValue = (() => {
      // GTPの場合
      if (pouch.isGroupTotalPouch) {
        const display = getMultiplierDisplay(displayMultiplier);
        return display.isDivision ? `/${display.value}${display.showSymbolAfter ? display.symbol : ''}` : `*${display.value}${display.showSymbolAfter ? display.symbol : ''}`;
      }
      
      // 時間パウチの場合：lastExpressionが有効で期限切れでない場合はそれを使用、そうでなければ時間形式で表示
      if (pouch.isTime) {
        if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
          return pouch.lastExpression;
        }
        
        // 増減ボタンで変更直後の場合、tempValue/valueRef.currentを優先
        let actualValue: number;
        if (tempValue !== null) {
          // tempValueがある場合はそれを使用（増減ボタン直後）
          actualValue = tempValue;
        } else if (valueRef.current !== null && valueRef.current !== displayValueForLayer()) {
          actualValue = valueRef.current;
        } else {
          actualValue = getTimeMinutesForDisplay();
        }
        
        const totalMinutes = Math.round(Math.abs(actualValue));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const sign = actualValue < 0 ? '-' : '';
        return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // 通常のパウチの場合：lastExpressionが有効で期限切れでない場合はそれを使用（GTPでは無効）
      if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
        return pouch.lastExpression;
      }
      
      return getPouchEditInitialString(pouch, currentSheet, resolvedActiveLayerId);
    })();
    
    let caretAtEndFromKeyboardSeed = false;
    const seed = keyboardValueEditSeedRef?.current;
    if (seed && seed.pouchId === pouch.id) {
      if (seed.text !== undefined) {
        newEditValue = seed.text;
      }
      caretAtEndFromKeyboardSeed = !!seed.caretAtEnd;
      if (seed.selectAllOnFocus) {
        caretAtEndFromKeyboardSeed = false;
        pendingSelectAllValueInputRef.current = true;
      }
      if (
        !pouch.isTime &&
        !pouch.isGroupTotalPouch &&
        seed.text !== undefined &&
        /^-?\d+(?:\.\d+)?$/.test(seed.text.trim())
      ) {
        const parsed = parseFloat(seed.text);
        if (!isNaN(parsed)) {
          setCurrentSize(calculateSize(Math.abs(parsed)));
        }
      }
      keyboardValueEditSeedRef.current = null;
    }
    valueEditFocusCaretAtEndRef.current = caretAtEndFromKeyboardSeed;
    
    originalEditValueRef.current = newEditValue;
    originalSizeRef.current = currentSize; // 元のサイズを保存
    setEditValue(newEditValue);
    // 編集開始時刻を記録（キーボード表示時のリサイズイベント誤検出防止用）
    editStartTimeRef.current = Date.now();
    // マウスフォーカスフラグをリセット（次のTabキーでのフォーカス移動に備える）
    isMouseFocusRef.current = false;
  }, [pouch.value, pouch.multiplier, pouch.lastExpression, pouch._isDragging, pouch.isGroupTotalPouch, pouch.id, groupTotalValue, displayMultiplier, currentSize, isExpressionOutdated, tempValue, isBalancingPouch, keyboardValueEditSeedRef]);

  // 計算エリアキーボード新規: フォーカス前にキャプチャで溜めた文字を編集値へ合流
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    const ex = keyboardOpenExtraCharsRef?.current;
    if (!ex || ex.pouchId !== pouch.id || !ex.chars) {
      return;
    }
    const add = ex.chars;
    keyboardOpenExtraCharsRef.current = null;
    setEditValue(v => v + add);
  }, [isEditing, pouch.id, keyboardGraceAppendBump, keyboardOpenExtraCharsRef]);

  useLayoutEffect(() => {
    if (!isEditing) {
      pendingSelectAllValueInputRef.current = false;
      return;
    }
    if (!pendingSelectAllValueInputRef.current) {
      return;
    }
    pendingSelectAllValueInputRef.current = false;
    const el = inputRef.current;
    if (el) {
      const len = el.value.length;
      el.setSelectionRange(0, len);
    }
  }, [isEditing]);

  // 重複実行防止用のref
  const isConfirmingRef = useRef(false);






  // 値の正規化関数（ExpressionEvaluator と同じ: 小数第2位）
  const normalizeValue = (value: number): number => roundPouchValue(value);

  // マルチプライヤー用の正規化関数（0.01の位で四捨五入）
  // 時間パウチの場合は正規化をスキップ（分数ベースの計算のため）
  const normalizeMultiplier = (value: number): number => {
    if (pouch.isTime) {
      return value; // 時間パウチの場合は正規化をスキップ
    }
    return Math.round(value * 100) / 100;
  };

  // 値の編集を確定（新版）
  const handleValueConfirm = useCallback(() => {
    if (!isEditing || isConfirmingRef.current) return;
    
    // キャンセルされている場合は編集確定をスキップ
    if (isCancelledRef.current) {
      isConfirmingRef.current = false;
      return;
    }
    
    isConfirmingRef.current = true;
    setIsEditing(false);
    
    // 一時的な値とrefをリセット
    setTempValue(null);
    setTempMultiplier(null);
    valueRef.current = pouch.isTime
      ? getTimeMinutesForDisplay()
      : displayValueForLayer();
    multiplierRef.current = layerEffectiveMultiplier;
    
    try {
      const currentValue = displayValueForLayer();
      const currentMultiplier = layerEffectiveMultiplier;
      
      // 空値チェック：空文字列や空白のみの場合は値を0にする
      if (!editValue || editValue.trim() === '') {
        if (onUpdateValueAndMultiplier) {
          valueRef.current = 0;
          onUpdateValueAndMultiplier(pouch.id, 0, pouch.multiplier, HistoryActionType.EDIT_POUCH_VALUE, true, "", undefined, undefined, pouch.isTime);
        }
        if (keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id) {
          keyboardNewPouchFirstValueEditPouchIdRef.current = null;
        }
        return;
      }
      
      // GTPの値変更チェック：値の変更を試みた場合はエラー
      let finalEditValue = editValue;
      if (pouch.isGroupTotalPouch) {
        // 数字だけの入力は *係数 に展開（% は Table のマルチ列と同じ解釈で数値化してから * を付ける）
        const _gtpTrimmed = editValue.trim();
        if (
          /^[-]?\d+(?:\.\d+)?%?$/.test(_gtpTrimmed) ||
          /^\+(?:\d+(?:\.\d+)?)%$/.test(_gtpTrimmed)
        ) {
          finalEditValue = expandGtpShorthandMultiplierInput(editValue);
        }
        
        // マルチ変更の場合は許可（*2, *-2, /3, /2*3, *-3/2, *190%, +10%, -5%, 10*-2/3 など。乗除の連鎖は通常パウチと同様）
        const multChain = '(?:\\s*[\\*×/÷]\\s*-?\\d+(?:\\.\\d+)?%?)*';
        const roundPostfix = '(?:~[^*×/÷]+)?';
        const isMultiplierChange =
          /^~[^*/]+$/.test(finalEditValue.trim()) ||
          new RegExp(`^[\\*×/÷+\\-]\\s*-?\\d+(?:\\.\\d+)?%?${multChain}${roundPostfix}$`).test(finalEditValue) ||
          new RegExp(`^\\d*\\.?\\d*\\s*[\\*×/÷+\\-]\\s*-?\\d+(?:\\.\\d+)?%?${multChain}${roundPostfix}$`).test(finalEditValue);
        if (!isMultiplierChange) {
          // 値の変更を試みた場合はエラー
          console.warn('GTPで値の変更を試みました。エラー状態を設定します。');
          
          // エラー表示
      if (onSetPouchError) {
        onSetPouchError(pouch.id, true);
      }
          return;
        }
      }
      
      // 統一処理：すべての入力を左から右に順次処理
      const allowZeroMultiplier = !!(pouch.isGroupTotalPouch && !pouch.isTime);
      const result = handleUnifiedExpression(
        finalEditValue,
        currentValue,
        currentMultiplier,
        pouch.isTime,
        allowZeroMultiplier,
      );
    
    // エラー処理：計算に失敗した場合はエラー表示（値は変更しない）
    if (result === null || (result && result.hasError)) {
      console.warn('計算に失敗しました。エラー表示します。値は元のまま保持されます。');
      // エラー表示
      if (onSetPouchError) {
        onSetPouchError(pouch.id, true);
      }
    } else {
      
      // 結果がある場合は1回だけ更新
      if (result.result && result.result.value !== undefined && result.result.multiplier !== undefined) {
        if (onUpdateValueAndMultiplier) {
          // lastExpressionの保存判定
          let lastExpressionToSave: string | undefined;
          let valueWhenLastExpression: number | undefined;
          let multiplyWhenLastExpression: number | undefined;
          let roundUnitWhenLastExpression: number | undefined;
          
          // 演算をしていない単純入力の場合は空文字列を記録、それ以外は数式を記録
          if (result.lastExpression !== undefined) {
            // 単純な数値の場合は空文字列を記録、それ以外は数式を記録
            const isSimpleNumber = /^-?\d+(?:\.\d+)?$/.test(result.lastExpression);
            if (isSimpleNumber) {
              // 単純な数値の場合は空文字列を記録
              lastExpressionToSave = "";
              valueWhenLastExpression = undefined;
              multiplyWhenLastExpression = undefined;
              roundUnitWhenLastExpression = undefined;
            } else {
              // 演算子が含まれている場合は数式を記録
              lastExpressionToSave = result.lastExpression;
              // 数式記録時の値とマルチプライヤーを記録
              valueWhenLastExpression = result.result.value;
              multiplyWhenLastExpression = result.result.multiplier;
              roundUnitWhenLastExpression = result.result.roundUnit;
            }
          } else {
            lastExpressionToSave = undefined;
            valueWhenLastExpression = undefined;
            multiplyWhenLastExpression = undefined;
            roundUnitWhenLastExpression = undefined;
          }
          
          const inferredGtpOpposite = pouch.isGroupTotalPouch ? deriveGtpOppositeToTotal(editValue, result) : undefined;
          const effectiveOppositeForInverse =
            inferredGtpOpposite !== undefined ? inferredGtpOpposite : pouch.oppositeToTotal;
          
          // GTPが参照グループに所属している場合、表示値を内部値に逆算
          let finalValue = result.result.roundUnitOnly ? currentValue : result.result.value;
          let finalMultiplier = result.result.roundUnitOnly ? currentMultiplier : result.result.multiplier;
          
          if (pouch.isGroupTotalPouch && isInReferencedGroup && !result.result.roundUnitOnly) {
            // 表示値の純マルチ値を計算（~N のみ変更時はマルチを触らない）
            const absMultiplier = Math.abs(result.result.multiplier);
            const displayPureMultiplier = result.result.multiplier < 0 ? 1 / absMultiplier : absMultiplier;
            
            // 純マルチ値が1以上の場合は、表示値から調整分を逆算
            if (displayPureMultiplier >= 1) {
              // oppositeToTotalフラグがtrueの場合は+1、falseの場合は-1で逆算（数式確定で更新する値を優先）
              const adjustment = effectiveOppositeForInverse ? 1 : -1;
              const internalPureMultiplier = displayPureMultiplier + adjustment;
              // 内部値のマルチプライヤーに変換
              finalMultiplier = result.result.multiplier < 0 ? -1 / internalPureMultiplier : internalPureMultiplier;
            }
          }
          
          // refを新しい値で更新（次回編集時に正しい値を使うため）
          // 時間パウチは valueRef に実効分数を保持（handlePressEnd の保存ロジック・useEffect と一致）
          valueRef.current = result.result.isTime
            ? getTimePouchEffectiveMinutes({ value: finalValue, multiplier: finalMultiplier, isTime: true })
            : finalValue;
          multiplierRef.current = finalMultiplier;
          
          onUpdateValueAndMultiplier(
            pouch.id,
            finalValue,
            finalMultiplier,
            HistoryActionType.EDIT_POUCH_VALUE,
            !result.result.roundUnitOnly,
            lastExpressionToSave,
            valueWhenLastExpression,
            multiplyWhenLastExpression,
            result.result?.isTime,
            inferredGtpOpposite,
            result.result.clearRoundUnit
              ? null
              : result.result.roundUnit !== undefined
                ? result.result.roundUnit
                : undefined,
            roundUnitWhenLastExpression,
          );
          if (keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id) {
            keyboardNewPouchFirstValueEditPouchIdRef.current = null;
          }
        }
      }
    }
    
    // パウチのサイズを再設定（エラー時も含む）
    const newSize = calculateSize(pouch.value);
    setCurrentSize(newSize);
    
  } catch (error) {
    console.error('Calculation error:', error);
    
    // エラー表示
    if (onSetPouchError) {
      onSetPouchError(pouch.id, true);
    }
    
    // エラー時もパウチのサイズを再設定
    const newSize = calculateSize(pouch.value);
    setCurrentSize(newSize);
  } finally {
    if (onEditConfirm) onEditConfirm();
    setTimeout(() => {
      setIsJustEdited(false);
        isConfirmingRef.current = false;
    }, 10);
    }
  }, [editValue, isEditing, pouch.value, pouch.id, pouch.isGroupTotalPouch, pouch.oppositeToTotal, isInReferencedGroup, onUpdateValue, onUpdateMultiplier, onUpdateValueAndMultiplier, onEditConfirm, keyboardNewPouchFirstValueEditPouchIdRef]);

  const applyEditValueFromKeyboard = useCallback(
    (newValue: string) => {
      setEditValue(newValue);
      if (!pouch.isTime && newValue) {
        const parsedValue = parseFloat(newValue);
        if (!isNaN(parsedValue)) {
          setCurrentSize(calculateSize(parsedValue));
        }
      }
    },
    [pouch.isTime, calculateSize],
  );

  const handleValueCancel = useCallback(() => {
    if (
      keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id &&
      onDeletePouchWithoutHistory
    ) {
      isCancelledRef.current = true;
      keyboardNewPouchFirstValueEditPouchIdRef.current = null;
      onDeletePouchWithoutHistory(pouch.id);
      setIsEditing(false);
      setIsJustEdited(false);
      inputRef.current?.blur();
      return;
    }
    isCancelledRef.current = true;
    setEditValue(originalEditValueRef.current);
    setCurrentSize(originalSizeRef.current);
    setIsEditing(false);
    setIsJustEdited(false);
    inputRef.current?.blur();
  }, [
    pouch.id,
    keyboardNewPouchFirstValueEditPouchIdRef,
    onDeletePouchWithoutHistory,
  ]);

  // タイトルの変更処理
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditTitle(e.target.value);
    setIsTitleEditing(true);
  }, []);

  // タイトルの編集を確定
  const handleTitleConfirm = useCallback(() => {
    if (!isTitleEditing) return;
    
    if (onUpdateTitle) {
      onUpdateTitle(pouch.id, editTitle);
    }
    setIsTitleEditing(false);
    if (onEditConfirm) onEditConfirm();
  }, [pouch.id, editTitle, onUpdateTitle, isTitleEditing, onEditConfirm]);

  // タイトルを更新する処理を修正
  const handleUpdateTitle = useCallback(() => {
    if (!isTitleEditing) return;
    
    if (onUpdateTitle) {
      // タイトル編集用の特別なアクションタイプを追加
      onUpdateTitle(pouch.id, editTitle);
    }
    setIsTitleEditing(false);
    if (onEditConfirm) onEditConfirm();
  }, [pouch.id, editTitle, onUpdateTitle, isTitleEditing, onEditConfirm]);

  /** Esc で blur したとき onUpdateTitle を呼ばず履歴を積まない */
  const titleEscapeCancelRef = useRef(false);
  const handleTitleBlur = useCallback(() => {
    if (titleEditBlurGuardRef.current) {
      return;
    }
    if (titleEscapeCancelRef.current) {
      titleEscapeCancelRef.current = false;
      return;
    }
    handleUpdateTitle();
  }, [handleUpdateTitle]);

  /** タイトル編集開始: state と value を先に揃えてから focus（iOS / iPad の KB 対策） */
  const beginTitleEditing = useCallback(() => {
    titleEditBlurGuardRef.current = true;
    editStartTimeRef.current = Date.now();
    flushSync(() => {
      setIsTitleEditing(true);
    });
    const el = titleInputRef.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() => {
        try {
          el.select();
        } catch {
          /* ignore */
        }
      });
    }
    window.setTimeout(() => {
      titleEditBlurGuardRef.current = false;
    }, 600);
  }, []);

  /** タイトル入力にフォーカスして全選択（編集開始直後用） */
  const focusTitleInputSelectAll = useCallback(() => {
    beginTitleEditing();
  }, [beginTitleEditing]);

  const handleValueGoToTitle = useCallback(() => {
    flushSync(() => {
      handleValueConfirm();
    });
    focusTitleInputSelectAll();
  }, [handleValueConfirm, focusTitleInputSelectAll]);

  const valueKeyboard = usePouchValueKeyboardBinding({
    active: isEditing && !isBalancingPouch,
    isTouchDevice,
    pouchId: pouch.id,
    variant: 'expression',
    isTime: !!pouch.isTime,
    value: editValue,
    onValueChange: applyEditValueFromKeyboard,
    onConfirm: handleValueConfirm,
    onCancel: handleValueCancel,
    onGoToTitle: handleValueGoToTitle,
    inputRef,
  });

  // IME入力中かどうかの状態
  const [isComposing, setIsComposing] = useState(false);

  // キー入力処理（値とタイトル共通）
  const handleKeyDown = useCallback((e: React.KeyboardEvent, isTitle: boolean) => {
    const ne = e.nativeEvent as KeyboardEvent;

    // 値編集中: 色ホットキー（文字は入れない）。DOMの値と選択を先に取り、flushSync で state を同期（queueMicrotask だと親更新に負けることがある）
    if (!isTitle) {
      const action = resolvePouchColorHotkey(e.nativeEvent);
      if (action) {
        const inputEl = inputRef.current;
        const snapshot = inputEl?.value ?? editValueRef.current;
        let selStart = snapshot.length;
        let selEnd = snapshot.length;
        if (inputEl) {
          const s = inputEl.selectionStart;
          const t = inputEl.selectionEnd;
          if (s != null) selStart = s;
          if (t != null) selEnd = t;
        }
        const clampSel = (n: number) => Math.max(0, Math.min(snapshot.length, n));
        const rangeA = clampSel(selStart);
        const rangeB = clampSel(selEnd);

        const afterColorUpdate = () => {
          flushSync(() => {
            setEditValue(snapshot);
          });
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
              el.focus();
              try {
                el.setSelectionRange(rangeA, rangeB);
              } catch {
                /* ignore */
              }
            }
          });
        };

        if (action.type === 'rotate' && onChangeColor) {
          e.preventDefault();
          e.stopPropagation();
          onChangeColor(pouch.id, action.reverse);
          afterColorUpdate();
          return;
        }
        if (action.type === 'direct' && onSetPouchColor) {
          e.preventDefault();
          e.stopPropagation();
          onSetPouchColor(pouch.id, action.colorId);
          afterColorUpdate();
          return;
        }
      }

      // 値編集で Space: 値を確定してタイトル編集へ（パウチ非編集ホバー時の F2/Space と同じ遷移）
      if ((e.key === ' ' || e.code === 'Space') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!isComposingRef.current && !ne.isComposing && !isComposing) {
          e.preventDefault();
          e.stopPropagation();
          flushSync(() => {
            handleValueConfirm();
          });
          focusTitleInputSelectAll();
        }
        return;
      }

      // 値編集共通: 許可された数字・記号は keydown で直挿入（Mac / 外付けキーボード）
      // タッチのソフトキーは自前パッド。二重入力防止のためブラウザ既定は使わない
      if (valueKeyboard.useDirectKeyInsert && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const k = e.key;
        const passThroughKeys = new Set([
          'Tab',
          'Enter',
          'Escape',
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Home',
          'End',
        ]);
        if (!passThroughKeys.has(k) && k.length === 1) {
          if (isAllowedPouchValueInputChar(k)) {
            e.preventDefault();
            e.stopPropagation();
            const el = inputRef.current;
            if (el) {
              const start = el.selectionStart ?? 0;
              const end = el.selectionEnd ?? 0;
              const cur = editValueRef.current;
              const joined = cur.slice(0, start) + k + cur.slice(end);
              const normalized = pouch.isTime
                ? normalizeTimePouchValueInputText(joined)
                : joined;
              const filtered = filterPouchExpressionInput(normalized, !!pouch.isTime);
              setEditValue(filtered);
              const newPos = Math.min(start + 1, filtered.length);
              requestAnimationFrame(() => {
                const inp = inputRef.current;
                if (inp) {
                  try {
                    inp.setSelectionRange(newPos, newPos);
                  } catch {
                    /* ignore */
                  }
                }
              });
            }
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // 数式で許可されない1文字は何も起きないようにする（全選択+アルファベット等で中身が消えるのを防ぐ）
      if (shouldSuppressValueFieldKeyInput(e.nativeEvent)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    // IME 未確定中は以降をスキップ（値編集は Enter 確定・Esc 取消も通す）
    if (isComposingRef.current || ne.isComposing || isComposing) {
      if (!(!isTitle && (e.key === 'Escape' || e.key === 'Enter'))) {
        return;
      }
    }

    // タイトルから Tab: 値の表示 div にフォーカスが当たると isMouseFocusRef の残留で編集に入れないことがあるため、
    // 既定のフォーカス移動を止めて値編集を直接開始する（帳尻パウチは値編集不可のためブラウザ既定の Tab のまま）
    if (e.key === 'Tab' && !e.shiftKey && isTitle) {
      if (pouch._isDragging || isBalancingPouch) {
        return;
      }
      e.preventDefault();
      isMouseFocusRef.current = false;
      handleValueClick(e as unknown as React.MouseEvent);
      return;
    }

    // タイトルで Enter: Tab と同様に値編集へ
    if (e.key === 'Enter' && !e.shiftKey && isTitle) {
      if (pouch._isDragging || isBalancingPouch) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      isMouseFocusRef.current = false;
      handleValueClick(e as unknown as React.MouseEvent);
      return;
    }

    // 値編集で Shift+Enter: Shift+Tab と同様にタイトル編集へ
    if (!isTitle && e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      flushSync(() => {
        handleValueConfirm();
      });
      focusTitleInputSelectAll();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isTitle) {
        handleUpdateTitle();
        titleInputRef.current?.blur();
      } else {
        handleValueConfirm();
        inputRef.current?.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (isTitle) {
        titleEscapeCancelRef.current = true;
        setEditTitle(pouch.title || '');
        setIsTitleEditing(false);
        titleInputRef.current?.blur();
      } else {
        handleValueCancel();
      }
    }
  }, [handleValueConfirm, handleValueCancel, handleUpdateTitle, handleValueClick, focusTitleInputSelectAll, pouch.title, pouch.value, pouch.id, pouch._isDragging, pouch.isTime, isBalancingPouch, isComposing, isTouchDevice, onChangeColor, onSetPouchColor, keyboardNewPouchFirstValueEditPouchIdRef, onDeletePouchWithoutHistory, valueKeyboard.useDirectKeyInsert]);

  // PC・ホバー時: F2/Space でタイトル編集、Enter で値編集
  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    if (isEditing || isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      if (e.key === 'F2' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        focusTitleInputSelectAll();
        return;
      }
      if (e.key === 'Enter') {
        if (isBalancingPouch) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        isMouseFocusRef.current = false;
        handleValueClick(e as unknown as React.MouseEvent);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    isEditing,
    isTitleEditing,
    pouch._isDragging,
    isBalancingPouch,
    handleValueClick,
    focusTitleInputSelectAll,
  ]);

  // PC・パウチホバー時の色ホットキー（値・タイトル編集中は無効）
  useEffect(() => {
    if (isTouchDevice || (!onChangeColor && !onSetPouchColor)) {
      return;
    }
    if (isEditing || isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      const action = resolvePouchColorHotkey(e);
      if (!action) {
        return;
      }
      if (action.type === 'rotate') {
        if (!onChangeColor) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onChangeColor(pouch.id, action.reverse);
        return;
      }
      if (action.type === 'direct' && onSetPouchColor) {
        e.preventDefault();
        e.stopPropagation();
        onSetPouchColor(pouch.id, action.colorId);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    pouch._isDragging,
    isEditing,
    isTitleEditing,
    onChangeColor,
    onSetPouchColor,
  ]);

  // PC・パウチホバー時: Delete / Backspace = 削除ボタン相当（タイトル・値など編集中・編集系フォーカス時は無効）
  useEffect(() => {
    if (isTouchDevice || !onDeletePouch) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.repeat || (e.key !== 'Delete' && e.key !== 'Backspace')) {
        return;
      }
      if (isComposing) {
        return;
      }
      if (isTableKeyboardContextActive()) {
        return;
      }
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditing || isTitleEditing) {
        return;
      }
      if (document.activeElement === titleInputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onDeletePouch(pouch.id);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    onDeletePouch,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    pouch._isDragging,
    isEditing,
    isTitleEditing,
    isComposing,
  ]);


  // 編集確定フラグ用のref
  const justConfirmedRef = useRef(false);
  const confirmTimeoutRef = useRef<number | null>(null);
  

  // キーボードが閉じられたときや画面の状態が変わったときに編集を確定するためのEffect
  useEffect(() => {
    // visibilityChangeイベントのハンドラー
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // 画面が非表示になったときに編集中なら確定
        if (isEditing && inputRef.current) {
          handleValueConfirm();
        }
        if (isTitleEditing && titleInputRef.current) {
          handleTitleConfirm();
        }
      }
    };

    // visualViewportのリサイズイベントのハンドラー（キーボードが閉じられたとき）
    const handleViewportResize = () => {
      // 既存のタイマーをクリア（デバウンス処理）
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
      }
      
      // 編集開始直後の一定時間（500ms）はリサイズイベントを無視
      // これによりキーボードが表示される際の誤検出を防止
      const now = Date.now();
      if (now - editStartTimeRef.current < 500) {
        return;
      }
      
      // デバウンス処理（100ms後に実行）
      resizeTimerRef.current = window.setTimeout(() => {
        if (titleEditBlurGuardRef.current) {
          resizeTimerRef.current = null;
          return;
        }
        // モバイルでキーボードが閉じられたとき、画面の高さが増加する
        if (window.visualViewport) {
          const currentHeight = window.visualViewport.height;
          const prevHeight = prevHeightRef.current;
          
          // 高さが増加した場合のみ処理（キーボードが閉じられた場合）
          // キーボードが表示される時は高さが減少するので、この条件では処理されない
          // 高さの変化が小さすぎる場合（5px未満）は無視（微細なリサイズを除外）
          if (currentHeight > prevHeight && 
              currentHeight >= window.innerHeight * 0.9 && 
              Math.abs(currentHeight - prevHeight) > 5) {
            
            // 編集中であれば確定処理を実行
            if (isEditing && inputRef.current) {
              handleValueConfirm();
              inputRef.current.blur();
            }
            if (isTitleEditing && titleInputRef.current) {
              handleTitleConfirm();
              titleInputRef.current.blur();
            }
          }
          
          // 現在の高さを記録
          prevHeightRef.current = currentHeight;
        }
        
        resizeTimerRef.current = null;
      }, 100);
    };

    // イベントリスナーの登録
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }
    window.addEventListener('resize', handleViewportResize);

    // クリーンアップ関数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
      window.removeEventListener('resize', handleViewportResize);
      
      // リサイズタイマーをクリア
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [isEditing, isTitleEditing, handleValueConfirm, handleTitleConfirm]);

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
    };
  }, []);

  // ボタン押下時の共通処理
  const handleButtonPress = useCallback((e: React.MouseEvent | React.TouchEvent, action: () => void, setPressed: (pressed: boolean) => void) => {
    if (pouch._isDragging) {
      return;
    }
    
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
      // 乗除変更を少し遅らせる
      setTimeout(action, 100);
      return;
    }

    // ボタン状態を更新
    setPressed(true);
    // 個別の長押し状態はhandlePressStartで管理されるため、ここではリセットしない
    isProcessingRef.current = true;
    
    // 即座に1回実行
    action();

    // 長押し判定用タイマーを設定
    if (longPressTimerRef.current === null) {
      longPressTimerRef.current = window.setTimeout(() => {
        if (pouch._isDragging) {
          clearAllTimers();
          setPressed(false);
          return;
        }
        
        // 個別の長押し状態はhandlePressStartで管理されるため、ここでは設定しない
        startRepeating(action);
      }, LONG_PRESS_THRESHOLD);
    }
  }, [startRepeating, clearAllTimers, pouch._isDragging, isEditing, handleValueConfirm]);

  // 減少ボタンのイベントハンドラー
  const handleDecreasePress = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    handleButtonPress(e, decreaseMultiplierValue, setIsDecreasePressed);
  }, [decreaseMultiplierValue, handleButtonPress]);

  // 増加ボタンのイベントハンドラー
  const handleIncreasePress = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    handleButtonPress(e, increaseMultiplierValue, setIsIncreasePressed);
  }, [increaseMultiplierValue, handleButtonPress]);

  /** roundUnit のみ更新（GTP 含め multiLayer 時は handleUpdateMultiplier が layerValues へ書く） */
  const persistRoundUnit = useCallback(
    (next: number | null) => {
      roundUnitRef.current = next;
      setTempRoundUnit(next === null ? 0 : next);
      if (onUpdateMultiplier) {
        onUpdateMultiplier(pouch.id, layerEffectiveMultiplier, false, next);
      }
    },
    [
      pouch.id,
      layerEffectiveMultiplier,
      onUpdateMultiplier,
    ],
  );

  const applyRoundUnitStep = useCallback(
    (increase: boolean, coarse: boolean): boolean => {
      if (pouch._isDragging || isBalancingPouch) return false;
      const mult = layerEffectiveMultiplier;
      const scaledCap = getRoundUnitScaledCap(mult);
      const currentRu = readRoundUnitForOp();
      if (!increase && (currentRu ?? 0) <= 0) {
        return false;
      }
      const next = applyRoundUnitShift(currentRu, scaledCap, increase, coarse);
      if (next === undefined) {
        onSetPouchError?.(pouch.id, true);
        return false;
      }
      if (next === null) {
        setLocalRoundUnit(null);
        return false;
      }
      setLocalRoundUnit(next);
      return true;
    },
    [
      pouch,
      onSetPouchError,
      readRoundUnitForOp,
      getRoundUnitScaledCap,
      layerEffectiveMultiplier,
      isBalancingPouch,
      setLocalRoundUnit,
    ],
  );

  const applyRoundUnitStepByDelta = useCallback(
    (increase: boolean, delta: number): boolean => {
      if (pouch._isDragging || isBalancingPouch) return false;
      const mult = layerEffectiveMultiplier;
      const scaledCap = getRoundUnitScaledCap(mult);
      const currentRu = readRoundUnitForOp();
      const next = applyRoundUnitDelta(currentRu, scaledCap, increase, delta);
      if (next === undefined) {
        onSetPouchError?.(pouch.id, true);
        return false;
      }
      if (next === null) {
        setLocalRoundUnit(null);
        return false;
      }
      setLocalRoundUnit(next);
      return true;
    },
    [
      pouch,
      onSetPouchError,
      readRoundUnitForOp,
      getRoundUnitScaledCap,
      layerEffectiveMultiplier,
      isBalancingPouch,
      setLocalRoundUnit,
    ],
  );

  const applyRoundUnitLongPress = useCallback(
    (increase: boolean): boolean => {
      if (pouch._isDragging || isBalancingPouch) return false;
      const mult = layerEffectiveMultiplier;
      const scaledCap = getRoundUnitScaledCap(mult);
      const currentRu = readRoundUnitForOp();
      if (!increase && (currentRu ?? 0) <= 0) {
        return false;
      }
      const start = startRoundUnitRef.current ?? 0;
      const next = applyRoundUnitLongPressStep(currentRu, start, scaledCap, increase);
      if (next === undefined) {
        onSetPouchError?.(pouch.id, true);
        return false;
      }
      if (next === null) {
        setLocalRoundUnit(null);
        return false;
      }
      setLocalRoundUnit(next);
      return true;
    },
    [
      pouch,
      onSetPouchError,
      readRoundUnitForOp,
      getRoundUnitScaledCap,
      layerEffectiveMultiplier,
      isBalancingPouch,
      setLocalRoundUnit,
    ],
  );

  const flushRoundUnitFromButtonOp = useCallback(() => {
    if (!isRoundUnitButtonOperationRef.current || !onUpdateMultiplier) return;
    const p = pouchRef.current;
    const finalRu = roundUnitRef.current;
    const orig = originalRoundUnitRef.current;
    const finalNorm =
      finalRu !== null && finalRu !== undefined && finalRu > 0 ? finalRu : undefined;
    const origNorm = orig !== undefined && orig !== null && orig > 0 ? orig : undefined;
    if (finalNorm !== origNorm) {
      const mult = getEffectiveMultiplierForPouch(p);
      onUpdateMultiplier(p.id, mult, false, finalNorm ?? null);
    }
  }, [getEffectiveMultiplierForPouch, onUpdateMultiplier]);

  const resetRoundUnitButtonOp = useCallback((restoreOriginal: boolean) => {
    if (restoreOriginal && originalRoundUnitRef.current !== null) {
      const orig = originalRoundUnitRef.current;
      roundUnitRef.current = orig ?? null;
      setTempRoundUnit(orig ?? 0);
    }
    originalRoundUnitRef.current = null;
    startRoundUnitRef.current = null;
    isRoundUnitButtonOperationRef.current = false;
  }, []);

  // ボタンリリース時のハンドラーを拡張
  const handleButtonRelease = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    isProcessingRef.current = false;
    clearAllTimers();
    setIsDecreasePressed(false);
    setIsIncreasePressed(false);
    setIsValueDecreasePressed(false);
    setIsValueIncreasePressed(false);
    // 全ての長押し状態をリセット
    setIsValueDecreaseLongPressed(false);
    setIsValueIncreaseLongPressed(false);
    setIsDecreaseLongPressed(false);
    setIsIncreaseLongPressed(false);
    setIsRoundDecreaseLongPressed(false);
    setIsRoundIncreaseLongPressed(false);
    if (isRoundUnitButtonOperationRef.current) {
      flushRoundUnitFromButtonOp();
      resetRoundUnitButtonOp(false);
    }
    startMultiplierRef.current = null;
    e.preventDefault();
  }, [clearAllTimers, flushRoundUnitFromButtonOp, resetRoundUnitButtonOp]);

  // ドラッグ状態が変わったことを検出するEffect
  useEffect(() => {
    if (pouch._isDragging) {
      clearAllTimers();
      setIsDecreasePressed(false);
      setIsIncreasePressed(false);
      setIsValueDecreasePressed(false);
      setIsValueIncreasePressed(false);
      // 全ての長押し状態をリセット
      setIsValueDecreaseLongPressed(false);
      setIsValueIncreaseLongPressed(false);
      setIsDecreaseLongPressed(false);
      setIsIncreaseLongPressed(false);
      if (isRoundUnitButtonOperationRef.current) {
        resetRoundUnitButtonOp(true);
      }
      startMultiplierRef.current = null;
      handleButtonRelease({ preventDefault: () => {} } as React.MouseEvent);
    }
  }, [pouch._isDragging, clearAllTimers, handleButtonRelease, resetRoundUnitButtonOp]);

  // カラー変更ボタンのクリックハンドラー
  const handleColorChange = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

    if (onChangeColor && !pouch._isDragging) {
      // 修飾キーが押されているかチェック
      let isShiftPressed = false;
      
      if (e.type === 'click' || e.type === 'mousedown' || e.type === 'mouseup') {
        // マウスイベントの場合
        const mouseEvent = e as React.MouseEvent;
        isShiftPressed = mouseEvent.shiftKey || false;
      } else {
        // タッチイベントの場合
        isShiftPressed = (window as any).__shiftKeyPressed || false;
      }     
      
      onChangeColor(pouch.id, isShiftPressed);
    }
    e.preventDefault();
  }, [pouch.id, onChangeColor, isEditing, handleValueConfirm]);

  // 削除ボタンのクリックハンドラー（短押し: 二段階タップ）
  const handleDelete = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

    if (!onDeletePouch || pouch._isDragging) {
      return;
    }

    handleConfirmingDeleteButtonClick(e, () => {
      onDeletePouch(pouch.id);
    });
  }, [pouch.id, onDeletePouch, pouch._isDragging, isEditing, handleValueConfirm]);

  const isSplitDisabled = useMemo(() => {
    if (isBalancingPouch || pouch.isGroupTotalPouch || pouch._isDragging) {
      return true;
    }
    return !canSplitPouchByMultiplier(pouch, currentSheet);
  }, [
    isBalancingPouch,
    pouch,
    pouch.isGroupTotalPouch,
    pouch._isDragging,
    currentSheet,
  ]);

  const handleSplit = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }
    if (process.env.NODE_ENV === 'development') {
      console.debug('[POUCHES split]', 'button click', {
        pouchId: pouch.id,
        isSplitDisabled,
        hasHandler: Boolean(onSplitPouch),
      });
    }
    if (isSplitDisabled || !onSplitPouch) {
      if (
        isSplitDisabled &&
        onSetPouchError &&
        !pouch._isDragging &&
        !isBalancingPouch &&
        !pouch.isGroupTotalPouch
      ) {
        onSetPouchError(pouch.id, true);
      }
      if (process.env.NODE_ENV === 'development') {
        console.debug('[POUCHES split]', 'button abort', {
          reason: isSplitDisabled ? 'disabled' : 'no onSplitPouch handler',
        });
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onSplitPouch(pouch.id);
  }, [isEditing, handleValueConfirm, isSplitDisabled, onSplitPouch, onSetPouchError, pouch.id, pouch._isDragging, isBalancingPouch, pouch.isGroupTotalPouch]);

  // 正負転換の処理を修正
  const handleToggleSign = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

      if (onUpdateValue && !pouch._isDragging) {
        // GTPの場合はoppositeToTotalフラグのみ変更
        if (pouch.isGroupTotalPouch) {
          setTempValue(null);
          setTempMultiplier(null);
          // 値は変更せず、oppositeToTotalフラグのみ更新
          onUpdateValue(pouch.id, pouch.value, HistoryActionType.EDIT_POUCH_VALUE);
          if (onEditConfirm) onEditConfirm();
          e.preventDefault();
          return;
        }
        
        const newValue = -displayValueForLayer();
      
      // 値の範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newValue) > 9999999999) {
        console.warn('正負転換時の値が範囲外です（9999999999を超えています）:', newValue);
        return; // 範囲外の場合は処理を中断
      }
      
      // 増減中の temp が残っていると表示だけ古い符号のままになるためクリアしてから反映
      setTempValue(null);
      setTempMultiplier(null);
      // 正負転換時の処理（アニメーション制御は削除）
      onUpdateValue(pouch.id, newValue, HistoryActionType.EDIT_POUCH_VALUE);
      if (onEditConfirm) onEditConfirm();
      e.preventDefault();
    }
  }, [pouch.id, pouch.value, pouch.isGroupTotalPouch, pouch.oppositeToTotal, onUpdateValue, onEditConfirm, isEditing, handleValueConfirm]);

  const handleToggleBalancingPouch = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (pouch._isDragging || pouch.isGroupTotalPouch) return;
    if (onToggleBalancingPouch) {
      onToggleBalancingPouch(pouch.id);
    }
  }, [pouch.id, pouch._isDragging, pouch.isGroupTotalPouch, onToggleBalancingPouch]);

  // テーブル用のパウチの値を計算（isNegativeの場合は-1をかける）
  const getTableValue = () => {
    return pouch.isNegative ? -Math.abs(pouch.value) : Math.abs(pouch.value);
  }

  // タイトルが変更された時にeditTitleを更新
  useEffect(() => {
    setEditTitle(pouch.title || '');
  }, [pouch.title]);

  // 乗数に応じたレプリカ円を生成（固定4個、activeクラスで制御）
  const renderReplicaCircles = () => {
    const mult = layerEffectiveMultiplier;
    // マルチが0の場合はレプリカを0個にする
    if (mult === 0) {
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={`replica-${i}`}
              className="pouch-replica"
              style={{
                '--replica-offset': `${(i + 1) * 8}px`,
                '--replica-opacity': '0',
              } as React.CSSProperties}
            />
          ))}
        </>
      );
    }
    
    // GTPの場合は表示値ベースのマルチプライヤーを使用
    const multiplierForReplica = pouch.isGroupTotalPouch ? getDisplayMultiplier(mult) : mult;
    const absMultiplierForReplica = Math.abs(multiplierForReplica);
    
    // 表示マルチが0の場合はレプリカを0個にする
    if (absMultiplierForReplica === 0) {
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={`replica-${i}`}
              className="pouch-replica"
              style={{
                '--replica-offset': `${(i + 1) * 8}px`,
                '--replica-opacity': '0',
              } as React.CSSProperties}
            />
          ))}
        </>
      );
    }
    
    // 等価な正の値に変換（÷0.8 → ×1.25）
    const equivalentPositiveValue = multiplierForReplica > 0 ? multiplierForReplica : 1 / absMultiplierForReplica;
    
    // 乗数が1以下の場合はレプリカを非表示（DOMは残す）
    const shouldShowReplicas = equivalentPositiveValue > 1;
    const replicaCount = shouldShowReplicas ? Math.min(equivalentPositiveValue - 1, 4) : 0;
    
    return (
      <>
        {[0, 1, 2, 3].map((i) => {
          const offset = (i + 1) * 8;
          const opacities = [0.7, 0.55, 0.4, 0.25];
          const isActive = i < replicaCount;
          
          return (
            <div
              key={`replica-${i}`}
              className={`pouch-replica ${isActive ? 'active' : ''}`}
              style={{
                zIndex: -i - 1,
                '--replica-offset': `${offset}px`,
                '--replica-opacity': `${opacities[i]}`,
              } as React.CSSProperties}
            />
          );
        })}
      </>
    );
  };

  // 時間パウチ専用の増分値を計算する関数
  const calculateTimePouchIncrementValue = (currentValue: number, startValue: number, isIncreasing: boolean, baseUnit: number = 1): number => {
    // 1ステップ目でない場合は、baseUnitを1に強制
    const actualBaseUnit = isFirstStepRef.current ? baseUnit : 1;
    // 1ステップ目を終了
    isFirstStepRef.current = false;
    
    // Shift+クリック/長押しの場合は10分刻みに調整
    if (actualBaseUnit === 10) {
      return shiftSnapIncrementMagnitude(currentValue, isIncreasing, {
        clampDecreaseTargetAtZero: true,
      });
    }
    
    // 最初のクリック/タップの場合は1分を返す
    if (Math.abs(currentValue - startValue) <= 1) {
      return 1;
    }
    
    // 時間パウチ専用の段階的増分システム
    // 次の区切り値を計算（時間パウチ用）
    const getNextTimeBoundary = (value: number, unit: number, increasing: boolean): number => {
      if (increasing) {
        // 増加時は切り上げ（例：91分 → 100分）
        return Math.ceil(value / (unit * 10)) * (unit * 10);
      } else {
        // 減少時は切り捨て（例：91分 → 90分）
        return Math.floor(value / (unit * 10)) * (unit * 10);
      }
    };
    
    // 現在の値が次の次の区切り値を超えているかチェック
    const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
      const nextBoundary = getNextTimeBoundary(startValue, unit, isIncreasing);
      const nextNextBoundary = isIncreasing 
        ? nextBoundary + unit * 10  // 増加時は上向き
        : nextBoundary - unit * 10; // 減少時は下向き
      return isIncreasing 
        ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
        : value <= nextNextBoundary; // 減少時は値が小さいかチェック
    };
    
    // 現在の単位を取得（時間パウチ用：1分→10分→60分→600分）
    let currentUnit = 1; // 1分
    
    if (isPassedNextNextBoundary(currentValue, 1)) {
      currentUnit = 10; // 10分
      if (isPassedNextNextBoundary(currentValue, 10)) {
        currentUnit = 60; // 60分（1時間）
        if (isPassedNextNextBoundary(currentValue, 60)) {
          currentUnit = 600; // 600分（10時間）
        }
      }
    }
    
    return currentUnit;
  };

  // 現在値に応じた増分値を計算する関数
  const calculateIncrementValue = (currentValue: number, startValue: number, isIncreasing: boolean, isMultiplier: boolean = false, baseUnit: number = 1): number => {
    // Shift+マルチ: 固定刻み配列（長押し連打も baseUnit=10 の間は同じ）
    if (isMultiplier && baseUnit === 10) {
      return shiftMultiplierStepMagnitude(currentValue, isIncreasing);
    }
    // 1ステップ目でない場合は、baseUnitを1に強制
    const actualBaseUnit = isFirstStepRef.current ? baseUnit : 1;
    // 1ステップ目を終了
    isFirstStepRef.current = false;
      // マルチプライヤーの場合の例外処理
      if (isMultiplier) {
        const multiplierSteps =
          pouch.isGroupTotalPouch && !pouch.isTime ? GTP_MULTIPLIER_STEPS : MULTIPLIER_STEPS;
        const allowZeroMultiplierStep = pouch.isGroupTotalPouch && !pouch.isTime;
        // 配列外の場合は表示値ベースで計算
        if (currentValue < -2 || currentValue > 2) {
          // 表示値ベースのマルチ値を取得
          const displayMultiplier = getDisplayMultiplier(currentValue);
          const baseValue = displayMultiplier;

        // 通常の処理（baseUnit = 1）
        // 次の区切り値を計算
        const getNextBoundary = (value: number, unit: number, increasing: boolean): number => {
          if (increasing) {
            // 増加時は切り上げ（例：25241 → 25250）
            return Math.ceil(value / (unit * 10)) * (unit * 10);
          } else {
            // 減少時は切り捨て（例：25241 → 25240）
            return Math.floor(value / (unit * 10)) * (unit * 10);
          }
        };

        // 配列内から配列外に出る場合かどうかを判定
        const isExitingFromArray = (isIncreasing && startValue <= 2 && currentValue > 2) || 
                                   (!isIncreasing && startValue >= -2 && currentValue < -2);
        
        // 配列内から配列外に出る場合は配列境界を基準、それ以外は従来通りstartValueを基準
        const referenceValue = isExitingFromArray ? (isIncreasing ? 2 : -2) : startValue;
        
        // 最初のクリック/タップの場合は1を返す
        if (Math.abs(currentValue - startValue) <= baseUnit) {
          return baseUnit;
        }

        // 現在の単位を取得
        let currentUnit = baseUnit;

        // 現在の値が次の次の区切り値を超えているかチェック（NextBoundaryは表示値ベース）
        const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
          const nextBoundary = getNextBoundary(referenceValue, unit, isIncreasing);
          const nextNextBoundary = isIncreasing 
            ? nextBoundary + unit * 10  // 増加時は上向き
            : nextBoundary - unit * 10; // 減少時は下向き
          return isIncreasing 
            ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
            : value <= nextNextBoundary; // 減少時は値が小さいかチェック
        };

        // 単位を順に確認
        if (isPassedNextNextBoundary(baseValue, 1)) {
          currentUnit = 10;
          if (isPassedNextNextBoundary(baseValue, 10)) {
            currentUnit = 100;
            if (isPassedNextNextBoundary(baseValue, 100)) {
              currentUnit = 1000;
              if (isPassedNextNextBoundary(baseValue, 1000)) {
                currentUnit = 10000;
              }
            }
          }
        }
        

        // 通常パウチのみ *0 をスキップ（GTP は steps に 0 を含む）
        if (!allowZeroMultiplierStep) {
          let nextValue1 = isIncreasing ? currentValue + currentUnit : currentValue - currentUnit;
          if (nextValue1 === 0) {
            if (isIncreasing) {
              const increment = -2 - currentValue;
              return increment;
            } else {
              const increment = currentValue - 2;
              return increment;
            }
          }
          
          if (isIncreasing && currentValue < 0 && currentValue + currentUnit >= 0) {
            const increment = -2 - currentValue;
            return increment;
          } else if (!isIncreasing && currentValue > 0 && currentValue - currentUnit <= 0) {
            const increment = currentValue - 2;
            return increment;
          }
        }

        // 配列外から配列内への遷移時のみ制限（配列の端を飛び越えないように）
        if (isIncreasing && currentValue < 2 && currentValue + currentUnit > 2) {
          // 増加時：配列外から配列内（*2）への遷移
          const increment = 2 - currentValue;
          return increment;
        } else if (!isIncreasing && currentValue > -2 && currentValue - currentUnit < -2) {
          // 減少時：配列外から配列内（/2）への遷移
          const increment = currentValue - (-2);
          return increment;
        }
        

        return currentUnit;
      }
      
      // 配列内（/2〜*2）の場合は配列の刻みに従う
      // 浮動小数点数精度を考慮した配列内チェック
      const roundedCurrentValue = Math.round(currentValue * 100) / 100;
      let isInArray = false;
      let matchedValue = null;
      
      for (const step of multiplierSteps) {
        const roundedStep = Math.round(step * 100) / 100;
        if (Math.abs(roundedCurrentValue - roundedStep) < 0.001) {
          isInArray = true;
          matchedValue = step;
          break;
        }
      }
      
      if (currentValue >= -2 && currentValue <= 2 && isInArray) {
        if (!allowZeroMultiplierStep && currentValue === 0) {
          if (isIncreasing) {
            const increment = -2 - currentValue;
            return increment;
          } else {
            const increment = currentValue - 2;
            return increment;
          }
        }
        
        // 配列内では押し始めからのステップ数に応じて刻みを変える
        // 配列内でのステップ数を計算（配列のインデックスベース）
        const currentIndex = getMultiplierIndex(matchedValue || currentValue, multiplierSteps);
        const startIndex = getMultiplierIndex(startValue, multiplierSteps);
        const stepsFromStart = Math.abs(currentIndex - startIndex);
        
        // 20ステップ付近までは1%刻み、それ以降は10%刻み
        const useTenPercentStep = stepsFromStart >= 20;
        
        // 配列の端に到達した場合は配列外のロジックを使用
        // ただし、配列内から配列外への最初の移動時のみ
        if (isIncreasing && currentValue === 2 && startValue <= 2) {
          // *2から配列外（増加方向）へ（配列内から配列外への最初の移動）
          return 1; // 配列外の通常の刻み
        } else if (!isIncreasing && currentValue === -2 && startValue >= -2) {
          // /2から配列外（減少方向）へ（配列内から配列外への最初の移動）
          return 1; // 配列外の通常の刻み
        }
        
        // ステップ数に応じて刻みを決定
        const stepSize = useTenPercentStep ? 10 : 1;
        
        // 配列の次の値に正確に移動
        if (isIncreasing) {
          const nextIndex = Math.min(currentIndex + stepSize, multiplierSteps.length - 1);
          const targetValue = multiplierSteps[nextIndex];
          const currentExactValue = multiplierSteps[currentIndex];
          const increment = targetValue - currentExactValue;
          // 配列内の場合は正確な次の値を直接返す
          return targetValue - currentValue;
        } else {
          const prevIndex = Math.max(currentIndex - stepSize, 0);
          const targetValue = multiplierSteps[prevIndex];
          const currentExactValue = multiplierSteps[currentIndex];
          const increment = currentExactValue - targetValue;
          // 配列内の場合は正確な次の値を直接返す
          return currentValue - targetValue;
        }
      }
    }

    // 通常の値の場合はbaseUnitパラメータを使用
    const baseValue = currentValue;

    // Shift+クリック/長押しの場合は1桁目の端数をなくした数字に移動
    if (actualBaseUnit === 10) {
      return shiftSnapIncrementMagnitude(currentValue, isIncreasing, {
        clampDecreaseTargetAtZero: true,
      });
    }

    // 通常の処理（baseUnit = 1）
    // 次の区切り値を計算
    const getNextBoundary = (value: number, unit: number, increasing: boolean): number => {
      if (increasing) {
        // 増加時は切り上げ（例：25241 → 25250）
        return Math.ceil(value / (unit * 10)) * (unit * 10);
      } else {
        // 減少時は切り捨て（例：25241 → 25240）
        return Math.floor(value / (unit * 10)) * (unit * 10);
      }
    };

    // 最初のクリック/タップの場合は1を返す
    if (Math.abs(currentValue - startValue) <= actualBaseUnit) {
      return actualBaseUnit;
    }

    // 現在の単位を取得
    let currentUnit = actualBaseUnit;

    // 現在の値が次の次の区切り値を超えているかチェック
    const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
      const nextBoundary = getNextBoundary(startValue, unit, isIncreasing);
      const nextNextBoundary = isIncreasing 
        ? nextBoundary + unit * 10  // 増加時は上向き
        : nextBoundary - unit * 10; // 減少時は下向き
      return isIncreasing 
        ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
        : value <= nextNextBoundary; // 減少時は値が小さいかチェック
    };

    // 単位を順に確認
    if (isPassedNextNextBoundary(baseValue, 0.01)) {
      currentUnit = 0.1;
      if (isPassedNextNextBoundary(baseValue, 0.1)) {
        currentUnit = 1;
        if (isPassedNextNextBoundary(baseValue, 1)) {
          currentUnit = 10;
          if (isPassedNextNextBoundary(baseValue, 10)) {
            currentUnit = 100;
            if (isPassedNextNextBoundary(baseValue, 100)) {
              currentUnit = 1000;
              if (isPassedNextNextBoundary(baseValue, 1000)) {
                currentUnit = 10000;
              }
            }
          }
        }
      }
    }

    return currentUnit;
  };

  // 値の増減用の状態を追加
  const [isValueDecreasePressed, setIsValueDecreasePressed] = useState(false);
  const [isValueIncreasePressed, setIsValueIncreasePressed] = useState(false);

  // レイヤー切替: 旧レイヤーの valueRef・遅延保存を捨て、アクティブレイヤーの表示値に同期
  useEffect(() => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (tempResetTimerRef.current !== null) {
      window.clearTimeout(tempResetTimerRef.current);
      tempResetTimerRef.current = null;
    }
    isButtonOperationRef.current = false;
    originalValueRef.current = null;
    originalMultiplierRef.current = null;
    startValueRef.current = null;
    startMultiplierRef.current = null;
    setTempValue(null);
    setTempMultiplier(null);
    setTempRoundUnit(null);
    originalRoundUnitRef.current = null;
    startRoundUnitRef.current = null;
    isRoundUnitButtonOperationRef.current = false;
    activePressTypeRef.current = null;
    roundUnitRef.current = undefined;
    setIsRoundDecreasePressed(false);
    setIsRoundIncreasePressed(false);
    setIsRoundDecreaseLongPressed(false);
    setIsRoundIncreaseLongPressed(false);
    valueRef.current = pouch.isTime
      ? getTimeMinutesForDisplay()
      : displayValueForLayer();
    multiplierRef.current = layerEffectiveMultiplier;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layerSwitchNonce / resolvedActiveLayerId のみで意図的に実行
  }, [layerSwitchNonce, resolvedActiveLayerId]);

  // pouch 更新時に valueRef を追随（連打中は上書きしない）
  useEffect(() => {
    if (!isButtonOperationRef.current) {
      valueRef.current = pouch.isTime
        ? getTimeMinutesForDisplay()
        : displayValueForLayer();
    }
  }, [pouch.value, pouch.multiplier, pouch.isTime, pouch.layerValues, displayValueForLayer, getTimeMinutesForDisplay, layerEffectiveMultiplier]);

  // 丸め temp: 永続値が追いついたら temp 表示を解除（1.5s 固定タイマーで戻さない）
  useEffect(() => {
    if (tempRoundUnit === null || isRoundUnitButtonOperationRef.current) return;
    const tempNorm = tempRoundUnit > 0 ? tempRoundUnit : undefined;
    const storedNorm =
      displayRoundUnit !== undefined && displayRoundUnit > 0 ? displayRoundUnit : undefined;
    if (tempNorm === storedNorm) {
      cancelTempResetTimer(tempResetTimerRef);
      setTempRoundUnit(null);
      roundUnitRef.current = undefined;
    }
  }, [displayRoundUnit, tempRoundUnit]);

  // タッチイベントが発生したかどうかを追跡
  const isTouchEventRef = useRef(false);
  const lastPressEndTimeRef = useRef(0);
  
  // マウスイベントによるフォーカスかどうかを追跡
  const isMouseFocusRef = useRef(false);
  
  // 1ステップ目だけShift+クリック/長押しの処理を適用するためのフラグ
  const isFirstStepRef = useRef(true);
  
  // ボタンによる増減中かどうかを追跡するフラグ
  const isButtonOperationRef = useRef(false);
  
  // ドラッグ判定前の元の値を保存するためのref
  const originalValueRef = useRef<number | null>(null);
  const originalMultiplierRef = useRef<number | null>(null);
  
  // マウスダウン時の位置を記録するref
  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null);
  
  // 保存処理のデバウンスタイマー
  const saveTimerRef = useRef<number | null>(null);
  // tempリセットタイマー
  const tempResetTimerRef = useRef<number | null>(null);
  
  // ドラッグ判定時にテンポラリ値に戻す関数
  const revertToOriginalValues = useCallback(() => {
    
    if (originalValueRef.current !== null) {
      setTempValue(originalValueRef.current);
      valueRef.current = originalValueRef.current;
    }
    if (originalMultiplierRef.current !== null) {
      setTempMultiplier(originalMultiplierRef.current);
      multiplierRef.current = originalMultiplierRef.current;
    }
  }, [tempValue, tempMultiplier, pouch.value, pouch.multiplier]);

  // マウスダウン/タッチスタート時の処理を修正
  const handlePressStart = (
    type: 'value' | 'multiplier' | 'roundUnit',
    direction: 'increase' | 'decrease',
    isTouchEvent: boolean = false,
    options?: { roundUnitCoarse?: boolean },
  ) => {
    // 前回の操作のタイマーをクリア（重要！）
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    cancelTempResetTimer(tempResetTimerRef);
    clearAllTimers();

    // 最後のPressEndから100ms以内の場合は無視
    if (Date.now() - lastPressEndTimeRef.current < 100) {
      // return;
    }

    // タッチイベントかどうかを記録
    isTouchEventRef.current = isTouchEvent;

    // ドラッグ中は処理をスキップ
    if (pouch._isDragging) return;

    // 帳尻パウチ（BP）: 値・マルチの手動調整は不可（ボタン非表示と同様）
    if (isBalancingPouch) return;

    activePressTypeRef.current = type;

    if (type === 'roundUnit') {
      const increase = direction === 'increase';
      const coarse =
        options?.roundUnitCoarse !== undefined
          ? options.roundUnitCoarse
          : document.documentElement.classList.contains('altcontrol-key');
      isRoundUnitButtonOperationRef.current = true;

      const currentRu = readRoundUnitForOp();
      roundUnitRef.current = currentRu ?? null;
      setTempRoundUnit(currentRu ?? 0);
      if (originalRoundUnitRef.current === null) {
        originalRoundUnitRef.current = displayRoundUnit ?? undefined;
      }
      startRoundUnitRef.current = currentRu ?? 0;

      if (coarse) {
        applyRoundUnitStep(increase, true);
      } else {
        applyRoundUnitStepByDelta(increase, 1);
      }

      armHeldRepeat(
        { longPressTimerRef, repeatTimerRef },
        {
          shouldAbort: () => !!pouch._isDragging,
          onThreshold: () => {
            if (direction === 'decrease') {
              setIsRoundDecreaseLongPressed(true);
            } else {
              setIsRoundIncreaseLongPressed(true);
            }
          },
          onTick: () => {
            const cur = readRoundUnitForOp() ?? 0;
            if (!increase && cur <= 0) {
              return { continue: false, intervalMs: 160 };
            }
            const start = startRoundUnitRef.current ?? 0;
            if (coarse) {
              const ok = applyRoundUnitStep(increase, true);
              return { continue: ok, intervalMs: getRepeatInterval(100) };
            }
            const increment = calculateRoundUnitIncrement(cur, start, increase);
            const ok = applyRoundUnitLongPress(increase);
            return { continue: ok, intervalMs: getRepeatInterval(increment) };
          },
        },
      );
      return;
    }

    // 修飾キーが押されているかチェック
    let isShiftPressed = false;
    
    if (!isTouchEvent) {
      // マウスイベントの場合（現在のイベントから取得）
      // 注意: この時点ではマウスイベントが直接取得できないため、
      // グローバルな修飾キー状態を参照
      isShiftPressed = (window as any).__shiftKeyPressed || false;
    } else {
      // タッチイベントの場合
      isShiftPressed = (window as any).__shiftKeyPressed || false;
    }
    
    // 1ステップ目をリセット
    isFirstStepRef.current = true;
    
    const wasButtonOp = isButtonOperationRef.current;

    // ボタン操作フラグを設定
    isButtonOperationRef.current = true;
    
    // baseUnitを決定（Shift+クリック/長押しの場合は10）
    const baseUnit = isShiftPressed ? 10 : 1;

    // ボタン操作開始時の処理（アニメーション制御は削除）

    // 連続更新の回数をリセット
    repeatCountRef.current = 0;

    // 現在の値を取得（連打時の最新値を優先）
    // refを最優先、次にtemp、最後にpouch
    let currentValue: number;
    if (type === 'value') {
      // valueRef は時間では「分数」。pouch.value は ±60 なので pouch.value との直接比較はしない
      const storedForCompare = pouch.isTime
        ? getTimeMinutesForDisplay(tempMultiplier)
        : displayValueForLayer();
      // 連打中のみ valueRef を優先（レイヤー切替直後の旧レイヤー ref は使わない）
      if (
        wasButtonOp &&
        valueRef.current !== null &&
        valueRef.current !== storedForCompare
      ) {
        currentValue = valueRef.current;
      } else if (tempValue !== null) {
        currentValue = tempValue;
      } else {
        currentValue = storedForCompare;
        valueRef.current = storedForCompare;
      }
    } else {
      // multiplierRef.currentを最優先で参照（連打時の最新値）
      if (multiplierRef.current !== null && Math.abs(multiplierRef.current - layerEffectiveMultiplier) > 0.001) {
        currentValue = multiplierRef.current;
      } else if (tempMultiplier !== null) {
        currentValue = tempMultiplier;
      } else {
        currentValue = layerEffectiveMultiplier;
      }
    }
    
    if (type === 'value') {
      // ドラッグ判定前の元の値をテンポラリ保存（連打時も正しく保存）
      if (originalValueRef.current === null) {
        // 初回のみ保存（連打中は最初の値を保持）
        originalValueRef.current = currentValue;
      }
      // 時間パウチの場合もcurrentValueを使用（tempValueには既に実際の時間（分数）が入っている）
      const baseValue = currentValue;
      startValueRef.current = currentValue;
      // 一時的な表示のみを更新（アニメーション無し）
      // 時間パウチの場合は専用の増分計算を使用
      const increment = pouch.isTime ? calculateTimePouchIncrementValue(baseValue, baseValue, direction === 'increase', baseUnit) : calculateIncrementValue(baseValue, baseValue, direction === 'increase', false, baseUnit);
      const newValue = direction === 'increase' ? baseValue + increment : baseValue - increment;
      
      // 値の範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newValue) > 9999999999) {
        console.warn('値が範囲外です（9999999999を超えています）:', newValue);
        return; // 範囲外の場合は処理を中断
      }
      
      // 時間パウチの場合は分数を時間変換して値60とマルチを調整
      if (pouch.isTime) {
        const totalMinutes = Math.round(Math.abs(newValue));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const sign = newValue < 0 ? -1 : 1;
        
        // 値±60、マルチは分率（非負）。実効は value×multiplier
        const adjustedValue = sign * TIME_POUCH_BALANCING_BASE_VALUE;
        const adjustedMultiplier = (hours * 60 + minutes) / 60;
        
        // 表示用には元の時間（newValue）を使用
        valueRef.current = newValue;
        multiplierRef.current = adjustedMultiplier;
        setTempValue(newValue);
        setTempMultiplier(adjustedMultiplier);
      } else {
        valueRef.current = newValue;
        setTempValue(newValue);
      }
    } else {
      // ドラッグ判定前の元の値をテンポラリ保存（連打時も正しく保存）
      if (originalMultiplierRef.current === null) {
        // 初回のみ保存（連打中は最初の値を保持）
        originalMultiplierRef.current = currentValue;
      }
      startMultiplierRef.current = currentValue;
      // 一時的な表示のみを更新（アニメーション無し）
      // 長押し時間に応じた増分値を計算
      const increment = calculateIncrementValue(currentValue, currentValue, direction === 'increase', true, baseUnit);
      const newMultiplier = direction === 'increase' 
        ? currentValue + increment
        : currentValue - increment;
      
      // マルチプライヤーの範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newMultiplier) > 9999999999) {
        console.warn('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
        return; // 範囲外の場合は処理を中断
      }
      
      multiplierRef.current = newMultiplier;
      setTempMultiplier(newMultiplier);
      
      // 時間モードの場合、valueRef.currentも更新（実際の時間・分数）
      if (pouch.isTime) {
        const actualTimeValue = pouch.value * newMultiplier;
        valueRef.current = actualTimeValue;
        setTempValue(actualTimeValue);
      }
    }

    // 長押しリピート（heldButtonRepeat 共通）
    armHeldRepeat(
      { longPressTimerRef, repeatTimerRef },
      {
        shouldAbort: () => !!pouch._isDragging,
        onThreshold: () => {
          if (type === 'value') {
            if (direction === 'decrease') {
              setIsValueDecreaseLongPressed(true);
            } else {
              setIsValueIncreaseLongPressed(true);
            }
          } else if (direction === 'decrease') {
            setIsDecreaseLongPressed(true);
          } else {
            setIsIncreaseLongPressed(true);
          }
        },
        onTick: () => {
          const getCurrentIncrement = () => {
            if (type === 'value') {
              const currentValue = valueRef.current;
              const startValue = startValueRef.current !== null ? startValueRef.current : currentValue;
              return pouch.isTime
                ? calculateTimePouchIncrementValue(
                    currentValue,
                    startValue,
                    direction === 'increase',
                    baseUnit,
                  )
                : calculateIncrementValue(
                    currentValue,
                    startValue,
                    direction === 'increase',
                    false,
                    baseUnit,
                  );
            }
            const currentMultiplier = multiplierRef.current;
            const startMultiplier =
              startMultiplierRef.current !== null ? startMultiplierRef.current : currentMultiplier;
            return calculateIncrementValue(
              currentMultiplier,
              startMultiplier,
              direction === 'increase',
              true,
              baseUnit,
            );
          };

          const increment = getCurrentIncrement();
          const interval = getRepeatInterval(increment);
          repeatCountRef.current++;

          if (type === 'value') {
            let current =
              valueRef.current !== null
                ? valueRef.current
                : tempValue !== null
                  ? tempValue
                  : displayValueForLayer();

            if (pouch.isTime) {
              const startActualValue =
                startValueRef.current !== null ? startValueRef.current : current;
              const step = calculateTimePouchIncrementValue(
                current,
                startActualValue,
                direction === 'increase',
                baseUnit,
              );
              const newValue = direction === 'increase' ? current + step : current - step;
              if (Math.abs(newValue) > 9999999999) {
                return { continue: false, intervalMs: interval };
              }
              const totalMinutes = Math.round(Math.abs(newValue));
              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;
              const sign = newValue < 0 ? -1 : 1;
              const adjustedMultiplier = (hours * 60 + minutes) / 60;
              valueRef.current = newValue;
              multiplierRef.current = adjustedMultiplier;
              setTempValue(newValue);
              setTempMultiplier(adjustedMultiplier);
            } else {
              const startValue = startValueRef.current !== null ? startValueRef.current : current;
              const step = calculateIncrementValue(
                current,
                startValue,
                direction === 'increase',
                false,
                baseUnit,
              );
              const newValue = direction === 'increase' ? current + step : current - step;
              if (Math.abs(newValue) > 9999999999) {
                return { continue: false, intervalMs: interval };
              }
              valueRef.current = newValue;
              setTempValue(newValue);
            }
          } else {
            let current =
              multiplierRef.current !== null
                ? multiplierRef.current
                : tempMultiplier !== null
                  ? tempMultiplier
                  : pouch.multiplier;
            const startValue = startMultiplierRef.current !== null ? startMultiplierRef.current : current;
            const step = calculateIncrementValue(
              current,
              startValue,
              direction === 'increase',
              true,
              baseUnit,
            );
            const newMultiplier = direction === 'increase' ? current + step : current - step;
            if (Math.abs(newMultiplier) > 9999999999) {
              return { continue: false, intervalMs: interval };
            }
            multiplierRef.current = newMultiplier;
            setTempMultiplier(newMultiplier);
            if (pouch.isTime) {
              const actualTimeValue = pouch.value * newMultiplier;
              valueRef.current = actualTimeValue;
              setTempValue(actualTimeValue);
            }
          }

          return { continue: true, intervalMs: interval };
        },
      },
    );
  };

  // マウスアップ/タッチエンド時の処理を修正
  const handlePressEnd = (currentMousePosition?: { x: number; y: number }) => {
    const isRoundPress = isRoundUnitButtonOperationRef.current;
    const isValueMultPress = isButtonOperationRef.current;

    if (
      (interaction?.type === 'DRAG' || interaction?.type === 'LONG_PRESS_DRAG') &&
      !isValueMultPress &&
      !isRoundPress
    ) {
      return;
    }

    if (!isValueMultPress && !isRoundPress) {
      return;
    }

    if (isRoundPress) {
      if (isDragBeyondThreshold(mouseDownPositionRef.current, currentMousePosition)) {
        mouseDownPositionRef.current = null;
        clearAllTimers();
        setIsRoundDecreasePressed(false);
        setIsRoundIncreasePressed(false);
        setIsRoundDecreaseLongPressed(false);
        setIsRoundIncreaseLongPressed(false);
        resetRoundUnitButtonOp(true);
        activePressTypeRef.current = null;
        return;
      }

      mouseDownPositionRef.current = null;
      lastPressEndTimeRef.current = Date.now();
      clearAllTimers();
      setIsRoundDecreasePressed(false);
      setIsRoundIncreasePressed(false);
      setIsRoundDecreaseLongPressed(false);
      setIsRoundIncreaseLongPressed(false);

      flushRoundUnitFromButtonOp();
      resetRoundUnitButtonOp(false);
      activePressTypeRef.current = null;
      return;
    }
    
    // マウスが移動している場合はドラッグと判定してスキップ
    if (isDragBeyondThreshold(mouseDownPositionRef.current, currentMousePosition)) {
        // ドラッグなので元の値に戻す
        if (originalValueRef.current !== null) {
          setTempValue(originalValueRef.current);
          valueRef.current = originalValueRef.current;
        }
        if (originalMultiplierRef.current !== null) {
          setTempMultiplier(originalMultiplierRef.current);
          multiplierRef.current = originalMultiplierRef.current;
        }
        
        // 長押しタイマーと保存タイマーをクリア
        if (longPressTimerRef.current) {
          window.clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        if (repeatTimerRef.current) {
          window.clearTimeout(repeatTimerRef.current);
          repeatTimerRef.current = null;
        }
        if (saveTimerRef.current) {
          window.clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        if (tempResetTimerRef.current) {
          window.clearTimeout(tempResetTimerRef.current);
          tempResetTimerRef.current = null;
        }
        
        // 全ての長押しフラグをリセット
        setIsValueDecreaseLongPressed(false);
        setIsValueIncreaseLongPressed(false);
        setIsDecreaseLongPressed(false);
        setIsIncreaseLongPressed(false);
        
        // ボタン操作フラグをリセット
        isButtonOperationRef.current = false;
        repeatCountRef.current = 0;
        
        // 一時的な値をリセット
        setTimeout(() => {
          setTempValue(null);
          setTempMultiplier(null);
          const p = pouchRef.current;
          valueRef.current = p.isTime
            ? getTimePouchEffectiveMinutes(p, getEffectiveMultiplierForPouch(p))
            : p.value;
          multiplierRef.current = getEffectiveMultiplierForPouch(p);
          originalValueRef.current = null;
          originalMultiplierRef.current = null;
        }, 10);
        
        mouseDownPositionRef.current = null;
        activePressTypeRef.current = null;
        return;
    }
    
    // 最後のPressEndから100ms以内の場合は無視
    const now = Date.now();
    if (now - lastPressEndTimeRef.current < 100) {
      // return;
    }
    lastPressEndTimeRef.current = now;
    
    // マウスダウン位置をリセット
    mouseDownPositionRef.current = null;
    
    // originalValueRef/originalMultiplierRefをリセット（次回クリック時に新しい値を保存できるように）
    originalValueRef.current = null;
    originalMultiplierRef.current = null;

    // 長押しタイマーをクリア
    clearAllTimers();

    // 全ての長押しフラグをリセット
    setIsValueDecreaseLongPressed(false);
    setIsValueIncreaseLongPressed(false);
    setIsDecreaseLongPressed(false);
    setIsIncreaseLongPressed(false);

    // 連続更新の回数をリセット
    repeatCountRef.current = 0;

    // 既存の保存タイマーをクリア（連打時は延期される）
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    // 既存のtempリセットタイマーをクリア（連打時は延期される）
    cancelTempResetTimer(tempResetTimerRef);

    // 非時間パウチのマルチは遅延経路のみだと、タイマー発火時の閉包・比較ズレで onUpdate がスキップされ、
    // temp 解除（1.5s後）後に見た目だけ古いマルチへ戻ることがあるため、離したタイミングで即時確定する
    const pImmediate = pouchRef.current;
    if (!pImmediate.isTime && onUpdateMultiplier) {
      const mNow = multiplierRef.current;
      const storedMult = getEffectiveMultiplierForPouch(pImmediate);
      if (mNow !== null && Math.abs(mNow - storedMult) > 0.001) {
        onUpdateMultiplier(pImmediate.id, normalizeMultiplier(mNow));
      }
    }

    // 値を更新（デバウンス処理：連打が止まってから50ms後に保存。マルチは上で即時のため時間外のみここ）
    saveTimerRef.current = window.setTimeout(() => {
      const finalTempValue = valueRef.current;
      const finalTempMultiplier = multiplierRef.current;
      const p = pouchRef.current;

      // 時間パウチの場合は値とマルチプライヤーを同時に保存（値±60・マルチは分率）
      if (p.isTime) {
        if (finalTempMultiplier !== null && finalTempValue !== null) {
          const absMinutes = Math.round(Math.abs(finalTempValue));
          const hours = Math.floor(absMinutes / 60);
          const minutes = absMinutes % 60;
          const saveVal = finalTempValue < 0 ? -TIME_POUCH_BALANCING_BASE_VALUE : TIME_POUCH_BALANCING_BASE_VALUE;
          const saveMult = (hours * 60 + minutes) / 60;
          if (Math.abs(saveMult - getEffectiveMultiplierForPouch(p)) > 0.001 || p.value !== saveVal) {
            onUpdateValueAndMultiplier?.(p.id, saveVal, saveMult, HistoryActionType.EDIT_POUCH_VALUE);
          }
        }
      } else {
        // GTP の value は syncMirroredMulGtpValues で更新される。isButtonOperationRef=true 中は
        // valueRef が古いままのため、ボタン離しタイミングで stale 値を書き戻すと EDIT_POUCH_VALUE が
        // oppositeToTotal を誤ってトグルする。GTP は value 保存をスキップして multiplier のみ保存する。
        if (
          !p.isGroupTotalPouch &&
          finalTempValue !== null &&
          finalTempValue !== getPouchDisplayValueForEdit(p, currentSheet)
        ) {
          onUpdateValue?.(p.id, finalTempValue, HistoryActionType.EDIT_POUCH_VALUE);
        }
        // マルチは handlePressEnd 直後に即時保存済み
      }

      saveTimerRef.current = null;
    }, 50);

    // 一時的な値をリセット（保存+状態更新完了後に十分な時間を待つ）
    scheduleTempReset(tempResetTimerRef, () => {
      if (interaction?.type !== 'DRAG' && interaction?.type !== 'LONG_PRESS_DRAG') {
        setTempValue(null);
        setTempMultiplier(null);
        setTempRoundUnit(null);
        roundUnitRef.current = undefined;
      }
      isTouchEventRef.current = false;
      isButtonOperationRef.current = false;
      activePressTypeRef.current = null;
    });
  };

  // マウスアップ・タッチエンド時の再補正処理
  const handleMouseUp = useCallback(() => {
    
    
    // ドラッグ判定があった場合の再補正処理
    if (interaction?.type === 'DRAG' || interaction?.type === 'LONG_PRESS_DRAG') {
      
      // テンポラリ値と異なっている場合は再度テンポラリ値に戻す
      if (originalValueRef.current !== null && tempValue !== originalValueRef.current) {
        setTempValue(originalValueRef.current);
        valueRef.current = originalValueRef.current;
      }
      if (originalMultiplierRef.current !== null && tempMultiplier !== originalMultiplierRef.current) {
        setTempMultiplier(originalMultiplierRef.current);
        multiplierRef.current = originalMultiplierRef.current;
      }
      
      // テンポラリ値をリセット
      setTimeout(() => {
        // 一時的な値をリセット
        setTempValue(null);
        setTempMultiplier(null);
        valueRef.current = pouch.isTime
          ? getTimeMinutesForDisplay()
          : displayValueForLayer();
        multiplierRef.current = layerEffectiveMultiplier;
        // テンポラリ保存した値もリセット
        originalValueRef.current = null;
        originalMultiplierRef.current = null;
      }, 10);
    } else {
      // ドラッグ判定がなかった場合の処理
      setTimeout(() => {
        // 一時的な値をリセット
        setTempValue(null);
        setTempMultiplier(null);
        valueRef.current = pouch.isTime
          ? getTimeMinutesForDisplay()
          : displayValueForLayer();
        multiplierRef.current = layerEffectiveMultiplier;
        // テンポラリ保存した値もリセット
        originalValueRef.current = null;
        originalMultiplierRef.current = null;
      }, 10);
    }
  }, [
    interaction,
    pouch.id,
    originalValueRef,
    originalMultiplierRef,
    tempValue,
    tempMultiplier,
    pouch.value,
    layerEffectiveMultiplier,
    pouch.isTime,
    displayValueForLayer,
  ]);

  // 外部からrevertToOriginalValuesを呼び出せるようにする
  React.useImperativeHandle(
    ref,
    () => ({
      revertToOriginalValues,
      handleMouseUp,
      keyboardSimulatePressStart: (
        type: 'value' | 'multiplier' | 'roundUnit',
        direction: 'increase' | 'decrease',
        shiftKey: boolean,
      ) => {
        if (pouch._isDragging) return false;
        if (isBalancingPouch) return false;
        if (type === 'roundUnit') {
          handlePressStart(type, direction, false, { roundUnitCoarse: shiftKey });
        } else {
          (window as any).__shiftKeyPressed = shiftKey;
          handlePressStart(type, direction, false);
        }
        return true;
      },
      keyboardSimulatePressEnd: () => {
        handlePressEnd();
      },
    }),
    [revertToOriginalValues, handleMouseUp, handlePressStart, handlePressEnd, pouch._isDragging, isBalancingPouch]
  );

  // コンポーネントのアンマウント時にタイマーとフラグをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
      isTouchEventRef.current = false;
    };
  }, []);

  // バッチ処理用のref
  const batchUpdateTimeoutRef = useRef<number | null>(null);
  const pendingValueUpdateRef = useRef<number | null>(null);
  const pendingMultiplierUpdateRef = useRef<number | null>(null);
  const isBatchUpdatingRef = useRef(false);

  // 連続更新の回数を追跡
  const repeatCountRef = useRef(0);

  // パウチコンテナ用のツールチップを生成
  const getPouchTooltipTitle = () => {
    let shouldShowTimeConversion = pouchCountsAsTimeForSheetDisplay(
      pouch,
      allPouches,
      allGroups,
      currentSheet?.balancingPouchId,
      currentSheet,
    );
    
    // マルチが1で時刻変換もない場合、×1の数値詳細を表示
    // (ボタンのツールチップで常に数値詳細を表示するため)
    
    // GTP はメイン表示と同じく groupTotalValue 基準で評価し、BP 時の stale pouch.value と乖離させない。
    const persistedDisplayBaseValue = pouch.isGroupTotalPouch
      ? groupTotalValue * (pouch.oppositeToTotal ? -1 : 1)
      : isBalancingPouch && !shouldShowMainTimeDisplay
        ? getPouchDisplayValueForEdit(pouch, currentSheet)
        : pouch.value;
    // 時間パウチの左辺は「ベース値(通常は±60)」を固定表示する。
    // 連打中 tempValue(実効分) をここに使うと「89 × ...」のように見えてしまうため除外する。
    const currentValue = pouch.isTime
      ? persistedDisplayBaseValue
      : (tempValue !== null ? tempValue : persistedDisplayBaseValue);
    const currentMultiplier = tempMultiplier !== null ? tempMultiplier : layerEffectiveMultiplier;
    
    // 実際の計算値（~ 丸め込み。時間は temp が実効分数）
    const scaledBeforeRound = pouch.isTime
      ? (tempValue !== null ? tempValue : getTimeMinutesForDisplay(currentMultiplier))
      : getPouchScaledValue({ ...pouch, value: currentValue, multiplier: currentMultiplier });
    const rawActualValue = roundScaledValue(
      scaledBeforeRound,
      effectiveDisplayRoundUnit ?? pouch.roundUnit,
    );
    
    // 浮動小数点数の誤差を修正（小数点以下10桁で丸め、整数近傍は整数へ吸着）
    const roundedActualValue = Math.round(rawActualValue * 10000000000) / 10000000000;
    // GTP はグループ小計由来で加算誤差が大きくなりうるため吸着を緩める（Group SUBTOTAL ツールチップと整合）
    const intSnapEps = pouch.isGroupTotalPouch ? 1e-4 : 1e-6;
    const actualValue =
      Math.abs(roundedActualValue - Math.round(roundedActualValue)) <= intSnapEps
        ? Math.round(roundedActualValue)
        : roundedActualValue;
    
    // ツールチップ数値は NumberCounter/合計と同じ 8桁(+必要時 ...) に統一
    const formatResultWithPattern = (value: number): string => {
      if (Math.abs(value - Math.round(value)) < 1e-10) {
        const integerValue = Math.round(value);
        return `${integerValue < 0 ? '-' : ''}${Math.abs(integerValue).toLocaleString()}`;
      }

      const roundedValue = Math.round(value * 100000000) / 100000000;
      const roundedValue9 = Math.round(value * 1000000000) / 1000000000;
      const hasMoreDigits = Math.abs(roundedValue9 - roundedValue) > 0;
      const integerPart = Math.floor(Math.abs(roundedValue));
      const decimalPart = Math.abs(roundedValue) - integerPart;
      const formattedInteger = integerPart.toLocaleString();
      let resultDisplay;
      if (decimalPart > 0) {
        const decimalString = decimalPart.toFixed(8).replace(/0+$/, '');
        resultDisplay = `${roundedValue < 0 ? '-' : ''}${formattedInteger}.${decimalString.substring(2)}`;
      } else {
        resultDisplay = `${roundedValue < 0 ? '-' : ''}${formattedInteger}`;
      }
      if (hasMoreDigits) {
        resultDisplay += '...';
      }
      return resultDisplay;
    };
    
    const resultDisplay = formatResultWithPattern(actualValue);
    
    // マルチの表示形式も含める（GTPの場合は内部値を使用）
    let tooltipMultiplierDisplay;
    if (pouch.isGroupTotalPouch) {
      // GTPの場合は内部値のマルチプライヤーを直接使用（表示値変換なし）
      const absMultiplier = Math.abs(currentMultiplier);
      const isGtp = true;
      
      // 絶対値が1以上、または整数の場合は通常表示
      if (absMultiplier >= 1 || absMultiplier === Math.floor(absMultiplier)) {
        tooltipMultiplierDisplay = {
          value: absMultiplier,
          symbol: isGtp ? '*' : '×',
          isDivision: currentMultiplier < 0,
          showSymbolAfter: false
        };
      } else {
        // 1未満の場合は%表示
        tooltipMultiplierDisplay = {
          value: absMultiplier * 100,
          symbol: '%',
          isDivision: currentMultiplier < 0,
          showSymbolAfter: true
        };
      }
    } else {
      // 通常のパウチの場合は表示値を使用
      tooltipMultiplierDisplay = multiplierDisplay;
    }
    
    const multiplierText = tooltipMultiplierDisplay.isDivision ? '÷' : '×';
    const multiplierValue = tooltipMultiplierDisplay.value;
    const multiplierSymbol = tooltipMultiplierDisplay.showSymbolAfter ? tooltipMultiplierDisplay.symbol : '';
    
    // NumberCounterと同じ小数表示ロジックを適用
    const formatValueLikeNumberCounter = (value: number): string => {
      // 8桁で丸めて9桁目以降をチェック
      const roundedValue8 = Math.round(value * 100000000) / 100000000;
      const roundedValue9 = Math.round(value * 1000000000) / 1000000000;
      const hasMoreDigits = Math.abs(roundedValue9 - roundedValue8) > 0;
      
      // 整数部分と小数部分を分離
      const integerPart = Math.floor(Math.abs(roundedValue8));
      const decimalPart = Math.abs(roundedValue8) - integerPart;
      
      // 整数部分に3桁カンマ区切りを適用
      const formattedInteger = integerPart.toLocaleString();
      
      // 小数部分がある場合は結合
      let result = '';
      if (decimalPart > 0) {
        // 小数部分を8桁まで表示（末尾の0は除去）
        const decimalString = decimalPart.toFixed(8).replace(/0+$/, '');
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}.${decimalString.substring(2)}`;
      } else {
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}`;
      }
      
      // 9桁目以降がある場合は"..."を追加
      if (hasMoreDigits) {
        result += '...';
      }
      
      return result;
    };
    
    // 時間変換の追加
    let timeConversion = '';
    if (shouldShowTimeConversion) {
      // actualValueを分として扱い、時:分形式に変換（四捨五入は時間変換でのみ適用）
      const totalMinutes = Math.round(Math.abs(actualValue));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      timeConversion = ` (${hours}:${minutes.toString().padStart(2, '0')})`;
    }
    
    // lastExpressionが有効な場合は2行目に追加（GTPでは無効）
    let expressionLine = '';
    if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
      expressionLine = `\n[${pouch.lastExpression}]`;
    }

    let roundUnitSegment = '';
    if (
      !pouch.isGroupTotalPouch &&
      effectiveDisplayRoundUnit !== undefined &&
      effectiveDisplayRoundUnit > 0
    ) {
      roundUnitSegment = ` ~ ${formatValueLikeNumberCounter(effectiveDisplayRoundUnit)}`;
    }
    
    return `${formatValueLikeNumberCounter(currentValue)} ${multiplierText} ${formatValueLikeNumberCounter(multiplierValue)}${multiplierSymbol}${roundUnitSegment} = ${resultDisplay}${timeConversion}${expressionLine}`;
  };

  const pouchNumericTooltipHtml = useMemo(() => {
    const visible = getPouchTooltipTitle();
    const hasLayers = (currentSheet?.layers?.length ?? 0) > 0;
    const layerLines = hasLayers
      ? buildPouchLayerTooltipLines(currentSheet!, pouch, allPouches, allGroups)
      : [visible];
    const forceEquationTooltip = shouldForcePouchEquationTooltip(pouch);
    let html = resolveLayerNumericTooltipHtml(currentSheet, visible, layerLines, {
      forceEquationTooltip,
    });
    if (html && hasLayers && !pouch.isGroupTotalPouch && !isExpressionOutdated()) {
      const lastExpr =
        currentSheet && pouchUsesLayerValues(currentSheet, pouch)
          ? getPouchLayerExpressionFields(pouch, resolvedActiveLayerId).lastExpression?.trim()
          : pouch.lastExpression?.trim();
      if (lastExpr) {
        html = appendLineToPouchNumericTooltipBoldBlock(html, `[${lastExpr}]`);
      }
    }
    return html;
  }, [
    currentSheet,
    pouch,
    allPouches,
    allGroups,
    tempValue,
    tempMultiplier,
    tempRoundUnit,
    groupTotalValue,
    multiplierDisplay,
    pouch.lastExpression,
    pouch.isTime,
    pouch.roundUnit,
    displayRoundUnit,
    effectiveDisplayRoundUnit,
    pouch.isGroupTotalPouch,
    pouch.oppositeToTotal,
    isExpressionOutdated,
    resolvedActiveLayerId,
  ]);

  /** ドラッグ・複製・分割（修飾キー付きは getModifierKeys 由来の表記） */
  const pouchGuideMovementHtml = useMemo(() => {
    const keys = getModifierKeys();
    const lines: string[] = [];

    lines.push(`Drag: ${tt.dragMovePouch}`);

    if (isBalancingPouch) {
      lines.push(`Long Press Drag: ${tt.longPressDragClonePouch}`);
      lines.push(`${keys.ctrlOption}+Drag: ${tt.clonePouchDrag}`);
    } else {
      const epv = pouch.multiplier > 0 ? pouch.multiplier : 1 / Math.abs(pouch.multiplier);
      const isX1 = epv === 1;
      const origResult = pouch.multiplier >= 0
        ? pouch.value * pouch.multiplier
        : pouch.value / Math.abs(pouch.multiplier);
      const canTear = (origResult - pouch.value) !== 0;
      const canSplit = canHalfSplitPouchByMultiplier(pouch, currentSheet);
      const canValueOps = Math.abs(pouch.value) >= 2;

      let isProgressive = false;
      if (isX1) {
        isProgressive = canValueOps;
      } else {
        isProgressive = canTear || canSplit;
      }

      lines.push(`Long Press Drag: ${isProgressive ? tt.longPressDragClonePouch : tt.clonePouchDrag}`);
      lines.push(`${keys.ctrlOption}+Drag: ${tt.clonePouchDrag}`);

      if (isX1) {
        if (canValueOps) {
          lines.push(`${keys.shift}+Drag: Tear Pouch`);
          lines.push(`${keys.shift}+${keys.ctrlOption}+Drag: Split Pouch`);
        }
      } else {
        if (canTear) {
          lines.push(`${keys.shift}+Drag: Tear Pouch`);
        }
        if (canSplit) {
          lines.push(`${keys.shift}+${keys.ctrlOption}+Drag: Split Pouch`);
        }
      }
    }

    if (!isGuideEnabled) return '';
    return lines.map(l => formatTooltip(l)).join('<br/>');
  }, [pouch.value, pouch.multiplier, isBalancingPouch, isGuideEnabled]);

  const pouchGuideColorExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    const keys = getModifierKeys();
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const colorCircle = (idx: number) => {
      const color = POUCH_COLORS[idx];
      return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    };
    const nextCircle = colorCircle(nextIndex);
    const prevCircle = colorCircle(prevIndex);
    const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    const rotateNext = formatKeyDescLine('C', changePouchColor(nextCircle, locale));
    const rotatePrev = formatKeysLeftWithDesc(
      `${formatTooltip(keys.shift)}${formatTooltip('C')}`,
      changePouchColor(prevCircle, locale),
    );
    return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
  }, [pouch.color, isGuideEnabled, isTouchDevice, locale]);

  /** パウチ内パーツ用: 2 秒後（ドラッグ → Edit → 色 → Delete） */
  const pouchInnerLongHoverExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    return [
      formatTooltip(`Drag: ${tt.dragMovePouch}`),
      formatPouchGuideArrowValueMultiplierPcHtml(),
      formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined),
      pouchGuideColorExtrasHtml,
      formatPouchGuideDeletePcExtrasHtml(),
    ].join('<br/>');
  }, [isGuideEnabled, isTouchDevice, pouchGuideColorExtrasHtml, isBalancingPouch]);

  /** 編集中ツールチップ用: 色ホットキー行のみ */
  const pouchEditModeColorExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    const keys = getModifierKeys();
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const colorCircle = (idx: number) => {
      const color = POUCH_COLORS[idx];
      return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    };
    const nextCircle = colorCircle(nextIndex);
    const prevCircle = colorCircle(prevIndex);
    const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    const rotateNext = formatKeyDescLine('C', changePouchColor(nextCircle, locale));
    const rotatePrev = formatKeysLeftWithDesc(
      `${formatTooltip(keys.shift)}${formatTooltip('C')}`,
      changePouchColor(prevCircle, locale),
    );
    return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
  }, [pouch.color, isGuideEnabled, isTouchDevice, locale]);

  /** ×/÷ 表示かつ数量≠1: 数式 [**] or [/][/] で値へ反映 */
  const reflectQtyToValueGuideHtml = useMemo(() => {
    if (!isGuideEnabled || isBalancingPouch || pouch.isGroupTotalPouch || pouch.isTime) {
      return '';
    }
    if (absMultiplier === 1) {
      return '';
    }
    return formatReflectQtyToValueFormulaGuideHtml(isDivisionMode);
  }, [
    isGuideEnabled,
    isBalancingPouch,
    pouch.isGroupTotalPouch,
    pouch.isTime,
    absMultiplier,
    isDivisionMode,
  ]);

  const multiplierValueTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    const boldDetail = pouchNumericTooltipHtml ?? '';
    const guide = formatTooltip(`Click: ${tt.resetQty}\nShift+Click: ${tt.resetQtyAndRounding}`);
    let core = [boldDetail, guide, reflectQtyToValueGuideHtml].filter(Boolean).join('<br/>');
    if (pouchGuideHoverTier >= 2 && pouchInnerLongHoverExtrasHtml) {
      core = core ? `${core}<br/>${pouchInnerLongHoverExtrasHtml}` : pouchInnerLongHoverExtrasHtml;
    }
    return core || '';
  }, [
    isEditing,
    isTitleEditing,
    pouchNumericTooltipHtml,
    tt.resetQty,
    tt.resetQtyAndRounding,
    reflectQtyToValueGuideHtml,
    pouchGuideHoverTier,
    pouchInnerLongHoverExtrasHtml,
  ]);

  /** 値／タイトル編集中のフローティングガイド */
  const pouchEditModeTooltipHtml = useMemo(() => {
    if (!isEditing && !isTitleEditing) return '';
    if (!isGuideEnabled) return '';
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const enterHint = formatKeyDescLine('Enter', esc(isEditing ? commitValue(locale) : tt.commitTitle));
    const escHint =
      isEditing && keyboardNewPouchFirstEditUi
        ? formatKeyDescLine('Esc', esc(tt.deletePouchNotSaved))
        : formatKeyDescLine('Esc', esc(isTitleEditing ? tt.discard : tt.cancel));
    const valueToTitleHint =
      isEditing && !isTouchDevice
        ? formatKeyDescLine('Space', esc(tt.editPouchTitle))
        : '';
    const tier1 = [enterHint, escHint, valueToTitleHint].filter(Boolean).join('<br/>');
    if (editGuideTier < 2) return tier1;
    if (isTouchDevice) return tier1;
    const tier2Extras = [isEditing ? reflectQtyToValueGuideHtml : '', pouchEditModeColorExtrasHtml]
      .filter(Boolean)
      .join('<br/>');
    if (tier2Extras) {
      return `${tier1}<br/>${tier2Extras}`;
    }
    return tier1;
  }, [
    isEditing,
    isTitleEditing,
    isGuideEnabled,
    editGuideTier,
    isTouchDevice,
    pouchEditModeColorExtrasHtml,
    keyboardNewPouchFirstEditUi,
    reflectQtyToValueGuideHtml,
    locale,
  ]);

  /** タイトル欄ホバー: 初期は Click のみ、2 秒後は内側パーツと同様の追記 */
  const pouchTitleTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    const phase1 = formatTooltip(`Click: ${tt.editPouchTitle}`);
    if (pouchGuideHoverTier < 2 || !isGuideEnabled) return phase1;
    if (pouchInnerLongHoverExtrasHtml) {
      return `${phase1}<br/>${pouchInnerLongHoverExtrasHtml}`;
    }
    return phase1;
  }, [isEditing, isTitleEditing, pouchGuideHoverTier, pouchInnerLongHoverExtrasHtml, isGuideEnabled]);

  const getButtonTooltipHtml = (guideText: string) => {
    if (isEditing || isTitleEditing) return '';
    const boldDetail = pouchNumericTooltipHtml ?? '';
    const guide = formatTooltip(guideText);
    let core = guide ? (boldDetail ? `${boldDetail}<br/>${guide}` : guide) : boldDetail;
    if (pouchGuideHoverTier >= 2 && pouchInnerLongHoverExtrasHtml) {
      core = core ? `${core}<br/>${pouchInnerLongHoverExtrasHtml}` : pouchInnerLongHoverExtrasHtml;
    }
    return core || '';
  };

  /** 分解ボタン */
  const splitButtonTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing || isBalancingPouch || pouch.isGroupTotalPouch) return '';
    const guideText = isSplitDisabled
      ? tt.splitPouch
      : `Click: ${tt.splitPouch}`;
    return getButtonTooltipHtml(guideText);
  }, [
    isEditing,
    isTitleEditing,
    isBalancingPouch,
    pouch.isGroupTotalPouch,
    isSplitDisabled,
    tt.splitPouch,
    pouchGuideHoverTier,
    pouchInnerLongHoverExtrasHtml,
    pouchNumericTooltipHtml,
  ]);

  /** 正負反転ボタン: Click と Esc / Shift+Esc の表記を揃え、tier2 で表示 */
  const getToggleSignButtonTooltipHtml = () => {
    if (isEditing || isTitleEditing) return '';
    const boldDetail = pouchNumericTooltipHtml ?? '';
    const clickLine = formatTooltip(`Click: ${tt.toggleSign}`);
    const keys = getModifierKeys();
    let keyboardExtras = '';
    if (isGuideEnabled && pouchGuideHoverTier >= 2 && !isTouchDevice) {
      if (isBalancingPouch) {
        keyboardExtras = `<br/>${formatKeyDescLine('Esc', tt.disableBalancing)}`;
      } else if (pouch.isGroupTotalPouch) {
        keyboardExtras = `<br/>${formatKeyDescLine('Esc', tt.toggleSign)}`;
      } else {
        keyboardExtras = `<br/>${formatKeyDescLine('Esc', tt.toggleSign)}<br/>${formatKeysLeftWithDesc(
          `${formatTooltip(keys.shift)}${formatTooltip('Esc')}`,
          tt.balancingSameAsBpButton,
        )}`;
      }
    }
    const guide = `${clickLine}${keyboardExtras}`;
    let core = guide ? (boldDetail ? `${boldDetail}<br/>${guide}` : guide) : boldDetail;
    if (pouchGuideHoverTier >= 2 && pouchInnerLongHoverExtrasHtml) {
      core = core ? `${core}<br/>${pouchInnerLongHoverExtrasHtml}` : pouchInnerLongHoverExtrasHtml;
    }
    return core || '';
  };

  const balancingPouchButtonTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing || pouch.isGroupTotalPouch) return '';
    const targetTotal = (currentSheet?.balancingTargetTotal ?? currentTotal).toLocaleString(undefined, { maximumFractionDigits: 10 });
    const currentTotalStr = currentTotal.toLocaleString(undefined, { maximumFractionDigits: 10 });
    const guideText = isBalancingPouch
      ? `Click: ${tt.disableBalancing}`
      : currentSheet?.balancingPouchId
        ? `Click: ${switchBalancingToTarget(targetTotal, locale)}`
        : `Click: ${enableBalancingToTotal(currentTotalStr, locale)}`;
    const base = getButtonTooltipHtml(guideText);
    if (!base || pouchGuideHoverTier < 2 || isTouchDevice || !isGuideEnabled) return base;
    const keys = getModifierKeys();
    const extra = isBalancingPouch
      ? `<br/>${formatKeyDescLine('Esc', tt.disableBalancing)}`
      : `<br/>${formatKeysLeftWithDesc(
          `${formatTooltip(keys.shift)}${formatTooltip('Esc')}`,
          tt.balancingSameAsThisButton,
        )}`;
    return `${base}<br/>${extra}`;
  }, [
    isEditing,
    isTitleEditing,
    pouch.isGroupTotalPouch,
    pouch,
    isBalancingPouch,
    currentSheet?.balancingPouchId,
    currentSheet?.balancingTargetTotal,
    currentTotal,
    pouchGuideHoverTier,
    isTouchDevice,
    pouchInnerLongHoverExtrasHtml,
    isGuideEnabled,
  ]);

  const colorButtonTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    if (!isGuideEnabled) return '';
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const nextCircle = `<span style="color:${POUCH_COLORS[nextIndex].value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    const prevCircle = `<span style="color:${POUCH_COLORS[prevIndex].value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    const keys = getModifierKeys();
    let html = [
      formatKeyDescLine('Click', changePouchColor(nextCircle, locale)),
      formatKeysLeftWithDesc(
        `${formatTooltip(keys.shift)}${formatTooltip('Click')}`,
        changePouchColor(prevCircle, locale),
      ),
    ]
      .filter(Boolean)
      .join('<br/>');
    if (pouchGuideHoverTier >= 2 && !isTouchDevice) {
      html += `<br/>${formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined)}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
    }
    return html;
  }, [isEditing, isTitleEditing, pouch.color, pouchGuideHoverTier, isTouchDevice, isBalancingPouch, isGuideEnabled]);

  // パウチ内ツールチップ: パウチ本体の少し上に統一表示
  const handlePouchTooltipOver = useCallback(
    (e: React.MouseEvent) => {
      const el = (e.target as HTMLElement).closest('[data-pouch-tooltip-html]');
      if (!(el instanceof HTMLElement)) {
        return;
      }
      if (!editModeActiveRef.current && pouchTooltipHoverAnchorRef.current !== el) {
        pouchTooltipHoverAnchorRef.current = el;
        setPouchGuideHoverTier(1);
        if (pouchGuideHoverTimerRef.current !== null) {
          window.clearTimeout(pouchGuideHoverTimerRef.current);
          pouchGuideHoverTimerRef.current = null;
        }
        if (isGuideEnabled) {
          pouchGuideHoverTimerRef.current = window.setTimeout(() => {
            setPouchGuideHoverTier(2);
            pouchGuideHoverTimerRef.current = null;
          }, GUIDE_HOVER_DELAY_MS);
        }
      }
      pouchTooltipTargetRef.current = el;
      setPouchTooltipContent(el.getAttribute('data-pouch-tooltip-html'));
    },
    [GUIDE_HOVER_DELAY_MS, isGuideEnabled],
  );

  const mainTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    const moveOnly = formatTooltip(`Drag: ${tt.dragMovePouch}`);
    const shortMain = () => {
      const boldDetail = pouchNumericTooltipHtml ?? '';
      if (boldDetail) {
        return moveOnly ? `${boldDetail}<br/>${moveOnly}` : boldDetail;
      }
      return moveOnly;
    };
    if (pouchGuideHoverTier < 2 || !isGuideEnabled) {
      return shortMain();
    }
    const boldPrefix = pouchNumericTooltipHtml ? `${pouchNumericTooltipHtml}<br/>` : '';
    if (isTouchDevice) {
      return `${boldPrefix}${pouchGuideMovementHtml}`;
    }
    const keys = getModifierKeys();
    const escToggleMain = isBalancingPouch
      ? `<br/>${formatKeyDescLine('Esc', tt.disableBalancing)}`
      : pouch.isGroupTotalPouch
        ? `<br/>${formatKeyDescLine('Esc', tt.toggleSign)}`
        : `<br/>${formatKeyDescLine('Esc', tt.toggleSign)}<br/>${formatKeysLeftWithDesc(
            `${formatTooltip(keys.shift)}${formatTooltip('Esc')}`,
            tt.balancingSameAsBpButton,
          )}`;
    const arrowGuide = isBalancingPouch ? '' : `${formatPouchGuideArrowValueMultiplierPcHtml()}<br/>`;
    return `${boldPrefix}${pouchGuideMovementHtml}<br/>${arrowGuide}${formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined)}<br/>${pouchGuideColorExtrasHtml}${escToggleMain}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
  }, [
    isEditing,
    isTitleEditing,
    pouchGuideHoverTier,
    pouchGuideMovementHtml,
    pouchGuideColorExtrasHtml,
    isGuideEnabled,
    isTouchDevice,
    pouch,
    isBalancingPouch,
    pouchNumericTooltipHtml,
  ]);

  useEffect(() => {
    if (isEditing || isTitleEditing) {
      setPouchTooltipContent(pouchEditModeTooltipHtml || null);
      return;
    }
    const el = pouchTooltipTargetRef.current;
    if (!el?.isConnected) {
      setPouchTooltipContent(null);
      pouchTooltipTargetRef.current = null;
      return;
    }
    const next = el.getAttribute('data-pouch-tooltip-html');
    if (next != null && next !== '') {
      setPouchTooltipContent(next);
    } else {
      setPouchTooltipContent(null);
    }
  }, [
    mainTooltipHtml,
    pouch.color,
    pouchGuideHoverTier,
    isEditing,
    isTitleEditing,
    pouchTitleTooltipHtml,
    pouchEditModeTooltipHtml,
    keyboardNewPouchFirstEditUi,
  ]);

  return (
    <div 
      className={`pouch-container ${classNames.join(' ')} ${pouch.isNegative ? 'negative' : ''} ${isTableSelected ? 'table-selected' : ''} ${pouch.isGroupTotalPouch ? 'group-total-pouch' : ''} ${showTimePouch ? 'time-pouch' : ''}${showTimeNegBase ? ' time-pouch-neg-base' : ''} ${isBalancingPouch ? 'balancing-pouch' : ''} ${pouchClassName}`}
      data-pouch-id={pouch.id}
      data-deleting={pouch._isDeleting ? 'true' : 'false'}
      data-pouch-tooltip-html={mainTooltipHtml ? mainTooltipHtml : undefined}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handlePouchTooltipOver}
    >
      {/* カラー変更ボタン - 左上 */}
      <button 
        className="pouch-color-button"
        tabIndex={-1}
        onClick={handleColorChange}
        onTouchEnd={handleColorChange}
        data-pouch-tooltip-html={colorButtonTooltipHtml || undefined}
      >
        <svg className="svg-icon" 
          viewBox="0 0 1024 1024" 
          version="1.1" 
          xmlns="http://www.w3.org/2000/svg"
          fill="var(--icon-color)"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M833.706667 216.746667A452.693333 452.693333 0 0 0 508.16 85.333333a426.666667 426.666667 0 0 0-2.133333 853.333334 110.08 110.08 0 0 0 107.946666-80.64 107.52 107.52 0 0 0-24.32-97.28 21.333333 21.333333 0 0 1 15.786667-35.413334h70.4A262.4 262.4 0 0 0 938.666667 483.413333a361.813333 361.813333 0 0 0-104.96-266.666666z m-541.866667 412.16a64 64 0 1 1 17.066667-88.746667 63.573333 63.573333 0 0 1-17.066667 88.746667zM354.133333 394.666667a64 64 0 1 1-23.466666-85.333334 64 64 0 0 1 23.466666 85.333334zM469.333333 298.666667a64 64 0 1 1 64-64A64 64 0 0 1 469.333333 298.666667z m245.333334 34.133333a64 64 0 1 1 23.466666-85.333333 64 64 0 0 1-23.466666 85.333333z" />
        </svg>
      </button>

      {/* 削除ボタン - 右上 */}
      <button 
        className="pouch-delete-button"
        tabIndex={-1}
        onClick={handleDelete}
        onTouchEnd={handleDelete}
        data-pouch-tooltip-html={getButtonTooltipHtml(`Double Click: ${tt.deletePouch}`) || undefined}
      >
        <svg 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          stroke="var(--icon-color)"
          strokeWidth="2"
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M3 6h18"></path>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </button>

      {/* 分解ボタン - 削除ボタンの右（BP↔正負と同じ間隔） */}
      {!pouch.isGroupTotalPouch && !isBalancingPouch && (
        <button
          className={`pouch-split-button ${isSplitDisabled ? 'disabled' : ''}`}
          tabIndex={-1}
          onClick={handleSplit}
          onTouchEnd={handleSplit}
          disabled={isSplitDisabled}
          data-pouch-tooltip-html={splitButtonTooltipHtml || undefined}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            stroke="var(--icon-color)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              width: 'var(--icon-size)',
              height: 'var(--icon-size)',
            }}
          >
            <path d="M3 12h4.597a5 5 0 0 1 3.904 1.877l.998 1.246A5 5 0 0 0 16.403 17H21m0 0-3-3m3 3-3 3m3-13h-5.078A4 4 0 0 0 12.8 8.501L11.201 10.5A4 4 0 0 1 8.078 12H6m15-5-3-3m3 3-3 3" />
          </svg>
        </button>
      )}

      {/* 帳尻パウチ（BP）トグルボタン - 正負転換ボタンの左 */}
      {!pouch.isGroupTotalPouch && (
        <button
          className={`pouch-balancing-button ${isBalancingPouch ? 'active' : ''}`}
          tabIndex={-1}
          onClick={handleToggleBalancingPouch}
          onTouchEnd={handleToggleBalancingPouch}
          data-pouch-tooltip-html={balancingPouchButtonTooltipHtml || undefined}
        >
          <svg
            viewBox="0 0 512 512"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: 'var(--icon-size)',
              height: 'var(--icon-size)'
            }}
          >
            <path d="M128,499.2h256c14.14,0,25.6-11.46,25.6-25.6S398.13,448,384,448H128c-14.14,0-25.6,11.46-25.6,25.6S113.86,499.2,128,499.2"/>
            <path d="M106.61,140.45L256,115.55l149.39,24.9c13.95,2.32,27.14-7.1,29.46-21.04c2.32-13.95-7.1-27.14-21.04-29.46l-153.6-25.6c-2.8-0.47-5.61-0.47-8.42,0l-153.6,25.6c-13.95,2.32-23.37,15.51-21.04,29.46C79.47,133.36,92.66,142.78,106.61,140.45L106.61,140.45z"/>
            <path d="M230.4,38.4v435.2c0,14.14,11.46,25.6,25.6,25.6c14.14,0,25.6-11.46,25.6-25.6V38.4c0-14.14-11.46-25.6-25.6-25.6C241.86,12.8,230.4,24.26,230.4,38.4"/>
            <path d="M202.1,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15L2.7,257.35c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6s-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2H25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45S208.42,270,202.1,257.35L202.1,257.35z"/>
            <path d="M509.29,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15l-76.8,153.6c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6c-14.14,0-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2h-25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45C510.49,285.37,515.61,270,509.29,257.35L509.29,257.35z"/>
          </svg>
        </button>
      )}

      {/* 正負転換ボタン - 左下 */}
      <button
        className="pouch-toggle-sign-button"
        tabIndex={-1}
        onClick={isBalancingPouch ? undefined : handleToggleSign}
        onTouchEnd={isBalancingPouch ? undefined : handleToggleSign}
        data-pouch-tooltip-html={getToggleSignButtonTooltipHtml() || undefined}
      >
        <svg
          viewBox="0 0 150 150"
          fill="var(--icon-color)"
          xmlns="http://www.w3.org/2000/svg"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M77.955,109.975c-4.037,0-7.309,3.271-7.309,7.308c0,4.037,3.272,7.31,7.309,7.31h56.029c4.037,0,7.309-3.271,7.309-7.31
            c0-4.036-3.271-7.308-7.309-7.308H77.955z"/>
          <path d="M0,44.2c0,4.037,3.272,7.308,7.309,7.308h19.489v19.49c0,4.036,3.271,7.309,7.308,7.309c4.037,0,7.309-3.271,7.309-7.309
            v-19.49H63.34c4.037,0,7.308-3.271,7.308-7.308c0-4.037-3.271-7.309-7.308-7.309H41.414V12.53c0-4.037-3.272-7.308-7.309-7.308
            c-4.037,0-7.308,3.271-7.308,7.308v24.361H7.308C3.272,36.892,0,40.164,0,44.2z"/>
          <path d="M131.428,17.261c-2.854-2.855-7.481-2.855-10.336,0L14.763,123.594c-2.853,2.854-2.853,7.481,0,10.334
            c1.425,1.429,3.298,2.143,5.167,2.143c1.868,0,3.742-0.714,5.169-2.143L131.428,27.599
            C134.283,24.746,134.283,20.114,131.428,17.261z"/>
        </svg>
      </button>

      {/* レプリカ円 - 後ろに配置 */}
      {renderReplicaCircles()}
      
      {/* メイン円 - 最前面に配置 */}
      <div className="pouch-main-circle">
        <input
          ref={titleInputRef}
          className={`pouch-title ${!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' ? 'show-expression' : ''} ${!pouch.isGroupTotalPouch && isExpressionOutdated() ? 'expression-outdated' : ''}`}
          type="text"
          tabIndex={2000 + groupIndex}
          data-pouch-tooltip-html={
            isTitleEditing
              ? pouchEditModeTooltipHtml || undefined
              : isEditing
                ? undefined
                : pouchTitleTooltipHtml || undefined
          }
          value={isTitleEditing ? editTitle : truncateText(editTitle)}
          placeholder={getPlaceholderText()}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          onKeyDown={(e) => handleKeyDown(e, true)}
          onCompositionStart={() => {
            isComposingRef.current = true;
            setIsComposing(true);
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            setIsComposing(false);
          }}
          onClick={(e) => {
            if(!pouch._isDragging) {
              if (suppressTitleClickRef.current) {
                suppressTitleClickRef.current = false;
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (!isTouchDevice) {
                e.preventDefault();
              }
              e.stopPropagation();
              if (!isTitleEditing) {
                beginTitleEditing();
              } else {
                titleInputRef.current?.focus();
              }
            }
          }}
          onMouseDown={(e) => {
            if(!pouch._isDragging) {
              if (isTitleEditing) {
                e.stopPropagation();
                const target = e.currentTarget;
                const wasFocused = document.activeElement === target;
                if (!wasFocused) {
                  requestAnimationFrame(() => {
                    target.select();
                  });
                }
              }
            }
          }}
          onMouseMove={(e) => {
            if(!pouch._isDragging && isTitleEditing) {
              e.stopPropagation();
            }
          }}
          onMouseUp={(e) => {
            if(!pouch._isDragging && isTitleEditing) {
              e.stopPropagation();
            }
          }}
          onTouchStart={(e) => {
            if(!pouch._isDragging) {
              if (isTitleEditing) {
                e.stopPropagation();
                const target = e.currentTarget;
                const wasFocused = document.activeElement === target;
                if (!wasFocused) {
                  target.focus();
                  requestAnimationFrame(() => {
                    target.select();
                  });
                } else {
                  target.focus();
                }
              } else {
                e.stopPropagation();
                suppressTitleClickRef.current = true;
                beginTitleEditing();
              }
            }
          }}
          onTouchEnd={(e) => {
            if (pouch._isDragging) {
              return;
            }
            if (suppressTitleClickRef.current) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (isTitleEditing) {
              e.stopPropagation();
            }
          }}
        />
        <div className="pouch-value">
          <button 
            className={`value-button value-control-button decrease ${isValueDecreasePressed ? 'pressed' : ''} ${isValueDecreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml(`Click: ${tt.valueMinus}\nLong Press: ${tt.valueMinusMinus}`) || undefined}
            onMouseDown={(e) => {
              setIsValueDecreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('value', 'decrease');
            }}
            onMouseUp={(e) => {
              setIsValueDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              setIsValueDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={() => {
              setIsValueDecreasePressed(true);
              handlePressStart('value', 'decrease', true);
            }}
            onTouchEnd={() => {
              setIsValueDecreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="value-icon"
            >
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="value-container">
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                className={pouch.isTime ? 'pouch-value-input--time' : undefined}
                lang={pouch.isTime ? 'en' : undefined}
                inputMode={
                  isTouchDevice && isEditing
                    ? valueKeyboard.inputProps.inputMode
                    : pouch.isTime
                      ? 'text'
                      : inputType === 'decimal'
                        ? 'decimal'
                        : 'text'
                }
                {...(isTouchDevice && isEditing
                  ? { virtualKeyboardPolicy: 'manual' as const }
                  : {})}
                tabIndex={2000 + groupIndex}
                data-pouch-tooltip-html={pouchEditModeTooltipHtml || undefined}
                value={editValue}
                onChange={handleEditValueChange}
                onBeforeInput={handleValueBeforeInput}
                onKeyDown={(e) => handleKeyDown(e, false)}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                  setIsComposing(true);
                }}
                onCompositionEnd={(e) => {
                  isComposingRef.current = false;
                  setIsComposing(false);
                  let v = (e.currentTarget as HTMLInputElement).value;
                  if (pouch.isTime) {
                    v = normalizeTimePouchValueInputText(v);
                  }
                  const filtered = filterPouchExpressionInput(v, !!pouch.isTime);
                  setEditValue(filtered);
                }}
                onBlur={(e) => {
                  if (isBlurToPouchValueKeyboard(e.relatedTarget)) {
                    return;
                  }
                  handleValueConfirm();
                }}
                autoFocus
                onFocus={(e) => {
                  const t = e.target as HTMLInputElement;
                  if (valueEditFocusCaretAtEndRef.current) {
                    valueEditFocusCaretAtEndRef.current = false;
                    const len = t.value.length;
                    queueMicrotask(() => {
                      t.setSelectionRange(len, len);
                    });
                    return;
                  }
                  t.select();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation(); // パウチのドラッグを防止
                }}
                onMouseMove={(e) => {
                  e.stopPropagation(); // 文字列選択ドラッグを可能にする
                }}
                onMouseUp={(e) => {
                  e.stopPropagation(); // パウチのドラッグ終了を防止
                }}
                onClick={(e) => {
                  e.stopPropagation(); // クリックイベントの伝播を防止
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            ) : (
              <div 
                className={`value-display${layerSlideSignNegative ? ' negative' : ''}`}
                tabIndex={2000 + groupIndex}
                data-pouch-tooltip-html={getButtonTooltipHtml(`Click: ${tt.editPouchValue}`) || undefined}
                onMouseDown={(e) => {
                  // 実際のマウスイベントの場合のみフラグを設定
                  if (e.type === 'mousedown' && e.button === 0) {
                    isMouseFocusRef.current = true;
                    // mousedown 後に別要素で mouseup した場合、フラグが残り Tab フォーカスで編集に入れないため解除する
                    const clearMouseFocusFlag = () => {
                      isMouseFocusRef.current = false;
                      window.removeEventListener('mouseup', clearMouseFocusFlag, true);
                    };
                    window.addEventListener('mouseup', clearMouseFocusFlag, true);
                  }
                  e.preventDefault();
                }}
                onMouseUp={handleValueClick}
                onTouchEnd={handleValueClick}
                onFocus={(e) => {
                  // タブ等でフォーカスされたとき編集に入る（mousedown だけして別要素で離したときのフラグ残りは上でクリア）
                  if (isMouseFocusRef.current || pouch._isDragging) {
                    return;
                  }
                  queueMicrotask(() => {
                    if (document.activeElement === e.target) {
                      handleValueClick(e as unknown as React.MouseEvent);
                    }
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleValueClick(e as any);
                  }
                }}
              >
                {currentSheet && !isEditing ? (
                  <LayerSlideNumber
                    slideEnabled={useLayerSlide || layerSwitchDirection !== 0}
                    value={layerSlideDisplay.value}
                    isNegative={layerSlideDisplay.isNegative}
                    displayAsTime={layerSlideDisplay.displayAsTime}
                    timeDisplayTag="strong"
                    direction={layerSwitchDirection}
                    switchNonce={layerSwitchNonce}
                    duration={isInitialLoading ? 600 : 300}
                    animateFromZero={isInitialLoading}
                    suppressTooltip={pouch.isTime || shouldShowMainTimeDisplay}
                  />
                ) : (
                  <>
                {shouldShowMainTimeDisplay ? (
                  <strong>{getMainTimeDisplayString()}</strong>
                ) : null}
                  <NumberCounter
                    key={`pv-${pouch.id}-${pouchValueDisplayRaw < 0 ? 'n' : 'p'}`}
                    value={Math.abs(pouchValueDisplayRaw)}
                    isNegative={pouchValueDisplayRaw < 0}
                    duration={isInitialLoading ? 600 : 300}
                    suppressTooltip={pouch.isTime || shouldShowMainTimeDisplay}
                  />
                  </>
                )}
              </div>
            )}
          </div>
          <button 
            className={`value-button value-control-button increase ${isValueIncreasePressed ? 'pressed' : ''} ${isValueIncreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml(`Click: ${tt.valuePlus}\nLong Press: ${tt.valuePlusPlus}`) || undefined}
            onMouseDown={(e) => {
              setIsValueIncreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('value', 'increase');
            }}
            onMouseUp={(e) => {
              setIsValueIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              setIsValueIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={() => {
              setIsValueIncreasePressed(true);
              handlePressStart('value', 'increase', true);
            }}
            onTouchEnd={() => {
              setIsValueIncreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="value-icon"
            >
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div
          className={`pouch-multiplier-control ${
            !displayShowMultiplierControl ? 'no-multiplier' : ''
          }${showRoundUnitSlot ? ' has-round-slot' : ''}${
            hasRoundUnitDisplay ? ' has-round-unit' : ''
          }${displayPureRoundZero ? ' pure-round-zero' : ''}${
            displayPureMultOne ? ' pure-mult-one' : ''
          } ${isBalancingPouch ? 'balancing-locked' : ''}`}
        >
          <div className="multiplier-stack">
          <div className="multiplier-main-row">
          <button
            className={`multiplier-button decrease ${isDecreasePressed ? 'pressed' : ''} ${isDecreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml(`Click: ${tt.multiMinus}\nLong Press: ${tt.multiMinusMinus}`) || undefined}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDecreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('multiplier', 'decrease');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setIsDecreasePressed(true);
              handlePressStart('multiplier', 'decrease', true);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="multiplier-icon"
            >
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="multiplier-scale-group">
          <div
              className={`multiplier-value ${layerSlideSignNegative ? 'negative' : 'positive'}`}
              data-pouch-tooltip-html={multiplierValueTooltipHtml || undefined}
            >
              {(useLayerSlide || layerSwitchDirection !== 0) ? (
                <>
                  <LayerSlideTimeMultBase
                    visible={isTimeVisualForLayout}
                    negative={isTimeNegBaseForLayout}
                    slideEnabled={multLayerSlideEnabled}
                    direction={layerSwitchDirection}
                    switchNonce={layerSwitchNonce}
                  />
                  <LayerSlideMultiplier
                    display={multiplierDisplay}
                    slideEnabled={multLayerSlideEnabled}
                    direction={layerSwitchDirection}
                    switchNonce={layerSwitchNonce}
                    animateFromZero={isInitialLoading}
                    duration={multiplierCounterDuration || 300}
                    onClick={handleMultiplierValueClick}
                    style={{ cursor: isBalancingPouch ? 'default' : 'pointer' }}
                  />
                </>
              ) : (
                <>
                  {isTimeVisualForLayout && (
                    <span className={`time-mult-base ${isTimeNegBaseForLayout ? 'negative' : 'positive'}`}>
                      {isTimeNegBaseForLayout ? '-60' : '60'}&nbsp;
                    </span>
                  )}
                  <span
                    onClick={handleMultiplierValueClick}
                    style={{ cursor: isBalancingPouch ? 'default' : 'pointer' }}
                  >
                    {multiplierDisplay.isDivision ? '/' : '×'}{' '}
                    <NumberCounter
                      value={multiplierDisplay.value}
                      animateFromZero={isInitialLoading}
                      duration={multiplierCounterDuration}
                      suffix={multiplierDisplay.showSymbolAfter ? multiplierDisplay.symbol : ''}
                      bridgePercentModeTransition
                      suppressTooltip
                    />
                  </span>
                </>
              )}
          </div>
          </div>
          <button
            className={`multiplier-button increase ${isIncreasePressed ? 'pressed' : ''} ${isIncreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml(`Click: ${tt.multiPlus}\nLong Press: ${tt.multiPlusPlus}`) || undefined}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsIncreasePressed(true);
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('multiplier', 'increase');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setIsIncreasePressed(true);
              handlePressStart('multiplier', 'increase', true);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="multiplier-icon"
            >
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          </div>
          {showRoundUnitSlot && (
            <div className="round-unit-row">
              <button
                type="button"
                className={`round-unit-button decrease ${isRoundDecreasePressed ? 'pressed' : ''} ${isRoundDecreaseLongPressed ? 'long-pressed' : ''}`}
                tabIndex={-1}
                data-pouch-tooltip-html={
                  getButtonTooltipHtml(
                    `Click: step +1\nControl+Click: coarse step\nLong Press: +5, then +100/+100/+1000`,
                  ) || undefined
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
                  setIsRoundDecreasePressed(true);
                  handlePressStart('roundUnit', 'decrease');
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  setIsRoundDecreasePressed(false);
                  handlePressEnd({ x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                  setIsRoundDecreasePressed(false);
                  handlePressEnd({ x: e.clientX, y: e.clientY });
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  setIsRoundDecreasePressed(true);
                  handlePressStart('roundUnit', 'decrease', true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  setIsRoundDecreasePressed(false);
                  handlePressEnd();
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="multiplier-icon">
                  <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {(useLayerSlide || showRoundUnitSlot) && (
                <LayerSlideRoundUnit
                  roundUnit={effectiveDisplayRoundUnit}
                  showZeroWhenIdle
                  countAnimate={!(isRoundIncreaseLongPressed || isRoundDecreaseLongPressed)}
                  slideEnabled={useLayerSlide || layerSwitchDirection !== 0}
                  direction={layerSwitchDirection}
                  switchNonce={layerSwitchNonce}
                  className="multiplier-round-unit"
                  isNegative={layerSlideDisplay.isNegative}
                  animateFromZero={isInitialLoading}
                  tooltipHtml={
                    hasRoundUnitDisplay
                      ? getButtonTooltipHtml(`Click: ${tt.resetRounding}`) || undefined
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (pouch._isDragging || !hasRoundUnitDisplay || isBalancingPouch) return;
                    persistRoundUnit(null);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (pouch._isDragging || !hasRoundUnitDisplay || isBalancingPouch) return;
                    persistRoundUnit(null);
                  }}
                />
              )}
              <button
                type="button"
                className={`round-unit-button increase ${isRoundIncreasePressed ? 'pressed' : ''} ${isRoundIncreaseLongPressed ? 'long-pressed' : ''}`}
                tabIndex={-1}
                data-pouch-tooltip-html={
                  getButtonTooltipHtml(
                    `Click: step +1\nControl+Click: coarse step\nLong Press: +5, then +100/+100/+1000`,
                  ) || undefined
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
                  setIsRoundIncreasePressed(true);
                  handlePressStart('roundUnit', 'increase');
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  setIsRoundIncreasePressed(false);
                  handlePressEnd({ x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                  setIsRoundIncreasePressed(false);
                  handlePressEnd({ x: e.clientX, y: e.clientY });
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  setIsRoundIncreasePressed(true);
                  handlePressStart('roundUnit', 'increase', true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  setIsRoundIncreasePressed(false);
                  handlePressEnd();
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="multiplier-icon">
                  <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
      {pouchTooltipContent
        ? createPortal(
            <Tooltip
              id={`pouch-tt-${pouch.id}`}
              anchorSelect={`[data-pouch-id="${pouch.id}"]`}
              html={pouchTooltipContent}
              isOpen
              place="top"
              offset={15}
              className="app-tooltip"
              noArrow={true}
              delayShow={0}
              delayHide={120}
              positionStrategy="fixed"
              style={{ zIndex: 100000000 }}
            />,
            document.body,
          )
        : null}
    </div>
  );
});

PouchComponent.displayName = 'PouchComponent';

// --- React.memo 最適化 ---
// 値編集時に未関係のパウチが再描画されるのを防ぐ。
// 比較で true を返すと再描画スキップ。安全側に倒し、少しでも疑わしければ再描画する。
const POUCH_FLAG_KEYS: (keyof PouchComponentProps['pouch'])[] = [
  '_isDragging', '_isDeleting', '_animateFromZero', '_isCloneSource', '_isCloning',
];

function shallowEqualPouch(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    // layerValues 等のネストは参照比較（未編集パウチでは参照が保たれる）
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// React.memo の比較関数。true を返すと再描画スキップ。
// 値編集時に「編集対象でないパウチ」が再描画されるのを防ぐ。安全側に倒し、
// パウチ表示に影響しうる props に少しでも差があれば false（再描画）を返す。
//
// 既知の許容トレードオフ: 帳尻ボタンのツールチップに表示する「全体合計」は
// allPouches 全体の値に依存する（currentTotal）。これを比較すると O(N^2) になり
// 最適化が無効化されるため比較しない。結果として、他パウチを編集した直後に
// 「このパウチを帳尻にする」ボタンのツールチップ合計が、当該パウチが次に
// 再描画されるまで一時的に古いままになりうる（ホバー時のみ・表示値や計算結果には影響なし）。
function arePouchPropsEqual(prev: PouchComponentProps, next: PouchComponentProps): boolean {
  const p = next.pouch;
  // GTP は他パウチの値に依存するため常に再描画
  if (p.isGroupTotalPouch || prev.pouch.isGroupTotalPouch) return false;
  // 帳尻パウチは他パウチ変更で値が自動調整されるため常に再描画
  const bpId = next.currentSheet?.balancingPouchId;
  if (bpId && (bpId === p.id || bpId === prev.pouch.id)) return false;
  // 帳尻パウチが存在する間は通常パウチのツールチップ合計が変わり得る → 安全側で再描画
  if (next.currentSheet?.balancingPouchId || prev.currentSheet?.balancingPouchId) {
    if (prev.allPouches !== next.allPouches) return false;
  }

  // シート全体の構造/モードシグネチャ（値以外でパウチ表示に影響する要素）
  if (prev.pouchRenderSignature !== next.pouchRenderSignature) return false;

  // スカラー UI プロパティ
  if (prev.lastPouchColor !== next.lastPouchColor) return false;
  if (prev.isInitialLoading !== next.isInitialLoading) return false;
  if (prev.isSheetSwitching !== next.isSheetSwitching) return false;
  if (prev.isTouchDevice !== next.isTouchDevice) return false;
  if (prev.isTableSelected !== next.isTableSelected) return false;
  if (prev.keyboardGraceAppendBump !== next.keyboardGraceAppendBump) return false;

  // ホバー（自分が対象かどうかの変化のみ気にする）
  const id = p.id;
  if ((prev.hoveredPouchId === id) !== (next.hoveredPouchId === id)) return false;

  // 自パウチの内容比較（mirror で毎回新オブジェクトになるため値比較）
  if (!shallowEqualPouch(prev.pouch, next.pouch)) return false;

  // フラグの明示比較（shallow で拾えるが念のため）
  for (const k of POUCH_FLAG_KEYS) {
    if ((prev.pouch as any)[k] !== (next.pouch as any)[k]) return false;
  }

  return true;
}

const MemoizedPouchComponent = React.memo(PouchComponent, arePouchPropsEqual);
(MemoizedPouchComponent as any).displayName = 'PouchComponent';

export default MemoizedPouchComponent;
// 数式処理関数をエクスポート（TableComponentから使用）
export { handleUnifiedExpression } from '../utils/ExpressionEvaluator';
